export const STORAGE_KEYS = {
  JWT_TOKEN: 'jwtToken',
  TOKEN_INFO: 'tokenInfo',
  USER_DETAILS: 'userDetails',
  USER_PROFILE_CACHE: 'userProfileCache',
  AUTH_REDIRECT_URL: 'authRedirectUrl',
  HAWB_BULK_EDIT_DATA: 'hawbBulkEditData',
  BAG_BULK_EDIT_DATA: 'bagBulkEditData',
  MANIFEST_BULK_EDIT_DATA: 'manifestBulkEditData',
  BAG_ID: 'bagId',
  MANIFEST_ID: 'manifestId',
  TIMEZONE_ID: 'timezoneId',
} as const;

export const ReportFilterFormControlNames = {
  code: 'code',
  accountNumber: 'accountNumber',
  location: 'location',
  codeValue: 'codeValue',
  accountValue: 'accountValue',
  locationValue: 'locationValue',
  salesperson: 'salesperson',
} as const;

export const ReportSectionCodes = {
  summary: 'summary',
  details: 'details',
} as const;

/** Normalized `ReportTypeFilterDto.filterType` values for dynamic report filters. */
export const ReportFilterNormalizedTypes = {
  dateRange: 'date-range',
  dateRangeCompact: 'daterange',
  datePicker: 'date-picker',
  monthPicker: 'month-picker',
  monthCompact: 'month',
  select: 'select',
  autocomplete: 'autocomplete',
  radio: 'radio',
  unknown: 'unknown',
} as const;

export type NormalizedReportFilterType =
  | typeof ReportFilterNormalizedTypes.dateRange
  | typeof ReportFilterNormalizedTypes.datePicker
  | typeof ReportFilterNormalizedTypes.monthPicker
  | typeof ReportFilterNormalizedTypes.select
  | typeof ReportFilterNormalizedTypes.autocomplete
  | typeof ReportFilterNormalizedTypes.radio
  | typeof ReportFilterNormalizedTypes.unknown;

/** `ReportFilterFieldRuntime.controlKind` */
export const ReportFilterRuntimeControlKind = {
  date: 'date',
  month: 'month',
  select: 'select',
  autocomplete: 'autocomplete',
  radio: 'radio',
} as const;

export type ReportFilterRuntimeControlKindType =
  (typeof ReportFilterRuntimeControlKind)[keyof typeof ReportFilterRuntimeControlKind];

export const ReportFilterWidgetKind = {
  dateRange: 'dateRange',
  singleDate: 'singleDate',
  month: 'month',
  select: 'select',
  autocomplete: 'autocomplete',
  radio: 'radio',
} as const;

export type ReportFilterWidgetKindType =
  (typeof ReportFilterWidgetKind)[keyof typeof ReportFilterWidgetKind];

export const ReportFilterWidgetTrackKey = {
  dateRangePair: 'range',
} as const;

/** Static Yes/No values for lookup-less `radio` report filters. `no` is the default and is never sent in the payload. */
export const ReportFilterRadioOptions = {
  yes: 'yes',
  no: 'no',
} as const;

export const ReportFilterRadioOptionLabels = [
  { value: ReportFilterRadioOptions.yes, label: 'Yes' },
  { value: ReportFilterRadioOptions.no, label: 'No' },
] as const;

/** `total_charges` = No disables (and unticks) the `charges` section column. */
export const ReportFilterTotalCharges = {
  fieldId: 'totalCharges',
  chargesDataKey: 'charges',
} as const;

/** CamelCase fragment before mapping `sales_person` → {@link ReportFilterFormControlNames.salesperson}. */
export const ReportFilterFieldNormalization = {
  salesPersonCamel: 'salesPerson',
} as const;

export const ReportFilterMapperWarnings = {
  moreThanTwoDateRangeIgnored: 'More than two date-range filters were ignored.',
  dateRangeRequiresTwoRows: 'Date range requires two filter rows; skipping date filters.',
  unsupportedFilterType: (filterType: string, filterName: string) =>
    `Unsupported filter type "${filterType}" for ${filterName}.`,
  missingMasterLookupType: (filterName: string) =>
    `Filter ${filterName} is missing masterLookupType.`,
  missingMasterLookupFieldName: (filterName: string) =>
    `Filter ${filterName} requires masterLookupFieldName for lookup-backed fields.`,
} as const;

export const ReportFilterRequiredLabels = {
  fieldIsRequired: (displayLabel: string) => `${displayLabel} is required`,
} as const;

const reportDownloadModalTypeNameByCode: Record<string, string> = {
  CHARGES_BY_CREATE_DATE: 'Charges by Create Date',
  CHARGES_BY_INVOICE_DATE: 'Charges by Invoice Date',
};

const defaultReportDownloadModalCode = 'CHARGES_BY_CREATE_DATE';

export function getReportDownloadModalContent(reportCode: string): {
  title: string;
  instruction: string;
} {
  const typeName =
    reportDownloadModalTypeNameByCode[reportCode] ??
    reportDownloadModalTypeNameByCode[defaultReportDownloadModalCode];
  return {
    title: `Download ${typeName} Report`,
    instruction: `Please select the file type in which you would like the ${typeName} Report to be generated.`,
  };
}

export const reportSectionNamesListFormat = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

export const ErrorHandlerConstants = {
  Error_400: 'Element Not Found Please Provide Valid Trade Id',
  Error_500:
    'Sorry, Operation could not be completed at this time.\n Back-end returned with an Internal Server Error. Please contact customer support',
  Default_Error:
    'Sorry, Operation could not be completed at this time.\n Please contact customer support',
  Initial_Error_Text: 'Sorry, Operation could not be completed. \n Error: \n',
  String_Declare: 'string',
  Connective_Error:
    'Sorry, we are having connectivity issues, Please contact our Pulse customer engagement for more help',
  Error_502:
    'Sorry, Operation could not be completed at this time.\n Back-end returned with an Timeout Error. Please contact customer support',
  SessionTimout: 'Session timedout, Please login again',
  InvalidPackageType: 'Please enter a valid packaging type',
  AssociationError: 'associated with',
  RateScaleAssociationError: 'already associated',
};

export const SnackBar = {
  Close: 'Close',
  Error: 'error',
};

export const HttpSignatures: HttpSignature = {
  Post: 'POST',
  Put: 'PUT',
  Delete: 'DELETE',
  Get: 'GET',
};

export interface HttpSignature {
  [name: string]:
  | 'DELETE'
  | 'GET'
  | 'HEAD'
  | 'JSONP'
  | 'OPTIONS'
  | 'POST'
  | 'PUT';
}

export enum StatusCodes {
  Success = 200,
  SuccessfulRequest = 204,
  BadRequest = 400,
  Unauthorised = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  UnprocessableEntity = 422,
  InternalServerError = 500,
  PartialAutoRating = 207,
}

export const AccountUpdateConditionChecks = {
  serviceTypeDelinkRequiresConfirmation:
    'SERVICE_TYPE_DELINK_REQUIRES_CONFIRMATION',
} as const;

export const ModalPopupMessages = {
  sendManifestViaEmailMsg: 'Send Manifest via Email',
  sendItToMe: 'Send it to me',
  invalidEmailAddress: 'Invalid email address entered',
  manifestEmailRequired: 'At least one email address is required when Send it to me is unchecked',
  fileFormatRequired: 'File Format is required when Manifest Format is selected',
  subjectRequired: 'Subject is required',
  maxAttachmentsReached: 'Maximum of 5 attachments allowed',
  invalidAttachmentType: 'Only CSV, Excel, PDF, Text, and Image files are allowed',
  manifestPublishAttachmentRequestType: 'MANIFEST_PUBLISH',
  changeLookupFieldMessage: (fieldLabel: string) =>
    `Would you really like to change the ${fieldLabel} of the Shipment?`,
  changeLookupFieldTitle: (fieldLabel: string) => `Change ${fieldLabel}`,
  changeShipmentTitle: 'Change Shipment',
  changeShipmentAddModeMessage:
    'Changing the shipment number will remove all the values that you have entered. Would you like to go ahead?',
  changeShipmentEditModeMessage:
    'Changing the shipment number will remove all the values that you have updated. Would you like to go ahead?',
  ARShipmentPublishedWarning: (messageType: string) =>
    `Charge already published to GP and can’t be ${messageType}`,
  hawbArChargesBlockedNote:
    'Managing AR Invoices for blocked shipments not possible',
  syncApInvoicesHeader: 'Sync AP Invoices',
  syncApInvoicesPublishedTooltip: (invoiceNumber: string) =>
    `AR Invoice '${invoiceNumber}' has been published to GP. AP charges cannot be synced`,
  syncApInvoicesNoPendingTooltip: 'No new AP charges to sync',
  shipmentAutoRatedEventMessage: 'Shipment was Auto Rated',
  shipmentUnAutoRatedEventMessage: 'Shipment was Un-Auto rated',
  InvoiceDownloadedSuccessfully: 'Invoice downloaded successfully',
  AddComment: 'Add Comment',
  NotSignedUpServiceType:
    'You have not signed up for this service. Would you still like to go ahead and use this service?',
  accountUnserviceableShipment:
    'Creation of shipments not possible as Account is currently unserviceable',
  serviceTypeDeliveryAgentMandatoryForAccount:
    'Service Type and Delivery Agent are mandatory for the account',
  InvalidEntry: 'Invalid Entry',
  Validation: 'Validation',
  Confirmation: 'Confirmation',
  FillMandatoryFields:
    'Changes could not be saved. Please check your input and try again',
  AddHAWB: 'Would you like to add a Shipment for',
  DeleteHawb: (messageType: string) =>
    `Would you like to delete the selected ${messageType}?`,
  Warning: 'Warning',
  Error: 'error',
  Reset: 'You have unsaved changes. Would you like to go back?',
  ResetFlight: 'Reset Flight Information',
  ResetCommodity: 'Reset Commodity Information',
  close: 'close',
  resetHeader: 'Reset',
  ChangeSuccess: 'Changes have been saved successfully',
  success: 'success',
  error: 'error',
  ChangeResetSuccess: 'Changes reset successfully',
  changesDiscarded: 'Changes discarded',
  rolesPermissionsImpactedUsersConfirmationRequired:
    'There are users already assigned to the Department / Role combination. If you save the changes now, the existing users will be impacted by the change. Would you like to go ahead with it?',
  rolesPermissionsRevokeSpecialEditConfirmation:
    'Would you like to revoke the normal edit and bulk edit as well?',
  SaveChanges: 'Would you like to save the changes?',
  SaveCommodityInfo: 'Save Commodity Information',
  save: 'save',
  DeleteCommodity: 'Are you sure you want to delete this commodity?',
  MandatoryFields:
    'Please complete all the required fields and check all inputs before proceeding.',
  Block: 'Block',
  Unblock: 'Unblock',
  BlockMessage: 'Are you sure you want to block the Shipment ?',
  weightConfrimationMessages:
    'The measured weight and / or measured dimension varies from the data in the fullman file by more than 5%. Would you like to override the existing data?',
  UnblockMessage: 'Are you sure you want to unblock the Shipment ?',
  DeleteStatusCode: 'delete the Status Code',
  StatusCode: 'Status Code',
  DeletePOD: 'delete the POD',
  POD: 'POD',
  unlinkText: (count: number, itemName: string) =>
    `Are you sure you want to unlink these ${count} ${itemName}?`,
  linkShipment: 'Shipment added successfully',
  unlinkHeader: (itemName: string) => `Unlink ${itemName}`,
  noChanges: 'No Changes were made to save',
  noMoreFlights: 'Allowed only to add a maximum of 3 flights only',
  cancel:
    'You have made changes. Are you sure you want to discard the changes?',
  flightInfo: 'Flight Maximum Limit',
  noChangeSave: 'No Changes',
  notesInformationText:
    "Notes provide a convenient way to document key information. Users can enter relevant notes about the shipment in this section. By keeping notes clear and concise, you can enhance communication and streamline tracking processes across the shipment's lifecycle.",
  statusCodeInformationText:
    'The status code represents the current state or condition of the Shipment. Choose the appropriate status code that accurately reflects the progress or status of the shipment. Consistent use of status codes aids in effective tracking and management of shipments.',
  podInformationText:
    'The Proof of Delivery (POD) functionality encompasses various delivery confirmation methods to ensure a comprehensive and adaptable system for verifying the receipt of shipments.',
  hawbAttachmentsInformationText:
    'Attachments provide supplementary documentation for the shipment, offering additional context and detail. Users can upload relevant files, including invoices, receipts, and inspection reports, to enhance the shipment record with comprehensive supporting information.',
  miscellaneousInformationText:
    'This section captures additional details and miscellaneous information related to the Shipment. Here, user can include relevant details that contribute to a comprehensive understanding of the Shipment. Clear and accurate miscellaneous information enhances communication and clarity throughout the Shipment lifecycle',
  commodityAttachmentsInformationText:
    'Attachments provide supplementary documentation for the commodity, offering additional context and detail. Users can upload relevant files, including invoices, receipts, and inspection reports, to enhance the commodity record with comprehensive supporting information.',
  commodityNotesInformationText:
    'Notes serve as a valuable record of essential information. Users can add important notes related to the commodity here. Clear and concise notes are crucial for facilitating effective communication and efficient tracking throughout the entire commodity lifecycle.',
  commodityMiscellaneous:
    'This section captures additional details and miscellaneous information related to the commodity. Here, user can include relevant details that contribute to a comprehensive understanding of the commodity. Clear and accurate miscellaneous information enhances communication and clarity.',
  resetBag: 'Reset Bag Information ?',
  shipperConsigneeInfo:
    'The key roles associated with the shipment are managed from this facility',
  deleteNote: 'Are you sure you want to delete the note ?',
  app: 'App',
  ui: 'UI',
  bagNotesText:
    'Notes serve as a valuable record of essential information. Users can add important notes related to the Bag here. Clear and concise notes are crucial for facilitating effective communication and efficient tracking throughout the entire bag lifecycle.',
  mawbNotesText:
    'Notes serve as a valuable record of essential information. Users can add important notes related to the MAWB here. Clear and concise notes are crucial for facilitating effective communication and efficient tracking throughout the entire MAWB lifecycle',
  mawbFlightNotesText:
    'Notes serve as a valuable record of essential information. Users can add important notes related to the Flight here. Clear and concise notes are crucial for facilitating effective communication and efficient tracking throughout the entire Flight lifecycle.',
  loreum:
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmodtempor incididunt ut labore et dolore magna aliqua. Ut enim ad minimveniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex eacommodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
  manifestNotesInfo:
    'Notes serve as a valuable record of essential information. Users can add important notes related to the Manifest here. Clear and concise notes are crucial for facilitating effective communication and efficient tracking throughout the entire Manifest lifecycle.',
  manifestStatusCodeInfo:
    "The status code indicates the current state or condition of the manifest. It is essential to select a status code that accurately reflects the manifest's progress or status. Consistent use of these codes is vital for effective tracking and management of manifests.",
  manifestAttachmentInfo:
    'Attachments provide supplementary documentation for the Manifest, offering additional context and detail. Users can upload relevant files, including invoices, receipts, and inspection reports, to enhance the Manifest record with comprehensive supporting information.',
  bagAttachment:
    'Attachments provide supplementary documentation for the bag, offering additional context and detail. Users can upload relevant files, including invoices, receipts, and inspection reports, to enhance the bag record with comprehensive supporting information.',
  mawbAttachment:
    'Attachments provide supplementary documentation for the MAWB, offering additional context and detail. Users can upload relevant files, including invoices, receipts, and inspection reports, to enhance the MAWB record with comprehensive supporting information.',
  mawbAttachmentFlightMessage:
    'Attachments act as supporting documents for the flight, adding depth and clarity. Users can upload files such as manifests, flight plans, or other relevant documents to create a more detailed and informative flight record.',
  bagMiscellaneous:
    'This section allows users to capture supplementary details and miscellaneous information about the bag. It provides a space to include any relevant information that helps build a complete picture of the bag.',
  linkBagSuccess: 'Bag added successfully',
  resetManifest: 'Reset Manifest Information ?',
  clearConfirmationText: 'If so, type "Clear" and click Clear',
  commodityAdd: 'Commodity added successfully',
  FillAgentMandatoryField: 'Manifest cannot be published without Agent.',
  BagWithNoShipmentsPublishError: 'Manifest cannot be published as one or more Bags have no Shipments',
  podMandatoryBeforePublish: 'POD is mandatory before publishing the Manifest',
  nonePublishMatch: 'None of the selected Manifests meet the publish criteria',
  savedSearchesHeader: 'Saved Searches',
  recentSearchesHeader: 'Recent Searches',
  legacySavedSearchNotLoadable:
    'This search was saved in an older format and cannot be loaded into the form. Please run the search again.',
  saveAsNewSearchHeader: 'Save Search',
  pinSearch: 'Pin Search',
  apMultiShipment: 'Multiple Shipment Match Found',
  apMultiShipmentMessage: (itemName: string) => `
    There are multiple shipments associated with the entered ${itemName}. Please select all the appropriate shipments to proceed.`,
  associateUfoShipmentsBanner:
    'Below are the UFO shipments. Please select one or more shipments using the checkbox, associate them with the correct account, and click Proceed to update the account information.',
  lockedApChargeTooltip:
    'This charge is prevented from changes, as the AR Invoice was already published',
  ufoShipmentIdentifierTooltip:
    'This is a UFO shipment. Account information can be updated from the Shipment module.',
  mawbMiscellaneousMessage:
    "This section is dedicated to capturing miscellaneous information about the MAWB (Master Air Waybill). Users can input relevant details here to provide a comprehensive understanding of the MAWB. Entering clear and accurate miscellaneous information enhances communication and clarity throughout the MAWB's lifecycle. ",
  // PAC-24713: US CBP Entry Numbers
  usCbpEntryNumbersLabel: 'US CBP Entry Numbers',
  usCbpEntryNumbersAvailable: 'Available',
  usCbpEntryNumbersNotAvailableBadge: 'Not Available',
  usCbpEntryNumbersNotAvailable: 'Not available',
  usCbpEntryNumbersNoEntries: 'There are no entries for this MAWB',
  usCbpEntryNumbersOthersTooltip:
    'Click the link to view all US CBP Entry Numbers',
  usCbpEntryNumbersSearchPlaceholder: 'Search by Shipment or Entry Number',
  usCbpEntryNumbersNoMatch: 'No results matched the search',
  mawbMiscellaneousFlightMessage:
    "This section is dedicated to capturing miscellaneous information about the flight. Users can input relevant details here to provide a comprehensive understanding of the flight. Entering clear and accurate miscellaneous information enhances communication and clarity throughout the flight's lifecycle.",
  mawbStatusCodeMessage:
    "The status code indicates the current state or condition of the MAWB (Master Air Waybill). It is crucial to select a status code that accurately reflects the MAWB's progress or status. Consistent use of these codes is essential for effective tracking and management of MAWBs.",
  hawbStatusCodeMessage:
    "The status code indicates the current phase or condition of a shipment. Select the most appropriate code that accurately represents the shipment's progress. Proper and consistent use of these codes facilitates efficient tracking, effective management and clear communication",
  mawbEventMessage:
    'Key updates regarding the MAWB are listed under the Events section.',
  manifestEventMessage:
    'Key updates regarding the Manifest are listed under the Events section.',
  mawbEventFlightMessage:
    'The Events section provides a comprehensive view of all key updates and milestones related to the flight.',
  updateSearch: 'Update Search',
  searchDelete: 'Would you really like to delete the Search',
  clearRecentSearchContent: 'Do you want to delete all Recent Searches?',
  FillHawbMandatoryField:
    'Please fill out all mandatory fields before proceeding',
  resetMAWB: 'Reset MAWB Information?',
  restMawbInfo: 'Would you like to cancel the MAWB creation and go back?',
  BulkDeleteHeader: 'Bulk Delete ?',
  singleDeleteHeader: (headerName: string) => `Delete ${headerName} ?`,
  bulkDeleteMessage: (count: number | string, itemName: string) =>
    `Do you want to delete these ${count} ${itemName}?`,
  singleDeleteMessage: (itemName: string) =>
    `Do you want to delete the ${itemName} `,
  singleDeleteSuccessMessage: (itemName: string) =>
    `Selected ${itemName} deleted successfully`,
  bulkDeleteSuccessMessage: (count: number | string, itemName: string) =>
    `Selected ${count} ${itemName} deleted successfully`,
  downloadFileText: 'Select file type',
  reportFilterStartEndDateRequired:
    'Please select a start date and an end date.',
  reportFilterDateRangeLegendDefault: 'Select Date Range *',
  reportCouldNotBeGenerated: 'Report could not be generated.',
  reportDownloadSuccessItemName: 'Report',
  reportFilterMandatoryFieldsHint: 'Field with * are mandatory',
  reportFilterFieldRequiredDefault: 'Required',
  reportFilterSectionSelectAtLeastOneColumn:
    'Select at least one column in this section.',
  reportFilterSectionSelectAtLeastOneColumnSnackbar: (
    sectionNamesFormatted: string
  ) =>
    `Select at least one column in the ${sectionNamesFormatted} section.`,
  BagManifestDeleteSuccess: (itemName: string) =>
    `${itemName} removed from the Manifest`,
  DeleteSuccess: (itemName: string) => `${itemName} deleted successfully`,
  ShipmentBagDeleteSuccess: (itemName: string) =>
    `${itemName} removed from the Bag`,
  addShipment: 'Add Shipment?',
  downloadSuccess: (itemName: string) => `${itemName} downloaded successfully`,
  emailSentSuccessfully: (count: number) =>
    `${count} AR Invoices Published to Customer Successfully`,
  arSendEmailUnpublishedConfirmation: (
    totalCount: number,
    unpublishedCount: number,
    accountName: string
  ) =>
    `Out of ${totalCount} invoices, only ${unpublishedCount} have not been published to customer ${accountName} yet. Would you like to send only the unpublished invoices to customer?`,
  arProceedOnlyUnpublishedFromMixedSelection: (
    totalSelected: number,
    publishedCount: number,
    destinationPhrase: string
  ) =>
    `Out of the selected ${totalSelected} AR invoices ${publishedCount} ${publishedCount === 1 ? 'invoice is' : 'invoices are'
    } already published would you like to proceed to publish the unpublished AR invoice${publishedCount === totalSelected - 1 ? '' : 's'} ${destinationPhrase}?`,
  arSpecialRepublishMainPublishMessage: (invoiceNumbers: string) =>
    `Do you want to republish the selected AR Invoice(s) ${invoiceNumbers}? No file will be sent to GP.`,
  mawbExist: 'Mawb already exists. Please enter another one',
  resetAgentInfo: 'Would you like to cancel the Agent creation and go back?',
  resetAgent: 'Reset Agent Information ?',
  searchSuccess: (itemName: string, action: string, count?: number) =>
    count
      ? `${count} ${itemName} ${action} successfully`
      : `${itemName} ${action} successfully`,
  searchLimitWarning: (itemName: string, count: number) =>
    `${itemName} search limit reached: Maximum of ${count} searches allowed`,
  alreadyExists: (itemName: string) =>
    `${itemName} already exists. Please enter another one`,
  linkManifestSuccess: 'Manifest added successfully',
  unlinkManifestMawb: 'Are you sure you want to unlink this Manifest(s)?',
  unlinkManifest: 'Unlink Manifest(s)',
  actionCancel: 'Action cancelled',
  unlinkFlightMawb: 'Are you sure you want to delete this Flight(s)?',
  unlinkFlight: 'Delete Flight(s)',
  addFlightSuccess: 'Flight added successfully',
  MandatoryFlightFields:
    'Please fill out all mandatory fields and check all inputs before proceeding',
  DiscardChangesHeader: 'Discard changes',
  DiscardChangesContent: 'Are you sure you want to discard the changes?',
  PublishMessage: (itemName: string, moduleName: string) =>
    `Would you like to publish ${moduleName} ${itemName} ?`,
  PublishMultipleMessage: (count: number, moduleName: string) =>
    `Would you like to publish ${count} ${moduleName}s?`,
  PublishRemainingMessage: (items: any[]) =>
    `${items} Manifests do not fulfill the publish criteria. Would you like to still proceed with publishing the remaining Manifests?`,
  restMawb: 'Reset MAWB Information ?',
  flightDeleteSuccess: (itemName: string) => `${itemName} deleted successfully`,
  updateBag: 'Update Bag ?',
  blockShipmet: 'Block Shipment',
  weightConfirmation: 'Weight Confirmation',
  unblockShipment: 'Unblock Shipment',
  advancedSearchHelpModalHeaderText: 'Advanced Search Text Operators',
  fillAllMandatoryFields: 'Fill all the mandatory fields',
  Close: 'Close',
  deleteSingleAttachmentMessage: (itemName: string) =>
    `Do you want to delete the selected ${itemName} ?`,
  deleteBulkAttachmentMessage: (count: number, itemName: string) =>
    `Do you want to delete these ${count} ${itemName} ?`,
  printColumnSelection: 'Choose columns to print',
  organizationCrudModalHeader: 'Organization',
  deleteManifestFromMawb: (id: number) =>
    `Do you want to delete the Manifest ${id}?`,
  mawbPartValidationMessage:
    "Please enter 'A' for the first split, 'B' for the second split, or 'C' for the third split.",
  shipmentSelection: 'Shipment Selection',
  selectArInvoice: 'Select AR Invoice',
  autoRateInvoiceDatePrompt:
    'Select the Invoice Date to apply to the shipment(s) being auto-rated',
  arBulkInvoiceGenerateTitle: 'Generate AR Invoice',
  arBulkInvoiceNoEligibleSelection:
    'Only auto-rated shipments can be used to generate AR Invoices',
  arBulkInvoiceShipmentNotOnPage: (shipmentIdentifierValue: string) =>
    `Shipment '${shipmentIdentifierValue}' is not part of this page. Charges can be added only for shipments on this page`,
  arBulkInvoiceAlreadyInvoicedPartialConfirm: (
    selectedCount: number,
    invoicedCount: number,
    remainingCount: number
  ) => {
    const verb = invoicedCount === 1 ? 'is' : 'are';
    const noun = remainingCount === 1 ? 'shipment' : 'shipments';
    return `Among the selected ${selectedCount} shipments, ${invoicedCount} ${verb} already invoiced. AR Invoice will be generated for the remaining ${remainingCount} ${noun}. Do you want to proceed?`;
  },
  arBulkInvoiceDeleteAll: (count: number, invoiceDate: string) =>
    `Are you sure you want to delete all ${count} AR Invoices dated ${invoiceDate}?`,
  allInvoicesLabel: 'All Invoices',
  arInvoiceBlockedCannotAdd:
    'Blocked shipments could not be added to AR Invoice',
  accountInvoiceHold:
    'Account level configuration is to Hold the shipments from Invoice generation',
  accountInvoiceHoldSkipped: (count: number) =>
    `${count} shipment(s) ignored. Account level configuration is to Hold the shipments from Invoice generation`,
  autoRatingDisabledForAccount:
    'Auto-rating not possible as per the account level configuration',
  arInvoiceMawbBlockedCannotAddSuffix:
    'Thus the MAWB cannot be added to the AR Invoice',
  arInvoiceBlockedMultiple: (count: number) =>
    `${count} of the selected shipments are blocked. Thus the shipments cannot be added to the AR Invoice`,
  arInvoiceBlockedPartialConfirm: (
    eligibleCount: number,
    notEligibleCount: number
  ) =>
    `Out of the selected shipments, ${eligibleCount} ${eligibleCount === 1 ? 'is' : 'are'
    } eligible to generate an AR Invoice and ${notEligibleCount} ${notEligibleCount === 1 ? 'is' : 'are'
    } not. Are you sure you want to proceed to generate an AR Invoice with the eligible shipments?`,
  noteSuccess: 'Note added in shipment successfully',
  bulkRoutingHeader: 'Bulk Routing',
  bulkRoutingDefaultPrompt:
    'Choose a pre-configured route or create a new route to apply routing to the selected shipments.',
  bulkRoutingInfoNote:
    'Information: Choose a pre-configured route or create a new route to apply routing to the selected shipment.',
  bulkRoutingCreateRouteBanner: (origin: string, destination: string) =>
    `Transit points for ${origin} -> ${destination}. Use the grid below to add or edit transit points on this route.`,
  bulkRoutingUnableToFindRoute: 'Unable to find selected route data',
  bulkRoutingTransitPointsRequired:
    'At least one transit point is required to apply bulk routing',
  bulkPodHeader: 'Bulk POD',
  podLinkedShipmentError:
    'POD can be added only to the Master Shipment and not the Linked Shipment',
  podHeldShipmentCannotDeliver: "Held shipments can't be delivered",
  podBlockedShipmentCannotDeliver: "Blocked shipments can't be delivered",
  bulkPodAllHeld:
    "All the selected shipments are Held by customs and can't be delivered",
  bulkPodAllBlocked:
    "All the selected shipments are currently Blocked and can't be delivered",
  bulkPodAllHeldOrBlocked:
    "All the selected shipments are either Held or Blocked and can't be delivered",
  podMarkedDeliveredSuccess:
    "Selected Shipment and it's Linked shipments (if any) have been marked as Delivered",
  podDriverNameInvalid:
    'Driver Name should contain only alphabets and spaces',
  podDriverNameRequired: 'Driver Name is required',
  podCarrierNameRequiredField: 'Carrier Name / POD Name is required',
  podDateTimeRequired: 'Date and Time is required',
  podDateTimeInvalid: 'Invalid Date and Time format',
  podNameMinLength: 'Minimum 1 character is required',
  podNameMaxLength: (maxLength: number) =>
    `Maximum ${maxLength} characters allowed`,
  manifestPodLinkedShipmentsHeader: 'Shipments Linked to the Manifest',
  manifestPodHeldShipments: (heldCount: number) =>
    `There are ${heldCount} held shipments in the Manifest, which are not present in the below grid`,
  manifestPodNoHeldShipments:
    'There are no held shipments in the Manifest. All the shipments are listed in the below grid',
  manifestPodAllHeldBanner:
    'All the shipments are held and no status code shall be applied now',
  manifestPodAllHeldSave:
    "All the shipments are held and status code can't be added now",
  manifestPodAllBlockedSave:
    "All the shipments are blocked and status code can't be added now",
  manifestPodAllHeldOrBlockedSave:
    "All the shipments are held or blocked and status code can't be added now",
  manifestPodNoMatchingShipments: 'No matching shipments found',
  manifestPodViewLinkedShipments: (count: number) =>
    `View ${count} linked shipments`,
  viewAdditionalDetails: 'View Additional Details',
  bulkGatewayHeader: 'Bulk Gateway',
  bulkGatewayValidateSingleMessage: 'Would you like to gateway selected Shipment?',
  bulkGatewayValidateMultipleMessage: (count: number) =>
    `Would you like to gateway all selected ${count} Shipments?`,
  bulkBag: 'Bulk Bag',
  bulkManifest: 'Bulk Manifest',
  bulkMawb: 'Bulk MAWB',
  bulkBagConfirmationMessage: (pieceCount: number, bagExt: string) =>
    `Would you like to bag ${pieceCount === 1 ? 'selected' : 'all the selected ' + pieceCount} Shipment${pieceCount === 1 ? '' : 's'} into ${bagExt}?`,
  partialBulkBagConfirmationMessage: (
    totalPieces: number,
    baggedPieces: number,
    unBaggedPieces: number,
    bagExt: string
  ) =>
    `Out of the selected ${totalPieces} Shipments, ${baggedPieces === 1
      ? `${baggedPieces} Shipment is`
      : `${baggedPieces} Shipments are`
    } already bagged. Would you like to bag remaining ${unBaggedPieces === 1
      ? `${unBaggedPieces} Shipment`
      : `${unBaggedPieces} Shipments`
    } into ${bagExt}?`,
  bulkBaggingErrorMessage: (
    shipmentDestination: string,
    bagDestination: string
  ) =>
    `Shipment's Destination ${shipmentDestination} and entered Bag's destination ${bagDestination} are not same. Hence bulk bagging not possible.`,
  createBagErrorMessage: (
    shipmentDestination: string,
    bagDestination: string
  ) =>
    `Bag's Origin ${shipmentDestination} and entered Bag's destination ${bagDestination} are same. Hence creation of bag not possible.`,
  printWarningContent:
    'Selected Columns may not fit for the selected paper size while printing',
  printWarningContentConfirmation:
    'Are you sure you want to continue printing?',
  summaryPageDownloadCurrentPageWarning:
    'This action will download only the records displayed in current page. Would you like to proceed with it?',
  summaryPagePrintCurrentPageWarning:
    'This action will print only the records displayed in current page. Would you like to proceed with it?',
  mawbManifestOriginDestinationMessage: `The entered Origin or Destination does not match the linked Manifest's Origin and Destination. Please either delink the Manifest or update its Origin and Destination to match.`,
  attachmentsHeader: 'Attachments',
  podDetailsHeader: 'POD Details',
  additionalDetailsHeader: 'Additional Details',
  deleteLinkingSuccess: (itemName: string, action: string, count?: number) =>
    count ? `${count} ${itemName} ${action}` : `${itemName} ${action}`,
  gatewayWeightMismatchMessage:
    'The measured weight and/or measured dimension varies from the data in the fullman file by more than 5%. Would you like to override the existing data?',
  gatewayLocationMismatch: `The scanned shipment doesn't belong to this location. Would you still like to proceed with it?`,
  masterShipmentsHeader: 'Master Shipment Selection',
  gatewayCbpFailureMessage:
    'The shipment has not been cleared by the CBP. Would you like to proceed with the Gateway process?',
  /** AC1: super admin — uncleared OR held shipment; Confirm/Cancel with 5-sec delay. */
  gatewaySuperAdminCbpConfirmMessage:
    'The selected shipment is not eligible for gatewaying as it is still not cleared by customs or held by CBP for other users. Would you like to proceed with gatewaying with your Super Admin permissions?',
  /** AC3: special-perm user — uncleared (not held) shipment; Confirm/Cancel with 5-sec delay. */
  gatewaySpecialPermCbpConfirmMessage:
    'The selected shipment is not eligible for gatewaying as it is still not cleared by customs for other users. Would you like to proceed with gatewaying with your special permissions?',
  shipmentHeldByCustomsCannotGateway:
    'Shipment held by Customs and cannot be gatewayed',
  shipmentHeldByCustomsCannotUnblock:
    'Shipment is held by Customs and cannot be unblocked',
  heldShipmentUnblockConfirm:
    'Shipment is held by Customs. Unblocking will keep it blocked until CBP release. Continue?',
  heldShipmentHandoverPendingStatus: 'Blocked by Customs',
  allShipmentsHeldByCustomsCannotGateway:
    "All the Shipments are held by customs and can't be gatewayed",
  bagGatewayCbpFailureMessage:
    'One or more shipments has not been cleared by the CBP. Would you like to proceed with the Gateway Process?',
  gatewayCreateSuccess: 'Shipment created & gatewayed successfully',
  accountAlreadyExists: (
    accountNumber: string,
    autoGeneratedAccountNumber: string
  ) =>
    `Account ${accountNumber} already exists. \n Click below to use account number ${autoGeneratedAccountNumber} instead, or choose another option.`,
  firstLinkedAccount: `This is the first linked account for '{accountNumber}'. \n\n Do you want account '{accountNumber}' to continue to be used for shipments directly? \n\n If not then existing shipments will be changed to '{newAccountNumber}'.`,
  gatewaySuccess: 'Shipment gatewayed successfully',
  gatewayWeightDimensionCheckPrompt:
    'Do you want the weight and dimension check to be performed?',
  shipmentAlreadyGatewayedAtCurrentLocation:
    "Shipment already gatewayed at the current location and can't be gatewayed again",
  blockedShipmentCannotGateway:
    "Blocked shipment can't be gatewayed until it is unblocked",
  multiPieceGatewayPieceCountPrompt: (count: number) =>
    `There should be ${count} pieces in this Shipment. Please confirm the number of pieces received`,
  multiPieceGatewayPieceCountCbpPrompt: (count: number) =>
    `The shipment has not been cleared by the CBP. If you would like to proceed with the gateway process, kindly confirm the number of pieces received. There should be ${count} pieces in this shipment`,
  multiPiecePhysicalPieceCountPlaceholder:
    'Enter the number of pieces physically received',
  multiPieceShortageSelectionHint: (count: number) =>
    `Kindly select the ${count} shipment${count === 1 ? '' : 's'} that were received and are to be gatewayed`,
  multiPieceSelectionMismatch: (marked: number, selected: number) =>
    `You marked ${marked} shipments as received, but here selected ${selected} shipments`,
  multiPieceGatewayOverageError: (extraCount: number) =>
    `Please create ${extraCount} overage shipment${extraCount === 1 ? '' : 's'} and then proceed to gateway.`,
  /** SPEC-OCS-001 FR-007 (PAC-24802 AC4): overage no longer blocks the multi-piece gateway. */
  multiPieceGatewayOverageInfo: (extraCount: number) =>
    `Recorded pieces will be gatewayed. Please create ${extraCount} overage shipment${extraCount === 1 ? '' : 's'} after gateway.`,
  /** SPEC-GEF-001 / D-GEF-UI-002 */
  noMatchesFound: 'No matches found',
  /** SPEC-GEF-001 / D-GEF-UI-003 */
  allShipmentsGatewayedAlready: 'All shipments gatewayed already',
  createLinkedAccounts: `Would you like to create a Linked account to '{linkedAccountNumber}'? \n \n You can use the auto-generated account number or type an account number of your choice.`,
  createDuplicateShipment: 'Duplicate',
  userCreationHeader: 'User Creation',
  userCreationWarningMessage:
    'Are you sure you want to email the login credentials to the user?',
  userCreationAdditionalMessage:
    'Additionally, you can email the login credentials to another person using the textbox below.',
  createDuplicateShipments: 'Duplicate',
  cancelPopupMessage: 'Are you really sure you want to cancel?',
  noChangesMessage: 'No changes made to save',
  deleteOrganizationMessage: (organizationName: string) =>
    `Are you sure you want to delete the Organization ${organizationName}?`,
  customerServiceAddUpdate: (action: string) =>
    `Customer Service ${action} successfully`,
  accountCreate: 'Account created Successfully',
  DepartmentExists: 'Department already exist',
  DeliveryZoneExists: 'Delivery Zone already exist',
  DeliveryZoneRangeExists:
    'The postal code exists within another delivery zone for the agent',
  APChargeCodeExists: 'AP Charge Code already exist',
  APChargesNoMawbShipment:
    'The entered MAWB does not have any shipments associated with it. Do you want to continue?',
  APUnmappedAgentCode: (action: string[]) =>
    `Received Agent Charge Code ${action.map(code => code).join(', ')} ${action.length === 1 ? 'is' : 'are'
    } not available in our system.`,
  noShipmentsLinkedToMAWB: 'No Shipments Linked to MAWB',
  invalidShipmentIdentifierHeader: 'Invalid Shipment Identifier Value',
  invalidShipmentIdentifierMessage: (
    identifierType: string,
    enteredValue: string
  ) =>
    `The entered ${identifierType} '${enteredValue}' does not exist in the system.\nDo you want to proceed?`,
  identifierTypeLabels: {
    mawb: 'MAWB',
    agentTrackingNumber: 'Agent Tracking Number',
    shipment: 'Shipment',
  },
  APShipmentDuplicateEntry: 'Duplicate Charge',
  APShipmentDuplicateEntryMessage:
    'A charge with the same Charge Amount, Charge Code, and Shipment Details already exists in the system.',
  DepartmentCombinationNotExists: `Department Combination doesn't exist`,
  departmentAddUpdate: (action: string) => `Department ${action} successfully`,
  deliveryZoneGridAddUpdate: (action: string) =>
    `Delivery Zone ${action} successfully`,
  apChargeCodeGridAddUpdate: (action: string) =>
    `AP Charge Code ${action} successfully`,
  apChargeGridAddUpdate: (action: string) => `AP Charge ${action} successfully`,
  arChargeGridAddUpdate: (action: string) => `AR Charge ${action} successfully`,
  addUser: 'Add User',
  fromUserEmail: 'FROM_BASIC_SEARCH',
  AddUserInfo: 'Would you like to add a User?',
  rolesAndPermissionTooltip:
    "At least one of the fields: 'Department,' or 'Role' must be filled. Leaving all two blank is not allowed",
  shipmentGatewayTogglePermissionDenied:
    'You do not have permission to access Gateway',
  bagGatewayTogglePermissionDenied:
    'You do not have permission to access Gateway & Bag & Manifest by Incoming Container',
  dimensionalWeightTooltip:
    'Dimensional weight is Auto-calculated from Length, Width & Height divided by 139',
  masterShipmentIsMasterTooltip: 'This is a Master Shipment',
  hscodesAliaInfoTooltip:
    'Multiple aliases can be entered, please separate each alias with a comma',
  customerServiceMarkUpTooltip:
    'Mark up (%) mentioned here are applicable only on Weight based charges',
  ufoCustomerServiceGridInfoMessage:
    'Customer Service grid not applicable for UFO Account',
  groupRoleDepartmentMaxValidation: (requestType: string) =>
    `${requestType} Name should be only alphabets and more than 2 characters and unique and required`,
  deleteGroupDepartmentRoleMessage: (requestType: string, name: string) =>
    `Do you want to delete the ${requestType} ${name}?`,
  softValidationMessage:
    'Please fill out all fields and check all inputs before proceeding',
  numberRanges: 'Number Ranges',
  userLinked: 'User Linked',
  singleGrpDeptRoleLinkedUserMessage:
    'The selected permission is associated with existing user and cannot be deleted',
  multipleGrpDeptRoleLinkedUserMessage: (usersCount: number) =>
    `All of the ${usersCount} selected permissions are associated with the existing user(s) and cannot be deleted`,
  singleGrpDeptRoleInfoLinkedUserMessage:
    'This permission is associated with existing user and cannot be deleted',
  linkeUnlinkedUserMessage: (
    totalCount: number,
    linkedCount: number,
    unLinkedCount: number
  ) =>
    `Out of the selected ${totalCount} permissions, ${linkedCount} ${linkedCount == 1 ? 'permission is' : 'permissions are'
    } associated with existing users and cannot be deleted.
  Would you like to delete the remaining ${unLinkedCount} ${unLinkedCount == 1 ? 'permission' : 'permissions'
    }?`,
  airlineTooltip: `At least one of the fields: 'Airline Code' or 'ICAO Code' must be filled. Leaving all two blank is not allowed`,
  DiscardCommodityChangesContent:
    '"A commodity has been added but not saved. Would you like to save the commodity before saving the shipment? Click on "Return to Save Commodity" to save the added commodity, or click on "Proceed with Shipment Save" if you do not wish to add the commodity to the shipment."',
  DiscardCommodityChangesHeader: 'Commodity not saved',
  deleteStatusCodeMessage: (requestType: string, name: string) =>
    `Do you want to delete the ${requestType} ${name}?`,
  deleteNumberRangesMessage: `Are you sure you want to delete the Number Range `,
  deleteAgentsMessage: (agentName: string) => `Are you sure you want to delete the Agent ${agentName}?`,
  errorLog: 'Error Log',
  cbpStatusDescriptions: 'CBP Status Description',
  NoShipmentClearance: `Shipment doesn't need clearance`,
  blockedShipmentCannotBeSentForClearance:
    'Blocked shipment cannot be sent for clearance',
  NoBagManifestMawbClearance: (moduleName: string) =>
    `Shipments of the ${moduleName} doesn't need clearance`,
  NoShipmentsForBagManifestMawbClearance: (
    isSingle: boolean,
    moduleName: string
  ) =>
    `${isSingle ? moduleName + " doesn't" : 'None of the ' + moduleName}  have Shipment associated. Cannot be sent for clearance`,
  AgentType: 'Agent Type',
  EventDescription: 'Event Description',
  ServiceType: 'Service Type',
  Routing: 'Routing',
  setCBPResponse: 'Set CBP Response',
  ServiceTypeGroups: 'Service Type Groups',
  ServiceTypeGroup: 'Service Type Group',
  responseInformation: 'Response Information',
  sessionExpiredMessage:
    'Your session has expired. Please log in again to continue.',
  passwordChangedMessage:
    'Your password has been changed. Please log in using your new password.',
  passwordChangeSuccess: 'Your password has been updated successfully.',
  ClearanceType: 'Clearance Type',
  serviceablePopupMessage: (accountNumber: any) =>
    `Are you sure you want to mark the Account ${accountNumber} as Serviceable?`,
  nonServiceablePopupMessage: (accountNumber: any) =>
    `Are you sure you want to mark the Account ${accountNumber} as Non-serviceable?`,
  deleteServiceTypeMessage: (serviceType: any) =>
    `Do you want to delete the Service type ${serviceType}?`,
  deleteAirlineMessage: (airline: any) =>
    `Do you want to delete the Airline ${airline}?`,
  Airline: 'Airline',
  deleteAirportMessage: (airport: any) =>
    `Do you want to delete the Airport ${airport}?`,
  Airport: 'Airport',
  cbpSingleShipmentError: (messageList: string[]) => {
    const formattedSections = messageList.map(msg => `- ${msg}`).join('\n');
    return `Shipment can't be sent for clearance. Please review and correct the errors below: \n\n${formattedSections}`;
  },
  cbpSingleBagManifestMAWBError:
    'Ensure all Shipments are eligible for selected clearance channel - based on their Origin, Destination, HS Code and value. If any Shipment is failing any of the above, none of the Shipment will be sent for clearance',
  domesticShipmentError: 'Shipment does not require clearance',
  hsCodeMissing: 'HS Code information is missing',
  shipmentValueExceeds: (clearanceChannel: string) =>
    `Shipment value exceeds $800 limit for ${clearanceChannel}`,
  clearanceChannelNotConfigured:
    'ACAS and AAMS clearance channels are not configured. Contact your administrator.',
  noNeedClearanceForAccount:
    'Shipments of this account needs no clearance as per the Account configuration',
  clearanceTypeNotConfigured:
    'No clearance type is configured for the selected shipments. Contact your administrator.',
  bagManifestMAWBShipment: (moduleName: string) =>
    `None of the selected ${moduleName} with shipments are valid for the selected clearance channel`,
  deleteClearanceTypeMessage: (clearacneType: any) =>
    `Do you want to delete the Clearance Type  ${clearacneType}?`,
  setRangeHeader: 'Set Range(s)',
  createManifestErrorMessage: (
    bagDestination: string,
    manifestDestination: string
  ) =>
    `Manifest's Origin ${bagDestination} and entered Bag's destination ${manifestDestination} are same. Hence creation of bag not possible.`,
  bulkManifestingConfirmationMessage: (count: number, manifestNo: string) =>
    `Would you like to Manifest ${count === 1 ? 'selected' : 'all the selected ' + count} Bag${count === 1 ? '' : 's'} into ${manifestNo}?`,
  bulkManifestingErrorMessage: (destination: string, bagDestination: string) =>
    `Bags Destination ${destination} and entered Manifest's destination ${bagDestination} are not same. Hence bulk manifest is not possible.`,
  partialBulkManifestConfirmationMsg: (
    totalCount: number,
    manifestedPieces: number,
    unManifestedPieces: number,
    manifestNumber: string
  ) =>
    `Out of the selected ${totalCount} bags, ${manifestedPieces === 1
      ? `${manifestedPieces} bag is`
      : `${manifestedPieces} bags are`
    } already Manifested. Would you like to Manifest remaining ${unManifestedPieces === 1
      ? `${unManifestedPieces} bag`
      : `${unManifestedPieces} bags`
    } into ${manifestNumber}?`,
  numberRangeConfirmationText: 'Number Generation Confirmation',
  ibcUsersPanelNote: 'IBC users responsible for managing this account',
  ibcUsersEmailPlaceholder: 'Enter email addresses (comma separated)',
  ibcUsersEmailHint: 'e.g. jane@example.com, john@example.com',
  ibcUsersInvalidSelection: 'Select existing IBC users from the list',
  ibcUsersLinkedSuccess: 'IBC users linked successfully',
  ibcUsersUnlinkedSuccess: 'IBC user removed successfully',
  linkIbcUsersTitle: 'Link IBC Users',
  linkIbcUsersSelectedAccounts: (count: number) =>
    `${count} accounts selected`,
  accountUserResendCredentialsMessage:
    'By clicking "Send Credentials" an email with the user\'s login details will be sent to their registered email address.',
  sendCredentialsMessage: 'Send Credentials',
  nonServiceableAccountUserResendErrorMessage:
    'The selected account is Non-Serviceable, and credentials cannot be sent.',
  nonServiceableAccountUserCreationErrorMessage:
    'The selected account is Non-Serviceable, and new user cannot be created.',
  ServiceTypeGroupsDownload: 'Service_Type_Groups',
  partialBulkMawbedConfirmationMsg: (
    totalCount: number,
    mawbPieces: number,
    unMawbedPieces: number,
    mawbNumber: string
  ) =>
    `Out of the selected ${totalCount} Manifests, ${mawbPieces === 1
      ? `${mawbPieces} Manifest is`
      : `${mawbPieces} Manifests are`
    } already MAWBed. Would you like to MAWB remaining ${unMawbedPieces === 1
      ? `${unMawbedPieces} Manifest`
      : `${unMawbedPieces} Manifests`
    } into ${mawbNumber}?`,
  bulkMAWBConfirmationMessage: (count: number, MawbNo: string) =>
    `Would you like to MAWB ${count === 1 ? 'selected' : 'all the selected ' + count} Manifest${count === 1 ? '' : 's'} into ${MawbNo}?`,
  bulkMawbErrorMessage: (destination: string, bagDestination: string) =>
    `Manifest's Destination ${destination} and entered MAWB's destination ${bagDestination} are not same. Hence bulk MAWB is not possible.`,
  bulkMawbErrorMessageWithOiginDestination: (
    destination: string,
    mawbDestination: string,
    origin: string,
    mawbOrigin: string
  ) =>
    `Manifest’s Origin ${origin} and Destination ${destination} and entered MAWB’s Origin ${mawbOrigin} and Destination ${mawbDestination} are not same. Hence Bulk MAWB not possible.`,
  numberRangeSingleDownload: 'Number Range',
  discardPopupMessage: 'Are you really sure you want to discard the changes?',
  bulkMawbErrorMessageForOrigin: (origin: string, mawbOrigin: string) =>
    `Manifest's Origin ${origin} and entered MAWB's origin ${mawbOrigin} are not same. Hence bulk MAWB not possible.`,
  createLinkedAccountInfoScreen: `Would you like to create a Linked account to '{linkedAccountNumber}'?`,
  createLinkedAccountsInfoWithNew: (existAccount: string, newAccount: number) =>
    `Would you like to create a Linked account to ${existAccount}? \n \n  You can use the next available auto-generated account number ${newAccount} or use the account number you have entered.`,
  serviceTypeGroupDeleteFailure:
    'This Service Type Group is associated with an existing Service Type(s) and cannot be deleted',
  linkedAccountsHeader: 'Linked Accounts',
  facilityTypeChangeWarningMsg:
    'Cannot change type to Airport while airports are associated. Please remove them first',
  attachments: 'Attachments',
  PodDetails: 'POD Details',
  additionalDetails: 'Additional Details',
  cancelStatusCodePopupMessage:
    'Changes made to the Status Codes are not saved. Would you like to discard the changes?',
  confirmStatusCodeChange: 'Please Confirm',
  statusCodeDescChangesMsg:
    'Making change to the Code will clear the existing attachment and description. Would you like to Go ahead with it?',
  invalidAccountNumber:
    'No Master Account exists for the entered account number. Please create a Master Account first, ensuring the account number ends with "00"',
  ibcChargeCode: 'IBC Charge Code',
  deleteIbcChargeCodeMessage: (code: any) =>
    `Do you want to delete the IBC Charge Code ${code}?`,
  apChargeCodes: 'AP Charge Codes',
  apChargeCode: 'AP Charge Code',
  noAPChargeCodes: 'Selected agent do not have any Agent Charge Code data.',
  accessorialCharges:
    'Existing charges are present in the Accessorial Charges grid. Uploading a new charge sheet will overwrite the current charges. Do you want to proceed?',
  weightBasedCharges:
    'Existing charges are present in the Weight Based Charges grid. Uploading a new charge sheet will overwrite the current charges. Do you want to proceed?',
  hawbRolesInfoMessage:
    'All roles associated with the shipment, including those of the Shipper and Consignee, are managed from this facility.',
  miscellaneousHawbInfoMessage:
    'This section allows users to capture supplementary details and miscellaneous information about the shipment. It provides a space to include any relevant information that helps build a complete picture of the shipment.',
  hawbEventMessage:
    'Key updates regarding the shipment are listed under the Events section.',
  commodityRolesInfoMessage:
    'All roles associated with the commodity, including those of the Shipper and Consignee, are managed from this facility.',
  miscellaneousCommodityInformationText:
    'This section allows users to capture supplementary details and miscellaneous information about the commodity. It provides a space to include any relevant information that helps build a complete picture of the commodity.',
  hawbCommodityEventMessage:
    'Key updates regarding the commodity are listed under the Events section.',
  bagStatusCodeMessage:
    "The status code indicates the current state or condition of the bags. It is essential to select a status code that accurately reflects the bags' progress or status. Consistent use of these codes is vital for effective tracking and management of the bags.",
  bagEventMessage:
    'Key updates regarding the bag are listed under the Events section.',
  copyRateScale: 'Entered Rate Scale already exists',
  hawbCommodityBulkEdit: 'Commodity cannot be edited in Bulk Edit',
  minimumNumberRangeDownload:
    'Please select a file type and at least one number range to proceed with the download.',
  apInvoiceFetchFromSFTPMessage:
    'Agent Invoices from the configured SFTP/FTP source will be automatically synced on a regular basis. Once the Invoices are available, you can select, and create AP invoices directly from the list below.',
  apInvoiceFetchFromSFTPWarningMessage:
    'AP Invoices from different agents have been selected. Please select invoices from the same agent to proceed.',
  dimensionalWeightFactorTooltip: 'Used to calculate the Dimensional weight',
  dimensionalMawbWeightTooltip:
    'Length * Width * Height / Dimensional weight factor',
  apPublish: 'Publish Invoice',
  apPublishConfirmationMsg: (totalCount: number, unpublishData: number) =>
    `Out of ${totalCount} invoices, only ${unpublishData} have not been published yet. Would you like to send only the unpublished invoices to GP?`,
  singlePublishSuccessMessage: (itemName: string, text: string) =>
    `${itemName} Invoice  ${text} to GP Successfully`,
  bulkApToGpSuccessMessage: (count: number | string) =>
    `${count} AP Invoice Published to GP Successfully`,
  singleApPublish: (invoice: number | string, item: string) =>
    `Would you like to Publish ${invoice} Invoice  ${item} ?`,
  multiplApPublish: `Would you like to Publish all selected AP Invoices?`,
  apTpGpAlreadyMulti: (count: number | string) =>
    `All selected AP Invoices are already Published`,
  apTpGpAlreadySingle: `AP Invoice is already Published`,
  rePublishWarningMsg: (invoice: number | string, itemName: string) =>
    `Do you want to republish the selected ${itemName} Invoice ${invoice} ?`,
  bulkARToGpSuccessMessage: (count: number | string) =>
    `${count} AR Invoice Published to GP Successfully`,
  multiplARPublish: `Would you like to Publish all selected AR Invoices ?`,
  publishARInvoiceTitle: 'Publish AR Invoice',
  arTpGpAlreadyMulti: (count: number | string) =>
    `All selected AR Invoices are already Published`,
  arTpGpAlreadySingle: `Selected AR Invoice is already Published`,
  arSinglePublishConfirmation: (invoiceNumber: string) =>
    `Would you like to publish AR Invoice '${invoiceNumber}'?`,
  arInvoicePublishedSuccess: (count: number) =>
    count > 1
      ? `Selected ${count} AR Invoices published to GP and Customer successfully.`
      : 'Selected AR Invoice published to GP and Customer successfully.',
  arMultipleInvoiceEmailSubject: (accountInfo: string, invoiceNumbers: string) =>
    `AR Invoices for ${accountInfo}: ${invoiceNumbers}`,
  allVendorReconInvoicesCreatedSuccessfully:
    'All vendor recon invoices have been created successfully',
  sftpFtpModalHeaderMessage: 'Load AP Invoices from SFTP/FTP',
  pleaseSelectAtLeastOneFile: 'Please select at least one file',
  invalidFileSelection: 'Invalid file selection',
  vendorReconProcessedSuccessfully: 'AP Invoice created successfully',
  apInvoiceUpdatedSuccessfully: 'AP Invoice updated successfully',
  noAPInvoiceDataReceived: 'No AP Invoice data received',
  rePublishAPAR: (item: string, count: number) =>
    `If so, type "${item} ${count}" and click Confirm`,
  editPublishedInvoiceConfirmation: (invoiceNumber: string) =>
    `The selected Invoice is already 'Published'. Please provide a confirmation to proceed with editing it, Please type "Invoice ${invoiceNumber}" and click Confirm`,
  editPublishedInvoiceConfirmText: (invoiceNumber: string) =>
    `"Invoice ${invoiceNumber}"`,
  noChargePublishMessage: (moduleName: string) =>
    `${moduleName} Invoice cannot be published without any charge`,
  errorLogsHeaderText:
    'One or more fields contain invalid or mismatched data types, as listed below.Please review your file and ensure all values meet the required format.\nPlease correct the data and re-upload the file.',
  statusCodeAssociationError:
    'The Status Code is associated with an existing Shipments and cannot be deleted.',
  accountEventMessage:
    'Key updates regarding the account are listed under the Events section.',
  scanShipment: 'Scan Shipments',
  noChargeApToPublish: `None of the selected AP invoices have charges and cannot be Published to GP.`,
  someHaveChargeApToPublish: (
    total: number,
    noChargeCount: number,
    proceedCount: number,
    invoiceText: string
  ) =>
    `Out of ${total} invoices selected, ${noChargeCount} ${noChargeCount > 1 ? 'invoices' : 'invoice'
    } have no AP charges. Do you want to send only the remaining ${proceedCount} ${invoiceText} to GP?`,
  arInvoiceidRequired:
    'arInvoiceId is required for delete operation. Please ensure a valid invoice exists.',
  chargeIdRequired:
    'Charge ID is required for delete operation. Please ensure a valid charge exists.',
  noChargeArToPublish: `None of the selected AR invoices have charges and cannot be Published to GP.`,
  arNoChargesCannotBe: (action: string) =>
    `None of the selected AR invoices have charges and cannot be ${action}`,
  arNoChargesActionPublished: 'Published',
  arNoChargesActionSentToCustomer: 'Sent to Customer',
  arNoChargesActionSentToGP: 'Sent to GP',
  someHaveArChargeToPreceed: (
    arTotal: number,
    arNoChargeCount: number,
    arProceedCount: number,
    invoiceText: string
  ) =>
    `Out of ${arTotal} invoices selected, ${arNoChargeCount} ${arNoChargeCount > 1 ? 'invoices' : 'invoice'
    } have no AR charges. Do you want to send only the remaining ${arProceedCount} ${invoiceText} to GP?`,
  autoRatingNotAllowed:
    'Selected shipments are missing either the Outbound Status Code or they have not received AP charges from the agent. Hence, Auto-rating can’t be performed.',
  specialServiceTypeConfirm: (count: number) =>
    `The selected Shipment${count > 1 ? 's are' : ' is'
    }  'Specials' and can't be auto-rated. Would you like to create a new invoice?`,
  noARToPublish: 'No unpublished invoices with charges to send.',
  someHaveEligibleShipmentProceed: (
    notEligibleCount: number,
    totalSelected: number,
    eligibleCount: number
  ) =>
    `${notEligibleCount} of ${totalSelected} selected Shipment${totalSelected === 1 ? ' is' : 's are'
    } not eligible for auto‑rating. The remaining ${eligibleCount} Shipment${eligibleCount === 1 ? '' : 's'
    } will be auto‑rated. Do you want to proceed?`,
  unAutoRateShipmentConfirmation: (count: number = 1) =>
    `This will remove all the Auto-rated charges of the selected ${count === 1 ? 'Shipment' : 'Shipments'} in the corresponding AR Invoices. Would you really like to proceed with it?`,
  arInvoicePublishedGpUnAutoRateNotPossible:
    'AR Invoice already published to GP. Un-Auto-Rate not possible now',
  arWeightAfterGpPublishTooltip:
    "After publishing to GP, weight can't be updated",
  arWeightChangeImpactInvoice:
    'You made some changes to the weight which may impact the AR invoice. Would you like to update the AR invoice as well (if applicable)?',
  arOutlyingChangeImpactInvoice:
    'You changed the Outlying Shipment flag, which may change the AR charges. Would you like to recalculate the AR invoice charges as well (if applicable)?',
  arWeightChangeImpactInvoiceTitle: 'Confirm',
  shipmentAlreayAutoRated: `Shipment is already auto rated`,
  autoRateHawbSuccess: `Shipments Auto Rated Successfully`,
  consigneeZipRequiredBeforeAutoRate:
    'ZIP Code for Consignee mandatory before Auto-rating',
  destinationZipNotConfiguredInDeliveryZones:
    'Destination ZIP Code of the selected shipment not configured in Delivery Zones. Kindly update',
   someShipmentsDestinationZipNotInDeliveryZonesConfirm: (
    notConfiguredCount: number,
    totalSelected: number,
    remainingCount: number
  ) => {
    const shipmentWord = (n: number) =>
      n === 1 ? 'Shipment' : 'Shipments';
    return `Destination ZIP Code of ${notConfiguredCount} ${shipmentWord(notConfiguredCount)} of the selected ${totalSelected} ${shipmentWord(totalSelected)} not configured in Delivery Zones. Would you like to Auto-rate the remaining ${remainingCount} ${shipmentWord(remainingCount)}?`;
  },
  allSelectedShipmentsDestinationZipNotInDeliveryZones: (
    totalSelected: number
  ) =>
    `All ${totalSelected} selected shipments have a Destination ZIP Code that is not configured in Delivery Zones.`,
  someShipmentsMissingConsigneeZipConfirm: (
    missingCount: number,
    totalSelected: number,
    remainingCount: number
  ) => {
    const verbPhrase =
      missingCount === 1 ? "doesn't have" : "don't have";
    const shipmentWord =
      remainingCount === 1 ? 'Shipment' : 'Shipments';
    return `${missingCount} out of ${totalSelected} selected Shipments ${verbPhrase} ZIP Code for Consignee. Would you like to Auto-rate the remaining ${remainingCount} ${shipmentWord}?`;
  },
  allSelectedShipmentsMissingConsigneeZipSummary: (
    totalSelected: number
  ) =>
    `All ${totalSelected} selected Shipments do not have a ZIP Code for Consignee.`,
  heldReportSuccess: 'Held report',
  scanBags: 'Scan Bags',
  bagOriginAndDestinationMismatch: (origin: string, destination: string) =>
    `Bag origin (${origin}) and destination (${destination}) do not match the manifest's origin and destination.`,
  bagOriginMismatch: (origin: string) =>
    `Bag origin (${origin}) does not match the manifest's origin.`,
  bagDestinationMismatch: (destination: string) =>
    `Bag destination (${destination}) does not match the manifest's destination.`,
  scannedBagsAddedSuccess: 'Scanned Bags added to the Manifest successfully',
  currencyInformation: 'Currency Information',
  currencyManagement: 'Currency Management',
  currenctManagementInfoDetailsMessage:
    'The grid below lists the latest 10 changes made to this currency’s conversion rate.',
  noCurrencyAvailable:
    'No currencies available to select. Every currency in the list is already configured with a conversion rate.',
  autoSync: 'Auto Sync',
  autoSyncWarningMsg:
    'Auto Sync will automatically update your conversion rates using the latest available rates from the web.\n\nYour current rates will be overwritten. Do you want to continue?',
  deleteCurrencyRateMessage: (code: any) =>
    `Do you want to delete this Currency ${code}?`,
  arPartialPublishConfirmation: (
    total: number,
    noCharges: number,
    remaining: number,
    destination: string
  ) =>
    `Out of ${total} invoices selected, ${noCharges} invoices have no AR charges. Do you want to send only the remaining ${remaining} invoices to ${destination}?`,
  someHaveChargeARToPublish: (
    total: number,
    noARChargeCount: number,
    invoiceText: string,
    count: number
  ) =>
    `Out of ${total} invoices selected, ${noARChargeCount} ${noARChargeCount > 1 ? 'invoices' : 'invoice'
    } have no AR charges. Do you want to send only the remaining ${count} ${invoiceText} to GP?`,
  mawbPalletGridAddUpdate: (action: string) => `Pallet ${action} successfully`,
  apArEditConfirmation: (invoice: any, type: any) =>
    `The ${type} invoice ${invoice} has already been published to GP. Are you sure you want to proceed with editing it?`,
  apInvoicePublishedGpChangesWarning:
    'Invoice already published to GP. Changes made won\'t reflect in GP',
  apArEditConfirmationGpAndCustomer: (invoice: string) =>
    `The invoice ${invoice} has already been published to GP and Customer. Are you sure you want to proceed with editing it?`,
  lineHaulInfoMessage:
    'Charge Mismatch – The linehaul charge for the highlighted MAWB in the AP Charges grid doesn’t match the linehaul charge in the MAWB module.',
  arPublishedEditConfirmation: (invoiceNumber: string) =>
    `The invoice ${invoiceNumber} has already been published to Customer. Are you sure you want to proceed with editing it?`,
  sendInvoiceViaEmail: 'Send Invoice via Email',
  arInvoicePublishedToCustomer: 'AR Invoice Published to Customer Successfully',
  addChargesToExistingInvoice:
    'Would you like to add new charges to the existing Invoice?',
  arInvoiceEmailSubject: (
    invoiceNumbers: string,
    accountNumber: string,
    accountName: string,
    invoiceDates: string
  ) =>
    `AR ${invoiceNumbers} for ${accountNumber} - ${accountName} - ${invoiceDates}`,
  arSpecialInvoiceConfirmation: (
    totalCount: number,
    specialCount: number,
    destination: string
  ) =>
    `Are you sure you want to publish ${totalCount} invoices to ${destination}, out of which ${specialCount} are AR Invoices with Specials Shipment?`,

  arAllSpecialInvoiceConfirmation: (totalCount: number, destination: string) =>
    `Are you sure you want to publish ${totalCount} AR invoices with Specials Shipment to ${destination}?`,
  arSingleSpecialInvoiceConfirmation: (
    invoiceNumber: string,
    destination: string
  ) =>
    `Are you sure you want to publish ${invoiceNumber} AR Invoice with Specials Shipment to ${destination}?`,
  arSingleSpecialInvoiceRepublishConfirmation: (
    invoiceNumber: string,
    destination: string
  ) =>
    `Are you sure you want to re-publish ${invoiceNumber} AR Invoice with Specials Shipment to ${destination}?`,
  publishDestinationCustomer: 'Customer',
  publishDestinationGP: 'GP',
  currencyUsd: 'USD',
  hawbAlreadyAutoRated: `Shipment is already auto rated.`,
  hawbAutoRateAlreadyMulti: (count: number | string) =>
    `All selected Shipment are already auto rated.`,
  serviceTypeErrorMsg: 'Please fill the Service Type',
  accountRouteAddUpdate: (action: string) =>
    `Routing Criteria ${action} successfully`,
  minMaxValueValidation: 'Minimum value should be less than the maximum value',
  routingAlreadyExist: 'Entered Routing Criteria already exists',
  arFuelChargeAddWarning:
    'Please add the Base Delivery Charge first. Fuel Surcharge can only be applied after the Base Delivery Charge is added, as it is calculated based on that BAS charge.',
  ticketingSystemMsg: 'Create a ticket in the ticketing system',
  getPublishManifestSuccessWithTicketMessage: (manifestCount: number) =>
    `${manifestCount > 1 ? 'Manifests' : 'Manifest'} published and ticket created successfully${manifestCount > 1 ? '.' : ''}`,
  publishMMAWBSuccessWithTicket: 'MAWB published and ticket created successfully',
  publishDestinationGPAndCustomer: 'GP and Customer',
  routingRequiredForBagging:
    'Please choose a route for the shipment before bagging it',
  noAgentChargeCodesForSelectedAgent: 'No agent charge codes are associated with the selected agent',
  confirmAgentChangeMessage: 'All added charges in AP Charges grid will be lost if you change the agent. Do you want to continue?',
  confirmAgentChange: 'Confirm Agent Change',
  serviceTypeNotConfiguredWarning: 'Account chosen has not subscribed to the selected Service Type. Would you still like to go ahead with the Service Type selection?',
  serviceTypeWarningHeader: 'Service Type Warning',
  arMultiShipment: 'Multiple Shipment Match Found',
  arMultiShipmentMessage: (itemName: string) =>
    `There are multiple shipments associated with the entered ${itemName}. Please select all the appropriate shipments to proceed.`,
  arMultiShipmentNotAutoRatedWarning: 'One or more selected shipments are neither auto-rated nor special shipments and therefore cannot be added',
  downloadArInvoiceTitle: 'Download AR Invoice',
  autoRateInvoiceDateTitle: 'Please choose the Invoice date for the shipments',
  arInvoiceDownloadSuccess: 'AR Invoice downloaded successfully',
  arDifferentCustomerAccountPublish:
    'Selected invoices do not belong to the same customer account and therefore cannot be published',
};

export const errorMessagesForMasterShipmentLinking = {
  validationFailedLogHeader: 'Master Shipment Validation Failed',
};

export const errorMessagesForAutoRate = {
  partialAutoRate: 'Partial Auto Rating',
  allSelectedShipmentsNotEligible: 'All selected shipments are not eligible for auto rating.',
  autoRatingFailedLogHeader: 'Auto Rating Failed',
  autoRatingSingleShipmentFailedIntro:
    'Auto-rating of the selected Shipment not possible due to the following:',
  shipmentNotEligibleMessage: 'Shipment does not contain AP invoice details',
  someAlreadyAutoRatedProceed: (
    alreadyRatedCount: number,
    totalSelected: number,
    remainingCount: number
  ) =>
    `${alreadyRatedCount} of ${totalSelected} selected Shipment${totalSelected === 1 ? ' is' : 's are'} already auto rated. The remaining ${remainingCount} Shipment${remainingCount === 1 ? '' : 's'} will be auto rated. Do you want to proceed?`,
  parcelAutoRatingPreconditionFailed:
    "One or more shipments selected doesn't meet the Auto-rating pre-condition. Kindly update the shipments before auto-rating",
  partialAutoRatingPreListCaption: 'Auto-rating failed due to the following:',
}

export const actionText = {
  deleted: 'deleted',
  modified: 'modified',
  pinned: 'pinned',
  unpinned: 'unpinned',
  saved: 'saved',
  update: 'updated',
  pin: 'Pin',
  Saved: 'Saved',
  published: 'published',
  added: 'added',
  samesAsBillingAddress: 'Same as Address',
  removedFromMAWB: 'removed from the MAWB',
  sendCbpClearance: 'Send for Clearance',
  generateLabel: 'Print Label',
  sendTo: 'EDI',
  miscAccCannotBeDeleted: `Miscellaneous account can't be deleted`,
  ufoAccCannotBeDeleted: `UFO account can't be deleted`,
  copyRateScale: 'Copy Rate Scale',
  copyRoute: 'Copy',
  selectRouteToCopy: 'Select a Route to Copy',
  uploadFile: 'Upload File',
  cannotPublish: 'Cannot publish without charge',
  cannotPublishWithoutCharge: 'Cannot be sent to GP/Customer without a charge',
  recieveAPInvoice: 'Load AP Invoices from SFTP/FTP',
  autoRate: 'Auto Rate',
  autoRateText: 'Auto-Rate',
  unAutoRateText: 'Un-Auto-Rate',
  arInvoice: 'AR Invoice',
  generateArInvoice: 'Generate AR Invoice',
  dashboardRefresh: 'Refresh',
  capsUpdate: 'Updated',
  capsAdd: 'Added',
  capsDelete: 'Deleted',
  information: 'Information',
  previous: 'Previous',
  capsRemoved: 'Removed'
};

export const searchLimitCount = {
  pin: 10,
  saved: 1000,
};

export const SingleDeleteHeaderText = {
  Shipment: 'Shipment',
  Bag: 'Bag',
  Manifest: 'Manifest',
  Search: 'Search',
  Commodity: 'Commodity',
  Mawb: 'Mawb',
  RecentSearch: 'Recent search',
  RecentSearches: 'Recent searches',
  savedSearch: 'Saved search',
  savedSearches: 'Saved searches',
  Flight: 'Flight',
  Flights: 'Flights',
  Role: 'Role',
  Attachment: 'attachment',
  BillingAddress: 'Billing Address',
  Attachments: 'attachment(s)',
  SubAccounts: 'Sub Accounts',
  CustomerService: 'Customer Service',
  Account: 'Account',
  Organization: 'Organization',
  Department: 'Department',
  User: 'User',
  RolesAndPermissions: 'Roles & Permissions',
  NumberRanges: 'Number Range',
  ServiceTypes: 'Service Type',
  ServiceTypeGroup: 'Service Type Group',
  Agent: 'Agent',
  HsCode: 'HS Code',
  AgentType: 'Agent Type',
  Airline: 'Airline',
  Airport: 'Airport',
  ClearanceTypes: 'Clearance Type',
  AccountUsers: 'Users',
  ExternalUsers: 'External Users',
  IbcUsers: 'IBC Users',
  numberRange: 'Number Range',
  MawbCaps: 'MAWB',
  StatusCode: 'Status Code',
  facility: 'Facility',
  deliveryZone: 'Delivery Zone',
  ibcChargeCode: 'IBC Charge Code',
  apChargeCode: 'AP Charge Code',
  rateScaleDownloaded: 'Rate_Scale',
  rateScaleSuccess: 'Rate Scale',
  apCharges: 'AP Charge',
  arCharges: 'AR Charge',
  accountsPayable: 'Accounts Payable',
  apInvoice: 'AP Invoice',
  accountsReceivable: 'AR Invoice',
  singleInvoice: 'invoice',
  multipleeInvoice: 'invoices',
  publishedText: 'Published',
  multiple: 'Multiple',
  funtionText: 'function',
  autoRate: 'Auto-Rate',
  held: 'Held',
  currency: 'Currency',
  pallet: 'Pallet',
  Routing: 'Routing',
  Route: 'Route',
  Routes: 'Routes',
  RouteDownload: 'Route(s)',
  TransitPoint: 'Transit Point',
  linehaulCharge: 'Linehaul Charge',
  arInvoiceNumberText: 'AR Invoice Number',
  RoutingCriteria: 'Routing Criteria',
  FSC: 'FSC',
  BAS: 'BAS',
  agentShipmentTracker: 'Agent Tracking Number',
  ArInfoText: 'accounts-receivable-information',
  arText: 'accounts-receivable',
  mawbInformationPage: 'mawb-information',
  hawbInformationPage: 'hawb-information',
};

export const ShipmentBagParentEventMessages = {
  bagged: (shipmentLabel: string) => `${shipmentLabel} Bagged`,
  unbagged: (shipmentLabel: string) => `${shipmentLabel} Unbagged`,
} as const;

export const BulkDeleteItemNames = {
  Shipments: 'Shipments',
  Bags: 'Bags',
  Manifests: 'Manifests',
  Searches: 'Searches',
  Commodities: 'Commodities',
  Mawbs: 'MAWBs',
  Flights: 'Flights',
  Flight: 'Flight',
  Agents: 'Agents',
  AgentInformation: 'Agent Information',
  AdvancedSearch: 'Advanced Search',
  Commodity: 'Commodity',
  Accounts: 'Accounts',
  AccountInfo: 'Account Information',
  StatusCodes: 'Status Codes',
  RolesAndPermissions: 'Roles & Permissions',
  RolesAndPermissionsInfo: 'Roles & Permissions Information',
  UserManagement: 'User Management',
  UserManagementInfo: 'User Management Information',
  CustomerServices: 'Customer Services',
  Users: 'Users',
  Departments: 'Departments',
  RolesPermissions: 'Roles & Permissions',
  Airports: 'Airports',
  AirportInfo: 'Airport Information',
  User: 'User',
  NumberRanges: 'Number Ranges',
  Airlines: 'Airlines',
  AirlineInfo: 'Airline Information',
  AgentTypes: 'Agent Types',
  AgentType: 'Agent Type',
  HSCodes: 'HS Codes',
  HSCodeInfo: 'HS Code Information',
  Facilities: 'Facilities',
  FacilityInfo: 'Facility Information',
  ServiceTypes: 'Service Types',
  ServiceTypeGroups: 'Service Type Groups',
  ClearanceTypes: 'Clearance Types',
  smHsCode: 'sm-hs-codes',
  facility: 'Facility',
  bulkFacility: 'Facilities',
  ibChargeCodes: 'IBC Charge Codes',
  rateScales: 'Rate Scales',
  rateScaleInfo: 'Rate Scale Information',
  deliveryZone: 'Delivery Zones',
  deliveryZoneInfo: 'Delivery Zone Information',
  apChargeCodes: 'AP Charge Codes',
  apChargeCodesInfo: 'AP Charge Code Information',
  accountsPayable: 'Accounts Payable',
  accountsPayableInvoiceInfo: 'AP Invoice Information',
  accountsReceivable: 'Accounts Receivable',
  accountsReceivableInfo: 'AR Invoice Information',
  apCharges: 'AP Charges',
  Routings: 'Routings',
  RoutingInfo: 'Routing Information',
  Routes: 'Routes',
  arCharges: 'AR Charges',
  apInvoice: 'AP Invoices',
  arInvoice: 'AR Invoices',
  scannedBagsInManifest: 'Scanned Bags',
  currencyManagement: 'Currency Management',
  currencyInformation: 'Currency Information',
  currencies: 'Currencies',
  pallets: 'Pallets',
  RoutingCriteria: 'Routing Criteria',
};

export const AgentParcelCarrierMessages = {
  banner:
    'If no parcel carrier is selected, this agent is treated as an other agent and is not classified as FedEx, UPS, or DHL for auto-rating.',
} as const;

export const DateRangeHeaders = {
  ShipmentCreationDateRange: 'Shipment Creation Date Range',
  BagCreationDateRange: 'Bag Creation Date Range',
  ManifestCreationDateRange: 'Manifest Creation Date Range',
  MawbCreationDateRange: 'MAWB Creation Date Range',
  ManifestPublishDateRange: 'Publish Date Range',
  ManifestDateRange: 'Manifest Date',
  MawbDateRange: 'MAWB Date',
  FlightArrivalDateRange: 'Arrival Date',
};

export const DownloadItemNames = {
  Shipments: 'Shipments',
  Bags: 'Bags',
  Manifests: 'Manifests',
  Mawbs: 'MAWBs',
  Accounts: 'Accounts',
  Users: 'Users',
  Agents: 'Agents',
  RolesAndPermissions: 'Role & Permissions',
  StatusCode: 'Status Codes',
  AgentType: 'Agent Types',
  Servicetype: 'Service Types',
  HsCodes: 'HS Codes',
  Airline: 'Airlines',
  Airport: 'Airports',
  ServiceTypeGroups: 'Service Type Groups',
  ClearanceType: 'Clearance Types',
  numberRange: 'Number Ranges',
  facility: 'Facilities',
  roleAndPermission: 'Role & Permission',
  numberRangeDownlodfileName: 'Number_Ranges',
  ibcChargeCode: 'IBC Charge Codes',
  apChargeCode: 'AP Charge Codes',
  rateScales: 'Rate Scales',
  deliveryZone: 'Delivery Zones',
  Routing: 'Routings',
  rsAccessorialCharges: 'Accessorial Charges',
  rsWeightCharges: 'Weight Based Charges',
  apInvoiceCharges: 'AP Invoice Charges',
  accountPayable: 'Accounts Payable',
  accountReceivable: 'AR Invoice(s)',
  apInvoices: 'AP Invoice(s)',
};

export const popupButtons = {
  Clear: 'Clear',
  Cancel: 'Cancel',
  Delete: 'Delete',
  Download: 'Download',
  DownloadReport: 'Download Report',
  Yes: 'Yes',
  No: 'No',
  Discard: 'Discard Changes',
  ContinueEdit: 'Continue Editing',
  Block: 'Block',
  Print: 'Print',
  BulkBag: 'Bulk Bag',
  Confirm: 'Confirm',
  Select: 'Select',
  Preview: 'Preview',
  Ok: 'OK',
  Save: 'Save',
  Close: 'Close',
  gatewayOverrideExisting: 'Override existing',
  gatewayRetainExisting: 'Retain existing',
  gateway: 'Gateway',
  editShipment: 'Edit Shipment',
  proceedWithGateway: 'Proceed with Gateway',
  proceedWithBagGateway: 'Proceed with Bag Gateway',
  proceed: 'Proceed',
  create: 'Create',
  createLinkedShipments: 'Create Linked Shipments',
  gatewayReceivedShipments: 'Gateway Received Shipments',
  useSuggestedAccounts: 'Use Suggested Accounts',
  tryAnother: 'Try Another',
  sendCredentials: 'Send Credentials',
  createLinkedAccount: 'Create Linked Account',
  useSuggestedAccount: 'Use Suggested Account',
  smallDelete: 'delete',
  discardCreation: 'Discard Creation',
  editInformation: 'Edit Information',
  proceedShipmentSave: 'Proceed with Shipment Save',
  DiscardToSaveCommunity: 'Return to Save Commodity',
  sendForClearance: 'Send for Clearance',
  sendApplicable: 'Send applicable',
  createManifest: 'Create Manifest',
  retainExisting: 'Retain existing',
  changeExisting: 'Override existing',
  BulkManifest: 'Bulk Manifest',
  BulkMawb: 'Bulk MAWB',
  cancelEdit: 'Cancel Edit',
  GoAhead: 'Go ahead',
  sendEmail: 'Send Email',
  copyRateScale: 'Create a Copy',
  createAPInvoice: 'Create AP Invoice',
  createInvoice: 'Create Invoice',
  smallYes: 'yes',
  smallNo: 'no',
  Continue: 'Continue',
  GoBack: 'Go back',
  discardChanges: 'Discard changes',
  updateExistingUsers: 'Update Existing Users',
};

export const accountInfoDownload = {
  yes: 'Yes',
  no: 'No',
};

export const ActionIcons = {
  close: 'close',
  edit: 'edit',
  save: 'save',
  reset: 'reset',
  Reset: 'Reset',
  refresh: 'refresh',
  dashboardRefresh: 'dashboardRefresh',
  filter: 'filter',
  add: 'add',
  delete: 'delete',
  view: 'view',
  bulkEdit: 'bulk-edit',
  linkIbcUsers: 'linkIbcUsers',
  addCom: 'Addcom',
  AddTooltip: 'Add',
  addCommodity: 'addCom',
  download: 'Download',
  downloadFile: 'downloadFile',
  Downloadfile: 'Downloadfile',
  pinSearch: 'pin',
  unPinSearch: 'unpin',
  advanceSearch: 'advancedSearch',
  hamburger: 'hamburger',
  search: 'Search',
  searchSmallCase: 'search',
  default: 'Default',
  selected: 'Selected',
  searchPopupPin: 'searchActionPin',
  searchPopupEdit: 'searchActionEdit',
  searchPopupDelete: 'searchActionDelete',
  searchPopupSave: 'searchActionSave',
  hamburgerRecentListPin: 'hamburgerRecentListPin',
  hamburgerRecentListSave: 'hamburgerRecentListSave',
  hamburgerRecentListDelete: 'hamburgerRecentListDelete',
  rightArrow: 'arrowRight',
  print: 'print',
  bulkview: 'bulk-view',
  fieldClear: 'fieldClear',
  infoCircle: 'infoCircle',
  fetchResult: 'fetchResult',
  details: 'details',
  publish: 'Publish',
  advancedSearchTrigger: 'advancedSearchTriggerHeader',
  blockShipmet: 'Blockshipment',
  block: 'Block',
  advancedSearchHelp: 'advancedSearchHelpHeader',
  datePicker: 'datePicker',
  notesShortcut: 'notesShortcut',
  keyboardDownArrow: 'keyboard_arrow_down',
  helpOutlinedIcon: 'help_outline_outlined_icon',
  totalValueIcon: 'totalValue',
  blockIcon: 'block',
  Print: 'Print',
  advancedSearchPinHeader: 'advancedSearchPinHeader',
  advancedSearchHamburgerHeader: 'advancedSearchHamburgerHeader',
  weightIcon: 'weight',
  bulkGateway: 'bulkGateway',
  gateway: 'gateway',
  bulkRouting: 'bulkRouting',
  bulkPod: 'bulkPod',
  syncApInvoices: 'syncApInvoices',
  bulkBag: 'bulkBag',
  duplicate: 'createDuplicateShipment',
  mail: 'mail',
  visibilityTrue: 'visibilityTrue',
  visibilityFalse: 'visibilityFalse',
  lookupSearch: 'lookupSearch',
  noChanges: 'noChanges',
  changes: 'changes',
  sendCbpClearance: 'sendCbpClearance',
  /** SPEC-RSB-001 — resubmit rejected AMS / ACAS (same permission as Send for Clearance). */
  ccResubmit: 'ccResubmit',
  sendApplicable: 'Send applicable',
  generateLabel: 'generateLabel',
  sendInvoice: 'selectInvoice',
  generateArInvoice: 'generateArInvoice',
  generate: 'generate',
  edi: 'sendTo',
  copy: 'copy',
  recieveAPInvoice: 'recieveAPInvoice',
  uploadFile: 'uploadFile',
  autoRate: 'autoRate',
  unAutoRate: 'unAutoRate',
  held: 'held',
  gear: 'gear',
  remove: 'remove',
  tick: 'tick',
  publishMnifest: 'Publishing the Manifest',
  publishMawb: 'Publishing the MAWB',
  downloadArInvoice: 'Download AR Invoice',
  globe: 'globe',
  closeSm: 'closeSm',
  bulkedit: 'bulk edit',
  bulkdelete: 'bulk delete',
};

export const FieldClearSuffixConstants = {
  defaultTooltipText: 'Clear',
  hostDisplayHidden: 'none',
  hostAriaHiddenTrue: 'true',
  controlChangeKey: 'control',
  matFormFieldSelector: 'mat-form-field',
  inputSelector: 'input',
  zeroWidthSpace: '\u200b',
} as const;

export const ModeIcons = {
  mode: 'mode',
  edit: 'Edit',
  view: 'View',
  add: 'Add',
  save: 'Save',
  close: 'Close',
  bulkEdit: 'Bulk-Edit',
  smallview: 'view',
  smallEdit: 'edit',
  deleteAPI: 'deleteAPI',
  delete: 'delete',
  MainView: 'MainView',
  resetSelection: 'resetSelection',
  block: 'blockShipment',
  unblock: 'unblock',
  bulkView: 'Bulk-View',
  smallBulkView: 'bulk-view',
  uploadFile: 'uploadFile',
  autoRate: 'autoRate',
};

/** Shared page-mode check — prefer over repeating `pageMode === ModeIcons.view`. */
export function isViewPageMode(pageMode: string | null | undefined): boolean {
  return pageMode === ModeIcons.view;
}

export const HawbStatus = {
  unblocked: 'unblocked',
  blocked: 'blocked',
  default: 'default',
};

/** Block placed by the Customs City webhook while CBP holds the shipment; mirrors pactrak-node `CCITY_SYSTEM_USER_ID` and `amsHoldBlockReason`. */
export const AmsHoldSystemBlock = {
  userId: 1000,
  reason: 'AMS hold: pending CBP release',
};

export const TransitPointsInlineLoadKey = {
  build: (prefix: string | number, routeId: string | number | null | undefined) =>
    `${prefix}:${routeId ?? 'none'}`,
  addPrefix: 'add',
};

export const CrudActions = {
  create: 'c',
  update: 'u',
  delete: 'd',
  api: 'API',
  ui: 'UI',
  add: 'a',
  link: 'l',
  delink: 'e',
  removed: 'r',
  clearedCustome: 'CC',
  Update: 'update' as const,
  Create: 'create' as const,
  Delete: 'delete' as const,
  Delivery: 'D',
};

export const NotesType = {
  ibc: 'ibc',
  customer: 'customer',
};

export const pageType = {
  shipment: 'shipment',
  hawb: 'hawb',
  commodity: 'commodity',
  bag: 'bag',
  bagShipment: 'bag-shipment',
  pieceInfo: 'piece-information',
  commodityInfo: 'commodity-information',
  manifest: 'manifest',
  mawb: 'mawb',
  dataBind: 'dataBind',
  flightInformation: 'flight-information',
  agent: 'agent',
  mawbFlight: 'mawbFlight',
  agentInformation: 'agent-information',
  shipmentCommodity: 'shipment-commodity',
  account: 'account',
  rolesAndPermissions: 'rolesAndPermissions',
  usersManagement: 'userManagement',
  accountInformation: 'account-information',
  accountBillingAddress: 'accountBillingAddress',
  subAccount: 'sub-account',
  setupAndManitenance: 'sm-dashboard',
  hawbAdvanceSearch: 'hawb_advanceSearch',
  shipmentGateway: 'shipment-gateway',
  shipmentGatewayPiece: 'shipment-gateway-piece',
  /** SPEC-GEF-001 multi-exact match popup (Gateway-only; no Create placeholder) */
  shipmentGatewayPieceExactMatch: 'shipment-gateway-piece-exact-match',
  shipmentGatewayPieceShortage: 'shipment-gateway-piece-shortage',
  bagAdvanceSearch: 'bag_advanceSearch',
  statusCode: 'status-codes',
  mawbAdvanceSearch: 'mawb_advanceSearch',
  manifestAdvanceSearch: 'manifest_advanceSearch',
  smStatusCodes: 'sm-status-codes',
  smRolesAndPermissions: 'sm-roles-and-permissions',
  shipmentGatewayMasterShipment: 'shipment-gateway-master-shipment',
  shipmentBag: 'shipment-bag',
  userManagement: 'user-management',
  userDepartment: 'user-department',
  userLoginInfo: 'user-login-information',
  customerService: 'customer-service',
  smAirports: 'sm-airports',
  smNumberRanges: 'sm-number-ranges',
  smAirlines: 'sm-airlines',
  fetchFullManData: 'fetch-fullman-data',
  customsCityDraftStatus: 'customs-city-draft-status',
  customsCityAbiStatus: 'customs-city-abi-status',
  cbpResponseStatus: 'cbp-repsonse-status',
  cbpRequestStatus: 'cbp-request-status',
  sendCBPClearance: 'sendCBPClearance',
  bagGateway: 'bag-gateway',
  agentTypes: 'sm-agent-types',
  hsCodes: 'sm-hs-codes',
  facilities: 'sm-facilities',
  events: 'events',
  serviceTypes: 'sm-service-types',
  serviceTypeGroups: 'sm-service-type-groups',
  roles: 'roles',
  clearanceTypes: 'sm-clearance-types',
  numberRangesSetRange: 'number-ranges-set-range',
  smNumberRangesView: 'sm-number-ranges-view',
  smNumberRangesSeriesName: 'sm-number-ranges-series-name',
  accountUsers: 'sm-account-users',
  numberRangesEditSetRange: 'number-ranges-edit-set-range',
  linkedAccounts: 'accounts-linked-accounts',
  ibcChargeCodes: 'ibc-charge-codes',
  rateScales: 'rate-scales',
  rateScaleAccessorialManualEntry: 'rs-accessorial-manual-entry',
  rateScaleAccessorialUploadSheet: 'rs-accessorial-upload-agent-sheet',
  deliveryZone: 'sm-delivery-zone',
  deliveryZoneInfoTable: 'delivery-zone',
  apChargeCodes: 'ap-charge-codes',
  arApChargeCodeInfoTable: 'ap-charge-code',
  accountsReceivables: 'accounts-receivables',
  arChargeCodes: 'ar-charge-codes',
  apChargeCodeInfoTable: 'ap-charge-code',
  apChargeCodeLink: 'ap-charge-codes-link',
  apChargeCodeSetDetails: 'ap-charge-codes-set-details',
  accountsPayable: 'accounts-payable',
  apSftpFiles: 'ap-sftp-files',
  apChargesForApModule: 'ap-charges-for-ap-module',
  arChargesForARModule: 'ar-charges-for-ar-module',
  arChargesForShipmentMawbModule: 'ar-charges-for-shipment-mawb-info',
  apChargesForShipmentMawbModule: 'ap-charges-for-shipment-mawb-info',
  selectInvoiceFromShipment: 'select-invoice-from-shipments',
  scanShipmentBag: 'scan-shipment-bag',
  agentTrackingSelection: 'agent-tracking-selection',
  scannedBagsInManifest: 'scanned-bags-in-manifest',
  apChargeDuplicateEntry: 'ap-charge-duplicate-entry',
  routing: 'routing',
  routingInformation: 'routing-information',
  routingRoutes: 'routing-routes',
  transitPointsInline: 'transit-points-inline',
  currencyManagement: 'currency-management',
  currencyManagementDetails: 'currency-management-Details',
  currencyDetails: 'sm-currency-details',
  mawbPallet: 'Mawb-Pallet',
  apDashboardUnpublishedInvoices: 'unpublished-ap-invoices',
  apDashboardRecentInvoices: 'recent-ap-invoices',
  arDashboardInvoicesYetToSendToGP: 'ar-invoices-yet-to-be-sent-to-gp',
  arDashboardInvoicesYetToSendToCustomer: 'ar-invoices-yet-to-be-sent-to-customer',
  hawbRoutesSelection: 'hawb-routes-selection',
  hawbFinalDestRoutingConfirm: 'hawb-final-dest-routing-confirm',
  hawbTransitPoints: 'hawb-transit-points',
  arDashboardSpecialShipmentsYetToBeInvoiced:
    'specials-shipments-yet-to-be-invoiced',
  accountRoutingCriteria: 'account-routing-criteria',
  arChargesSelection: 'arChargesSelection',
  arMultiShipmentSelection: 'ar-multi-shipment-selection',
  associateUfoShipments: 'associate-ufo-shipments',
  manifestMultipleBagSelection: 'manifest-multiple-bag-selection',
  manifestPublishAttachments: 'manifest-publish-attachments',
  downloadArInvoice: 'downloadArInvoice',
  accountsModule: 'accounts-module',
  attachments: 'Attachments',
  notes: 'Notes',
  role: 'Role',
  agentTypesSummary: 'agent-types-summary',
  serviceTypesSummary: 'service-types-summary',
  serviceTypeGroupsSummary: 'service-type-groups-summary',
  rolseAndPermissionSummary: 'roles-and-permissions-summary',
  clearanceTypeSummary: 'clearance-types-summary'
};

export const basicSearchPayload = {
  accountPayableSearchPayload: 'accountPayableSearchPayload',
  accountSearchPayload: 'accountSearchPayload',
  airlineSearchPayload: 'airlineSearchPayload',
  airportSearchPayload: 'airportSearchPayload',
  apChargeCodeSearchPayload: 'apChargeCodeSearchPayload',
  bagSearchPayload: 'bagSearchPayload',
  deliveryZoneSearchPayload: 'deliveryZoneSearchPayload',
  facilitySearchPayload: 'facilitySearchPayload',
  hawbSearchPayload: 'hawbSearchPayload',
  hsCodeSearchPayload: 'hsCodeSearchPayload',
  manifestSearchPayload: 'manifestSearchPayload',
  mawbSearchPayload: 'mawbSearchPayload',
  rateScaleSearchPayload: 'rateScaleSearchPayload',
  roleSearchPayload: 'roleSearchPayload',
  userSearchPayload: 'userSearchPayload',
  accountReceivableSearchPayload: 'accountReceivableSearchPayload',
  routingPayload: 'routingPayload',
};

export const getBasicSearchDataFromApi = {
  getAccountPayableSearchDataFromApi: 'getAccountPayableSearchDataFromApi',
  getAccountSearchDataFromApi: 'getAccountSearchDataFromApi',
  getAirlineSearchDataFromApi: 'getAirlineSearchDataFromApi',
  getAirportDataFromAPI: 'getAirportDataFromAPI',
  getApChargeCodeSearchDataFromApi: 'getApChargeCodeDataSearchDataFromApi',
  getBagBasicSearchDataFromApi: 'getBagBasicSearchDataFromApi',
  getDeliveryZoneSearchDataFromApi: 'getDeliveryZoneSearchDataFromApi',
  getFacilitySearchDataFromApi: 'getFacilitySearchDataFromApi',
  getHawbBasicSearchDataFromApi: 'getHawbBasicSearchDataFromApi',
  getHsCodeSearchDataFromApi: 'getHsCodeSearchDataFromApi',
  getManifestBasicSearchDataFromApi: 'getManifestBasicSearchDataFromApi',
  getBasicSearchDataFromApi: 'getBasicSearchDataFromApi',
  getRateScaleSearchDataFromApi: 'getRateScaleSearchDataFromApi',
  getRoleSearchDataFromAPI: 'getRoleSearchDataFromAPI',
  getUserSearchDataFromAPI: 'getUserSearchDataFromAPI',
  getAccountReceivableSearchDataFromApi:
    'getAccountReceivableSearchDataFromApi',
  getRoutingSearchDataFromAPI: 'getRoutingSearchDataFromAPI',
};

export const pageUrl = {
  hawbSummary: '/hawb',
  hawbInformationPage: '/hawb/hawb-information',
  hawbAdvancedSearchPage: 'hawb/hawb-advanced-search',
  scanShipmentsPage: '/hawb/shipment/scan-shipments',
  bagSummary: '/bag',
  bagAdvancedSearchPage: 'bag/bag-advanced-search',
  bagPieceInformationPage: 'bag/bag-advanced-search/piece-information',
  mawbSummaryPage: '/mawb',
  mawbAdvancedSearchPage: 'mawb/mawb-advanced-search',
  manifestSummaryPage: '/manifest',
  manifestAdvancedSearchPage: '/manifest/manifest-advanced-search',
  hawbCommodityPage: '/hawb/hawb-information/commodity-information',
  bagInformationPage: '/bag/bag-information',
  manifestInformationPage: '/manifest/manifest-information',
  accountInformationPage: '/sm-dashboard/account/account-information',
  mawbInformationPage: '/mawb/mawb-information',
  accountsSummaryPage: 'sm-dashboard/account',
  bagPiecePage: '/bag/bag-information/hawb/hawb-information',
  printPreviewTable: '/print-preview',
  printPreviewInfoTable: '/info-print-preview',
  nestedPrintPreview: '/nested-print-preview',
  flightInfoPage: '/mawb/mawb-information/flight-information',
  rolesAndPermissionInfoPage:
    'sm-dashboard/roles-and-permissions/roles-and-permissions-information',
  userInfoPage: '/sm-dashboard/user-management/user-information',
  userManagemntSummaryPage: '/sm-dashboard/user-management',
  userProfile: '/user-profile',
  rolesAndPermissionsSummaryPage: '/sm-dashboard/roles-and-permissions',
  airportInformation: '/sm-dashboard/airports/airport-information',
  airportSummary: '/sm-dashboard/airports/',
  airlineSummary: '/sm-dashboard/airlines',
  airlineInformation: '/sm-dashboard/airlines/airline-information',
  hsCodeInformation: '/sm-dashboard/hs-codes/hs-code-information',
  hsCodeSummary: '/sm-dashboard/hs-codes',
  facilitiesSummary: '/sm-dashboard/facilities',
  facilitiesInformation: '/sm-dashboard/facilities/facility-information',
  generateShipmentLabel: '/shipment-label-generation-preview',
  rateScaleSummary: '/sm-dashboard/rate-scales',
  rateScaleInfo: '/sm-dashboard/rate-scales/rate-scale-info',
  deliveryZoneSummary: '/sm-dashboard/delivery-zone',
  deliveryZoneInformation:
    '/sm-dashboard/delivery-zone/delivery-zone-information',
  apChargeCodeSummary: '/sm-dashboard/ap-charge-codes',
  apChargeCodeInformation:
    '/sm-dashboard/ap-charge-codes/ap-charge-code-information',
  accountsPayableInformation: '/accounts-payable/accounts-payable-information',
  accountsPayableSummary: '/accounts-payable',
  accountsReceivableInformation:
    '/accounts-receivable/accounts-receivable-information',
  accountsReceiveSummary: '/accounts-receivable',
  currencyManagement: '/sm-dashboard/currency-management',
  agentSummaryPath: '/sm-dashboard/agent'
};

export const AttachmentDownloadRequestType = {
  hawb: 'HAWB',
  commodity: 'HAWB_COMMODITIES',
  bag: 'BAG',
  manifest: 'MANIFEST',
  mawb: 'MAWB',
  bagExt: 'Bag_Ext',
  mawbInfo: 'MAWB Information',
  mawbs: 'MAWBS',
  role: 'Role',
  shipment: 'Shipment',
  mawbFlight: 'MAWB_FLIGHT',
  account: 'Account',
  user: 'User',
  rolesAndPermission: 'Roles_and_Permissions',
  numberRange: 'Number Ranges',
  agent: 'Agent',
  airline: 'Airlines',
  hsCode: 'HS_Code',
  airport: 'Airports',
  agentTypes: 'Agent_Types',
  agentType: 'Agent_Type',
  serviceTypeGroups: 'Service Type Groups',
  ClearanceTypes: 'Clearance_Types',
  ServiceTypes: 'Service_Types',
  clearnceType: 'Clearance Type',
  statusCode: 'Status Codes',
  hsCodeSuccessMsg: 'HS Code',
  serviceTypeGrp: 'Service Type Group',
  Routing: 'Routes',
  facility: 'Facilities',
  numberRangeDownlodfileName: 'Number_Ranges',
  singleNumberRangeDownlodfileName: 'Number_Range',
  delivery: 'Delivery Zone',
  ibcChargeCode: 'IBC Charge Code',
  ibcChargeCodes: 'IBC Charge Codes',
  apChargeCode: 'AP Charge Code',
  rateScale: 'Rate_Scales',
  rsAccessorialCharges: 'RS_Accessorial_Charges',
  rsWeightBasedCharges: 'RS_Weight_Based_Charges',
  rateScaleSingle: 'Rate Scale',
  accountPayable: 'Accounts Payable',
  ApInvoiceCharges: 'AP_Invoice_Charges',
  apInvoice: 'AP invoice',
  accountReceivable: 'AR Invoice',
  accountReceivableDownload: 'AR_Invoice',
  currencyInformation: 'Currency Information',
  currency: 'Currency',
  gatewayed: 'Gatewayed',
  bagged: 'Bagged',
  manifested: 'Manifested',
  cbpHeld: 'CBP - Held',
  cbpCleared: 'CBP - Cleared',
  deliveryShipment: 'Delivery',
  currencyInformationForDownload: 'Currency_Information',
};

export const validationErrorMessages = {
  apModuleChargeValidation:
    'There are validation errors in the Accounts Payable (AP) module. Please correct them before saving.',
  arModuleChargeValidation:
    'There are validation errors in the Accounts Receivable (AR) module. Please correct them before saving.',
  invoiceIdNotFound: 'Invoice ID not found',
  originDestination: 'Origin and Destination should not be the same',
  autocompleteError: 'Invalid value entered',
  accountAutoCompleteError: 'Invalid Account entered',
  bagAlreadyPresent: 'Bag already in the Manifest',
  bagAlreadyLinkedToAnotherManifest: 'Bag already linked to another Manifest',
  invalidBagScan: 'Invalid Bag. Please try another bag',
  bagAlreadyPartofScannedBags: 'Bag already part of the scanned bags',
  shipmentAlreadyPresent: 'Shipment already in the Bag',
  invalidMawb: 'Invalid MAWB entered',
  invalidFlight: 'Invalid Flight Number entered',
  invalidFlightPattern:
    'Flight Number to start with 2-3 alphanumeric characters followed by 3-6 digits',
  manifestAfterSearch: 'No Manifests matched your search',
  manifestBeforeSearch: 'Please perform a search to view Manifests',
  mawbAfterSearch: 'No MAWBs matched your search',
  mawbBeforeSearch: 'Please perform a search to view MAWBs',
  pieceBagPackageError:
    'Invalid Packaging Type. Please select a valid packaging type from the following options: B Box, C Crate, G Gaylord, O Other, A Pallet.',
  bagAfterSearch: 'No Bags matched your search',
  bagBeforeSearch: 'Please perform a search to view Bags',
  resetHawb: 'Reset Shipment Information',
  hawbAfterSearch: 'No Shipments matched your search',
  hawbBeforeSearch: 'Please perform a search to view Shipments',
  accountRoutingValueMaxError: 'Minimum Value cannot be greater than Maximum Value',
  accountRoutingWeightMaxError: 'Minimum Weight cannot be greater than Maximum Weight',
  tenCharactersReq: '10 characters required',
  manifestAlreadyPresent: 'Manifest already in the MAWB',
  name: 'name',
  clearanceTypeError:
    'Invalid Clearance type. Please select a valid Clearance type from the following options: C - CFS, E - ECCF and 6 - CFS Type 86',
  originDestinationRequired: 'Both Origin and Destination are mandatory',
  destinationNotReached:
    'You have not reached the destination yet. You cannot save it.',
  firstTransitPointOriginMismatch: (originName: string, originCode: string) =>
    `The first transit point origin must match the route origin (${originName} (${originCode}))`,
  transitPointOriginMismatch: (previousDestinationName: string) =>
    `The origin must match the previous transit point's destination (${previousDestinationName})`,
  transitPointDestinationMismatch: (nextOriginName: string) =>
    `The destination must match the next transit point's origin (${nextOriginName})`,
  duplicateRouteExists:
    'The same route already exists. Please modify at least one of the following: mode of transport, starting time, transit points, availability days, or other route parameters before saving.',
  routeDeleteWithTransitPoints: (
    origin: string,
    destination: string,
    transitPoints: string
  ) =>
    `Do you want to delete Route for ${origin} - ${destination} with Transit points ${transitPoints}?`,
  routeDeleteWithoutTransitPoints: (origin: string, destination: string) =>
    `Do you want to delete Route for ${origin} - ${destination}?`,
  routingDeleteMessage: (origin: string, destination: string) =>
    `Do you want to delete the Route for ${origin} - ${destination}?`,
  publishCriteriaMismatchError:
    'MAWB cannot be published due to the following issues: Clearance agent not assigned, no flights linked, and Pieces from a blocked shipment are present in the linked Manifest',
  publishPartialSelectedMismatchError: (invalidCount: number) =>
    `${invalidCount} MAWBs do not fulfill the publish criteria. Would you like to still proceed with publishing the remaining MAWBs?`,
  publishPartialSelectedSingleMismatchError: (invalidCount: number) =>
    `${invalidCount} MAWB do not fulfill the publish criteria. Would you like to still proceed with publishing the remaining MAWB?`,
  publishClearanceAgent: 'MAWB cannot be published without a Clearance Agent',
  publishNoFlights: 'MAWB cannot be published without flight',
  publishShipmentBlocked:
    'MAWB cannot be published due to pieces from a blocked shipment in the linked Manifest',
  publishSelectedAllFailed:
    'None of the selected MAWBs meet the publish criteria',
  updateMawb: 'Update Mawb?',
  updateShipments: 'Update Shipments?',
  updateManifest: 'Update Manifest?',
  uploadFileTypeExtension: 'Upload only .csv, .pdf and .xls file types',
  fileSizeLimitMessage: 'File size exceeds the maximum limit of 25 MB',
  otherCriteria: ' and other criteria',
  hawbRoleAlreadyExists: (role: string) => `Role ${role} already exists`,
  mawbDateValidation: (months: number) =>
    `Please ensure the date remains within ${months} month(s) +/- from today's date`,
  manifestDateValidation: (months: number) =>
    `Please ensure the date remains within ${months} month(s) +/- from today's date`,
  BulkDeleteRolesHawb: (messageType: string, count: number) =>
    `Do you want to delete "${messageType}" from these ${count} Shipments?`,
  DeleteRolesHawb: (messageType: string) =>
    `Do you want to delete Role "${messageType}" from the Shipment?`,
  BulkDeleteHawbShipperConsignee: (messageType: string, count: number) =>
    ` ${messageType} deleted successfully from the Shipper & Consignee tab and Roles tab of ${count} Shipments`,
  DeleteRoleSuccess: (messageType: string) =>
    ` ${messageType} deleted successfully from the Roles tab of Shipment`,
  DeleteConsigneeShipperRoleSuccess: (messageType: string) =>
    ` ${messageType} deleted successfully`,
  hawbRestRoles: (messageType: string, count: number) =>
    ` ${messageType} deleted successfully from the Roles tab of ${count} Shipments`,
  invalidMasterHawb: 'Invalid Master shipment entered',
  sameMasterHawb: 'Shipment and Master Shipment cannot be same',
  originDestinationRequiredError:
    'Origin and Destination is required. Please fill origin and destination before linking shipment.',
  originDestinationRequiredForBag:
    'Origin and Destination is required. Please fill origin and destination before adding a bag',
  overageReferencePendingValidation:
    'Please tab out of this field to validate the reference before saving.',
  masterAccounNotExist: `Master Account doesn't exist`,
  accountAlreadyExist: 'Entered account number already exists',
  invalidEmailEntered: 'Invalid Email entered',
  departmentValidation:
    'At least one department must be added before proceeding',
  departmentDelete: 'At least one department must be present',
  deliveryZoneDelete: 'At least one delivery zone must be present',
  deliveryZoneNameDuplicate:
    'Delivery Zone name already exists. Please enter a unique zone name.',
  duplicatePriority:
    'Priority already exists. Please enter a unique priority number.',
  priorityOutOfRange: 'Priority must be a number from 1 to 10.',
  apDelete: 'At least one AP Charge Code must be present',
  AddUser: 'Would you like to add User?',
  existAccount: 'Account already exists',
  masterNotExist: `Master Account does not exist`,
  customerServiceDelete: 'At least one customer service must be present',
  governmentCustomerServiceRequired:
    'There should be at least one entry in the Customer Service grid under Government information',
  userDepartmentValidation: 'Please add at least 1 Department for the user',
  invalidOrigin: 'Invalid origin entered',
  invalidWeight: 'Invalid weight entered',
  invalidLength: 'Invalid length entered',
  invalidWidth: 'Invalid width entered',
  invalidHeight: 'Invalid height entered',
  invalidDimensionalWeight: 'Invalid dimensional weight entered',
  invalidActualWeight: 'Invalid actual weight entered',
  invalidChargeableWeight: 'Invalid chargeable weight entered',
  invalidTareWeight: 'Invalid tare weight entered',
  invalidEndNumber: 'End Number should be bigger than the Start Number',
  invalidPostalCode: 'Invalid Postal Code',
  invalidPostalEnd: 'Postal Code Start and End cannot be the same',
  sameTransitPoint: 'In Transit Point and Out Transit Point cannot be the same',
  noFacilitiesForCountry: 'No facilities available for the selected country',
  invalidShipmentExt: 'Please enter a valid Shipment',
  invalidShipmentAutoRate: 'Selected Shipment not yet Auto-rated',
  invalidMawbExt: 'Please enter a valid MAWB',
  invalidAgentNumberExt: 'Please enter a valid Agent Tracking Number',
  duplicateStatusCode: 'Status Code already exists',
  invalidState: 'Please select Country first',
  selectTableExtErr: (moduleName: string) =>
    `${moduleName} should be between 3 and 126 characters long and can have alphabets, numbers and special characters`,
  shipmentNotBagged: 'The Shipment is blocked and cannot be bagged.',
  usedNumberRangeDelete: (moduleName: string) =>
    `Range cannot be deleted as it is associated with a ${moduleName} record`,
  isValidAccountNo: 'Account number is valid.',
  atLeastOneRequired:
    'At least one is required from Markup (%), Flat Fee ($) or Charge Amount ($)',
  invalidDate: 'Please enter a valid date within the allowed range.',
  selectShipmentCreationFromDate: 'Please select Shipment Creation From Date',
  selectShipmentCreationToDate: 'Please select Shipment Creation To Date',
  selectBagCreationFromDate: 'Please select Bag Creation From Date',
  selectBagCreationToDate: 'Please select Bag Creation To Date',
  selectManifestCreationFromDate: 'Please select Manifest Creation From Date',
  selectManifestCreationToDate: 'Please select Manifest Creation To Date',
  selectMawbCreationFromDate: 'Please select MAWB Creation From Date',
  selectMawbCreationToDate: 'Please select MAWB Creation To Date',
  selectInDateFrom: 'Please select IN Date From',
  selectInDateTo: 'Please select IN Date To',
  selectOutDateFrom: 'Please select Out Date From',
  selectOutDateTo: 'Please select Out Date To',
  selectFromDate: 'Please select From Date',
  selectPublishDateFrom: 'Please select Publish Date From',
  selectPublishDateTo: 'Please select Publish Date To',
  dateOrderInvalid:
    'Please ensure the To Date is greater than the From Date',
  scanBagShipmentNotBagged:
    'The Shipment is blocked and cannot be bagged. Please select a different shipment',
  inValidHSCode: 'HS Code should be of 6-10 numeric digits only',
  warningMsgForNumberRange: 'Please select at least one range to download',
  selectAgentFirst: 'Please select an Agent Code first',
  phoneMinLengthDigits: 'Phone must have 8 to 11 digits',
  phoneAlphanumeric: 'Phone must have 8 to 10 alphanumeric characters',
  invalidIncomingBag: 'invalid bag ext',
  invalidInvoice: (moduleName: string) =>
    `Invalid ${moduleName} Invoice Number entered`,
  // Transit point time validation messages
  timeFormatInvalid: (example: string) =>
    `Please enter time in HH:MM format (e.g., ${example})`,
  startingTimeFormatInvalid: 'Please enter time in HH:MM format (e.g., 09:30)',
  arrivalTimeFormatInvalid: 'Please enter time in HH:MM format (e.g., 17:45)',
  prefixPErrorMsg: 'The prefix should always be set to "P"',
  singleDigitErrMsg: 'Please enter a single digit (0-9)',
  numberRangeStartNumberErrMsg: 'Start Number should greater than 600001',
  numberRangePendingRowMsg:
    'You have entered range values but have not added them. Click the + icon to add the range before saving.',
  numberRangePendingRowInvalidMsg:
    'The range values entered are incomplete or invalid. Correct the highlighted Start, End or Priority fields and click the + icon to add the range.',
  numberRangeNoRangeMsg:
    'Add at least one range. Enter Start, End and Priority values and click the + icon.',
  numberRangeTypeRequiredMsg: 'Select a Number Range Type before saving.',
  numberRangeInvalidFieldsMsg: (fieldLabels: string[]) =>
    `Please correct the highlighted field(s): ${fieldLabels.join(', ')}.`,
  invoiceNumberErrMsg:
    'Please enter an invoice number starting with "P" followed by 6 digits',
  enterValidInvoiceNumber: 'Enter a Valid Invoice Number',
  agentHasAssociatedChargeCodes: 'This agent has charge codes associated with it. Agent code cannot be updated.',
  downloadFailed: 'Failed to download file. Please try again.',
  noResultsFound: 'No results matched',
};

export const MasterLookup = {
  agentInfo: 'agentInfo',
  clearanceType: 'clearanceType',
  airlineCode: 'airlineCode',
  transportType: 'mode_of_transport',
  serviceTypeGroups: 'serviceTypeGroups',
  customerId: 'customerId',
  customerGroup: 'customerGroup',
  origin: 'origin',
  destination: 'destination',
  serviceType: 'serviceType',
  packaging: 'packaging',
  finalFacility: 'finalFacility',
  noApiControl: 'originIdVal',
  packagingTypeName: 'packagingTypeName',
  packagingType: 'packagingType',
  clearanceTypeVal: 'clearanceTypeVal',
  clearanceTypeCode: 'clearanceTypeCode',
  originID: 'originID',
  destinationID: 'destinationID',
  airlineName: 'airlineName',
  agentName: 'agentName',
  parcelCarrier: 'parcel_carrier',
  cargoOperatorName: 'cargoOperatorName',
  firstUsAirportName: 'firstUsAirportName',
  airportCode: 'airportCode',
  countries: 'countries',
  attachmentType: 'attachmentType',
  comparisonError: 'comparisonError',
  minHawbValue: 'minHawbValue',
  maxHawbValue: 'maxHawbValue',
  maxBagWeight: 'maxBagWeight',
  minBagWeight: 'minBagWeight',
  hawbFinalDestination: 'hawbFinalDestination',
  hawbFinalDestinationId: 'hawbFinalDestinationId',
  hawbRoleTypes: 'hawb-roleTypes',
  statusCode: 'statusCodes',
  organization: 'organization',
  organizationName: 'organizationName',
  rateScale: 'rateScale',
  userEmail: 'userEmail',
  selectEmailsForInvoiceNotification: 'selectEmailsForInvoiceNotification',
  selectEmailsforGatewayNotification: 'selectEmailsforGatewayNotification',
  salesPerson: 'salesPerson',
  manufacturerCountryCodes: 'manufacturerCountryCodes',
  clearanceChannel: 'clearanceChannel',
  fullmanFileValidation: 'fullmanFileValidation',
  group: 'groups',
  department: 'department',
  role: 'roles',
  timeZone: 'timeZone',
  statusCodesType: 'statusCodeTypes',
  applicableForModule: 'applicableForModules',
  numberRangesTypes: 'numberRangesTypes',
  scanPreference: 'scanPreference',
  cbpResponse: 'CbpResponse',
  cbpActionCodes: 'cbpActionCodes',
  agentTypes: 'agentTypes',
  facilityTypes: 'facilityTypes',
  agent: 'agent',
  ibcChargeCode: 'ibcChargeCode',
  agentChargeCode: 'agentChargeCode',
  chargeCode: 'chargeCode',
  agentChargeTypes: 'agentChargeTypes',
  paymentTerms: 'payment_terms',
  deliveryTerms: 'delivery_terms',
  billingType: 'billing_type',
  invoiceFileFormat: 'invoice_file_format',
  invoiceType: 'invoice_type',
  weightMetric: 'weight_metric',
  accessorialOrWeightCharge: 'accessorial_or_weight',
  shipmentIdentifier: 'shipment_identifier',
  currency: 'currencies',
  deliveryAgent: 'deliveryAgent',
  chargeType: 'chargeType',
  facility: 'facility',
  ibcWarehouses: 'ibcWarehouses',
  routeIBCwarehouses: 'routeIBCwarehouses',
  routeIBCWarehousesWithVendors: 'routeIBCWarehousesWithVendors',
  daysOfWeek: 'days_of_week',
  shipmentType: 'shipment_type',
  /** Master lookup key for `/v1/master/accounts/fetch` payload path (reports + special resolver). */
  accountsFetch: 'accounts/fetch',
  routeStopOrigin: 'routeStopOrigin',
  routeStopDestination: 'routeStopDestination',
  facilityWareHouse: 'facilityWarehouses',
};

export const FacilityType = {
  deliveryAgency: 'da',
};

/** ECCF, CFS, IBC generic — single system-wide default warehouse (SPEC-DWA-001) */
export const DefaultWarehouseFacTypes = ['ec', 'cf', 'ib'] as const;

export const DefaultWarehouseAirportMessages = {
  defaultWarehouseUncheckError:
    'In any given time, there should be one default warehouse. If you want to uncheck this, check some other warehouse as default',
  defaultAirportUncheckError:
    'In any given time, there should be one default airport for every country. If you want to uncheck this, check some other airport for the same country as default',
  confirmDefaultWarehouse:
    'Would you like to change this to the default warehouse?',
  confirmDefaultAirport: (countryName: string) =>
    `Would you like to change this to the default airport for ${countryName} country?`,
  defaultWarehouseUpdatedSuccess: 'Default warehouse updated successfully',
  defaultAirportUpdatedSuccess: 'Default Airport updated successfully',
  airportMadeDefaultAndUpdatedSuccess:
    'Airport made as Default and updated successfully',
  defaultFacilityUpdatedSuccess: 'Default Facility updated successfully',
  facilityMadeDefaultAndUpdatedSuccess:
    'Facility made as Default and updated successfully',
};

/** PAC-23349 / SPEC-DDG-001 — delete guards for default/sole warehouse and airport */
export const DefaultDeleteGuardBlockReason = {
  none: 'none',
  soleWarehouse: 'sole_warehouse',
  defaultWarehouse: 'default_warehouse',
  soleCountryAirport: 'sole_country_airport',
  defaultAirport: 'default_airport',
} as const;

export type FacilityDeleteBlockReason =
  | typeof DefaultDeleteGuardBlockReason.none
  | typeof DefaultDeleteGuardBlockReason.soleWarehouse
  | typeof DefaultDeleteGuardBlockReason.defaultWarehouse;

export type AirportDeleteBlockReason =
  | typeof DefaultDeleteGuardBlockReason.none
  | typeof DefaultDeleteGuardBlockReason.soleCountryAirport
  | typeof DefaultDeleteGuardBlockReason.defaultAirport;

export const DefaultDeleteGuardMessages = {
  defaultFacilityDeleteError:
    'Default facility should not be deleted. Kindly mark another facility as Default and then delete this',
  soleWarehouseFacilityDeleteError:
    'There should be at least one facility and it could not be deleted',
  defaultAirportDeleteError:
    'Default Airport should not be deleted. Kindly mark another Airport as Default for this country and then delete this',
  soleCountryAirportDeleteError:
    'There should be at least one Airport for a country and it could not be deleted',
  partialFacilityDeleteConfirm: (blockedCount: number, totalCount: number) =>
    `${blockedCount} out of the ${totalCount} selected facility is default and cannot be deleted. Would you like to delete the remaining ${totalCount - blockedCount}?`,
  partialAirportDeleteConfirm: (blockedCount: number, totalCount: number) =>
    `${blockedCount} out of the ${totalCount} selected airports are default and cannot be deleted. Would you like to delete the remaining ${totalCount - blockedCount}?`,
  defaultAgentDeleteError: "IBC Default Agent can't be deleted",
  partialAgentDeleteConfirm: (blockedCount: number, totalCount: number) =>
    `${blockedCount} out of the ${totalCount} selected agents is the IBC Default Agent and cannot be deleted. Would you like to delete the remaining ${totalCount - blockedCount}?`,
};

/** PAC-24812 / SPEC-IIDG-001 — block delete of international incoming entities */
export const IncomingInternationalEntity = {
  shipment: 'shipment',
  bag: 'Bag',
  manifest: 'Manifest',
  mawb: 'MAWB',
} as const;

export type IncomingInternationalEntityName =
  (typeof IncomingInternationalEntity)[keyof typeof IncomingInternationalEntity];

const incomingInternationalPlural = (
  entity: IncomingInternationalEntityName
): string =>
  entity === IncomingInternationalEntity.shipment ? 'shipments' : `${entity}s`;

export const IncomingInternationalDeleteGuardMessages = {
  /** Information page, or a single row selected on the landing page */
  singleDeleteError: (entity: IncomingInternationalEntityName) =>
    `This ${entity} is an International incoming ${entity} and can't be deleted. Use Cancel / Recover Shipments to delete it`,
  /** Landing page — every selected row is international incoming */
  allSelectedDeleteError: (entity: IncomingInternationalEntityName) =>
    `The selected ${entity}(s) are International incoming ${entity} and can't be deleted. Use Cancel / Recover Shipments to delete it`,
  /** Landing page — mixed selection (Yes / No) */
  partialDeleteConfirm: (
    entity: IncomingInternationalEntityName,
    blockedCount: number,
    totalCount: number
  ) => {
    const plural = incomingInternationalPlural(entity);
    return `${blockedCount} of the ${totalCount} selected ${plural} are international incoming ${plural} and can't be deleted. Would you like to delete the remaining ${plural}?`;
  },
};

/**
 * PAC-24812 — step 2 of the delete guard (runs after the international incoming check):
 * a published Manifest blocks deleting Shipment, Bag, Manifest and MAWB.
 */
const manifestPublishedPhrase = (
  entity: IncomingInternationalEntityName
): string => {
  switch (entity) {
    case IncomingInternationalEntity.manifest:
      return 'published';
    case IncomingInternationalEntity.mawb:
      return 'linked to a published Manifest';
    default:
      return 'part of a published Manifest';
  }
};

export const ManifestPublishedDeleteGuardMessages = {
  /** Information page, or a single row selected on the landing page */
  singleDeleteError: (entity: IncomingInternationalEntityName) => {
    const phrase = manifestPublishedPhrase(entity);
    return `This ${entity} is ${phrase} and can't be deleted`;
  },
  /** Landing page — every selected row is blocked */
  allSelectedDeleteError: (entity: IncomingInternationalEntityName) => {
    const phrase = manifestPublishedPhrase(entity);
    return `The selected ${entity}(s) are ${phrase} and can't be deleted`;
  },
  /** Landing page — mixed selection (Yes / No) */
  partialDeleteConfirm: (
    entity: IncomingInternationalEntityName,
    blockedCount: number,
    totalCount: number
  ) => {
    const plural = incomingInternationalPlural(entity);
    const phrase = manifestPublishedPhrase(entity);
    return `${blockedCount} of the ${totalCount} selected ${plural} are ${phrase} and can't be deleted. Would you like to delete the remaining ${plural}?`;
  },
};

/** Yes/No MessagePopup footer — no type-to-confirm (uses MessagePopupComponent isForArInvoiceWeightUpdate contract) */
export const DefaultSwapConfirmationPopupConfig = {
  isFooterRequired: true,
  isForArInvoiceWeightUpdate: true,
};

export const searchDataParam = {
  id: 'id',
  summary: 'summary',
  desc: 'desc',
  hawbExt: 'hawbExt',
  summaryInfo: 'summary-info',
  hawbId: 'hawbId',
  manifestId: 'manifestId',
  bagExt: 'bagExt',
  information: 'information',
  basicSearch: 'basicSearch',
  bagId: 'bagId',
  mawbExt: 'mawbExt',
  mawbId: 'mawbId',
  flightInformation: 'flight-information',
  asc: 'asc',
  storedUrl: 'storedUrl',
  previousShipmentStatus: 'previousShipmentStatus',
  refreshPaginationProps: 'refreshPaginationProps',
  PieceList: 'PieceList',
  gateway: 'gateway',
  accountId: 'accountId',
  userId: 'user_id',
  roleId: 'grp_dept_roles_permissions_id',
  account: 'account',
  cbpRequestAndResponsehawbId: 'hawb_id',
  dateTime: 'dateTime',
  serviceType: 'order_by',
  airport: 'airportId',
  hsCodeId: 'hs_code_id',
  airlineId: 'airlineId',
  serviceTypeGroups: 'order_by',
  clearanceType: 'createdDate',
  fac_id: 'fac_id',
  rateScaleId: 'rate_scale_id',
  agentDeliveryZonesId: 'agentDeliveryZonesId',
  currencyConverion: '',
};

export const shipmentStatus = {
  block: 'blocked',
  unblock: 'unblocked',
};

export const manifestFlightTootlip = {
  noFlight:
    'Flight Number to start with 2-3 alphanumeric characters followed by 3-6 digits',
};

export const WidgetValues = {
  noOfBags: 'noOfBags',
  noOfHawbs: 'noOfHawbs',
  totalWeight: 'totalWeight',
  totalNoOfShipments: 'totalNoOfShipments',
  totalValue: 'totalValue',
  noOfManifests: 'noOfManifests',
  noOfFlights: 'noOfFlights',
  noOfPallets: 'noOfPallets',
  lbUnit: ' lb',
  emptyValue: '--',
  singleZero: '0',
  multipleZero: '0.00',
  location: 'location',
  bagLength: 2,
};

export const GridColumnsStorageNames = {
  hawb: 'hawbDisplayColumns',
  scanShipments: 'scanShipmentsDisplayColumns',
  scanShipmentsDuplicates: 'scanShipmentsDuplicateDisplayColumns',
  bag: 'bagDisplayColumns',
  manifest: 'manifestDisplayColumns',
  mawb: 'mawbDisplayColumns',
  mawbManifest: 'mawbManifestDisplayColumns',
  mawbFlight: 'mawbFlightDisplayColumns',
  agent: 'agentDisplayColumns',
  agentFacility: 'agentFacilityDisplayColumns',
  shipmentCommodity: 'shipmentCommodity',
  bagPiece: 'bagDisplayColumns',
  account: 'accountDisplayColumns',
  shipmentGateway: 'shipmentGatewayColumns',
  shipmentGatewayPieceShortage: 'shipmentGatewayPieceShortageColumns',
  shipmentGatewayPiece: 'shipmentGatewayPieceDisplayColumns',
  statusCode: 'statusCodeDisplayColumns',
  smStatusCodes: 'smStatusCodeDisplayColumns',
  smRolesAndPermissions: 'smRolesAndPermissionsDisplayColumns',
  shipmentGatewayMasterShipment: 'gatewayMasterShipmentColumn',
  shipmentBag: 'bagShipment',
  smUserManagement: 'userManagementDisplayColumns',
  smUserDepartment: 'userDepartmentDisplayColumns',
  smUserLoginInfo: 'userLoginInformationColumns',
  customerService: 'customerServiceColumns',
  smAirports: 'airportsDisplayColumns',
  smNumberRanges: 'smNumberRangesColumns',
  smAirlines: 'smAirlines',
  fetchFullManFileData: 'fetchFullManFileData',
  customsCityDraftStatus: 'customsCityDraftStatus',
  customsCityAbiStatus: 'customsCityAbiStatus',
  cbpRequestStatus: 'cbpRequestStatus',
  cbpResponseStatus: 'cbpResponseStatus',
  bagGateway: 'bagGateway',
  agentTypes: 'agentTypes',
  hsCodes: 'hsCodes',
  facilities: 'facilities',
  serviceTypes: 'serviceTypes',
  serviceTypeGroups: 'serviceTypeGroup',
  clearanceTypes: 'clearanceTypes',
  accountUsers: 'smAccountUsers',
  linkedAccounts: 'smLinkedAccounts',
  ibcChargeCodes: 'ibcChargeCodes',
  rateScales: 'smRateScales',
  deliveryZone: 'deliveryZone',
  apChargeCodes: 'apChargeCodes',
  rsAccessorialChartUpload: 'smRsAccessorialChartUpload',
  accountsPayable: 'accountsPayable',
  apForAccountsPayablesModule: 'apChargesForApModule',
  arForAccountsReceivablesModule: 'arChargesForARModule',
  arChargesForShipmentMawbModule: 'arChargesForShipmentMawbModule',
  apChargesForShipmentMawbModule: 'apChargesForShipmentMawbModule',
  selectInvoiceFromShipments: 'selectInvoiceFromShipments',
  accountsReceivables: 'accountsReceivables',
  arChargeCodes: 'arChargeCodes',
  apChargesCodeDetail: 'apChargeCodeDetails',
  apSftpFiles: 'apSftpFilesDisplayColumns',
  agentTrackingSelection: 'agentTrackingSelectionColumns',
  scannedBagsInManifest: 'scannedBagsInManifestColumns',
  currencyManagement: 'currencyManagement',
  apDashboardUnpublishedInvoices: 'apDashboardUnpublishedInvoices',
  apDashboardRecentInvoices: 'apDashboardRecentInvoices',
  arDashboardInvoicesYetToSendToGP: 'arDashboardInvoicesYetToSendToGP',
  arDashboardInvoicesYetToSendToCustomer:
    'arDashboardInvoicesYetToSendToCustomer',
  arDashboardSpecialShipmentsYetToBeInvoiced:
    'arDashboardSpecialShipmentsYetToBeInvoiced',
  hawbRoutesSelection: 'hawbRoutesSelection',
  hawbFinalDestRoutingConfirm: 'hawbFinalDestRoutingConfirmColumns',
  routing: 'routingDisplayColumnsV3',
  routingRoutes: 'routingRoutesDisplayColumnsV2',
  transitPointsInline: 'transitPointsInlineDisplayColumnsV2',
  arMultiShipmentSelection: 'arMultiShipmentSelectionColumns',
  associateUfoShipments: 'associateUfoShipmentsColumns',
  manifestMultipleBagSelection: 'manifestMultipleBagSelectionColumns',
  manifestPublishAttachments: 'manifestPublishAttachmentsColumns',
};

export const keyStrokes = {
  enter: 'Enter',
  escape: 'Escape',
  arrowDown: 'ArrowDown',
  arrowUp: 'ArrowUp',
  home: 'Home',
  end: 'End',
};

export const confirmCountdownSeconds = 5;

export const basicSearchShortcutKeys = {
  advancedSearch: { key: 'q', code: 'KeyQ', label: 'Shift+Alt+Q' },
  recentAndPinnedSearches: { key: 'h', code: 'KeyH', label: 'Shift+Alt+H' },
};

export const actionIcons = {
  save: 'saveIcon',
  filter: 'filterIcon',
  search: 'searchIcon',
};

export const OverlayPositions = {
  top: 'top' as const,
  end: 'end' as const,
  center: 'center' as const,
};

export const BasicSearchPlaceholder = {
  searchByPlaceholder: 'Search by ',
  searchPerformedInfoText: 'Search can be performed using ',
  infoText: ' separated by a space',
  shipmentBagManifestMAWB:
    'Shipment, Agent Shipment, Customer Reference, Bag, MAWB, Manifest Number, Shipment Barcodes',
  agents: 'Agent Status, Code, Name, Type, Facility',
  accounts: 'Organization Name, Account Number, Account Name',
  statusCodes:
    'Type, Agent, External Code, IBC Code, Facility,  Applicable For, Description and Notes',
  rolesAndPermissions: 'Group, Department, Role, Facility',
  userManagement:
    'First Name, Last Name, Full Name, Email Address, Default Facility, Last Logged In, Last Accessed IP Address',
  airports: 'Airport Code, ICAO Code, Name, Country',
  numberRanges: 'Series Name, Origin, Priority, Additional Information',
  airlines: 'Airline Code, ICAO Code, Numeric Code, Name, Address, Information',
  agentTypes: 'Agent Type, Name, Description',
  hsCodes:
    'HS Code, Start Date, End Date, Description, Information, Alias, Alias Information',
  facilities:
    'Facility Code, Name, Type, Address(Line1, Line2), Country, City, State/Province/Region, Postal Code, Phone, Email',
  serviceTypes: 'Service Type, Name, Group Code',
  serviceTypeGroups: 'Group Code, Name and Description',
  clearanceTypes: 'Search by Clearance Type, Name and Description',
  ibcChargeCodes: 'IBC Charge Code and  IBC Charge Description',
  rateScales: 'Rate Scale, Destination, Agent',
  apChargeCodes:
    'Agent Code, Agent Name, Agent Charge Code, IBC Charge Code, Charge Type, Agent Charge Description',
  deliverZone: 'Delivery Zone, Agent Name, Agent Code',
  accountsPayable:
    'Invoice Number, Invoice Date, Agent Name, Agent Code, Invoice Reference, Currency, Conversion Rate, Published to GP',
  accountsReceivable:
    'Invoice Number, Invoice Date, Account Number, Account Name',
  scanShipment: 'Click here and scan the shipment using scanner',
  searchScanShipmentText: 'The shipments have not been scanned yet.',
  currencyManagement: 'Currency Code and Currency Name',
  routing: 'Origin, Destination, Transit Points, Mode of Transport',
};

export const gridMessages = {
  noSearch: 'Please perform a search to view',
  noRecordsMatch: (itemName: string) => `No ${itemName} matched your search`,
  noRecentSearch: 'No recent search is performed',
  noSavedSearch: 'No search is saved',
  accountNoSearch: 'Please perform a search to view Accounts',
  noUser: 'User has not logged in yet',
  noNumberRangeSearch: 'Please perform a search to view Number Ranges',
  noRecordsAvailable: (itemName: string) =>
    `No results found in the ${itemName}`,
  noCbpRequestSearch: `Shipments from the selected file doesn't need clearance`,
  noCustomsCityDraftSearch: `No draft clearance jobs found for the selected file`,
  noCustomsCityAbiSearch: `No ABI submission records found for the selected job`,
  noBagGateway: 'Scan the bag to get the details',
  noFullmanFiles: `Selected account doesn't have any fullman file submitted`,
  noFullmanFileSearch:
    'Please select an account to view the list of validated Fullman files',
  noAgentTypes: 'No Agent types matched your search.',
  noAgentTypeSearch: 'Please perform a search to view Agent Types.',
  noRolesAndPermissionsSearch:
    'Please perform a search to view Roles & Permissions',
  noServiceTypeSearch: 'Please perform a search to view Service Types',
  noServiceTypeMatch: 'No Service Types matched your search.',
  noMatchingServiceTypes: 'No matching Service Types',
  noAirportSearch: 'Please perform a search to view Airports',
  noAirportMatch: 'No Airports matched your search',
  noAirlinesSearch: 'Please perform a search to view Airlines',
  noAirlinesMatch: 'No Airlines matched your search',
  noStatusCodesSearch: 'Please perform a search to view Status Codes',
  noStatusCodesMatch: 'No Status Codes matched your search.',
  noUserManagementSearch: 'Please perform a search to view User Management',
  noUserManagementMatch: 'No User Management records matched your search.',
  noHsCode: 'Please perform a search to view HS Codes',
  noClearanceTypeSearch: 'Please perform a search to view Clearance Types',
  noServiceTypeGroupsMatch: 'No Service type groups matched your search',
  noServiceTypeGroupsSearch:
    'Please perform a search to view Service type groups',
  noFacilitiesSearch: 'Please perform a search to view Facilities',
  noFacilitiesMatch: 'No Facilities matched your search',
  noDeliveryZoneSearch: 'Please perform a search to view Delivery Zone',
  noDeliveryZoneMatch: 'No Delivery Zone matched your search',
  noApChargeCodeSearch: 'Please perform a search to view AP Charge Codes',
  noApChargeCodeMatch: 'No AP Charge Codes matched your search',
  noRateScaleSearch: 'Please perform a search to view Rate Scales',
  noRateScaleMatch: 'No Rate Scales matched your search',
  noAPSummarySearch: 'Please perform a search to view Account Payable Invoices',
  noApCodeMatch: 'No AP Invoice matched your search',
  noARSummarySearch: 'Please perform a search to view AR Invoice',
  noARCodeMatch: 'No AR Invoice matched your search',
  noApSftpFiles: 'No files are currently available for reconciliation',
  noScannedBagsInManifest: 'No Scanned Bags are available',
  noCurrencyInformation: 'No Currency matched your search',
  noCurrencySearchInformation:
    'Please perform a search to view Currency Information',
  noRoutesSearch: 'Please select the Origin and Destination to get started',
  noRoutesMatch: 'No Routes present',
  noRoutingSearch: 'Perform a search to view the Routes',
  noRoutingMatch: 'No matching Routes found',
  noMatchingRoutesFound:
    'No matching routes found. Kindly configure a new route',
};

export const AccordionTabs = {
  Roles: 'Roles',
  ShipperConsginee: 'Shipper & Consignee',
  Miscellaneous: 'Miscellaneous',
  Events: 'Events',
  Attachments: 'Attachments',
  Details: 'Details',
  Users: 'Users',
  Notes: 'Notes',
  Shipments: 'Shipments',
  Bags: 'Bags',
  Manifests: 'Manifests',
  Mawbs: 'Mawbs',
  SpecialSearches: 'Specialsearches',
  StatusCodes: 'Statuscodes',
  Criteria: 'Criteria',
  StatusCodeTitle: 'Status Codes',
  mawbmiscellaneous: 'MAWB Miscellaneous',
  Route: 'Route',
  ShipperConsgineeTitle: 'Shipperconsignee',
  LoginInfo: 'Login Information',
  BusinessInformation: 'Business Information',
  GovernmentInformation: 'Government Information',
  Address: 'Address',
  ap: 'AP',
  ar: 'AR',
  otherPermissions: 'Other Permissions',
  CustomerManagement: 'Customer Management',
  validatedFullmanFiles: 'List of Validated Fullman Files',
  customsCityDraftStatus: 'Send Draft To Customs City',
  customsCityAbiStatus: 'ABI Entry To CBP',
  cbpRequestStatus: 'CBP Request Status',
  cbpResponseStatus: 'CBP Response Status',
  uploadFiles: 'Upload Fullman File',
  routing: 'Routing',
};

export const DataTypes = {
  String: 'string',
  Object: 'object',
  Number: 'number',
  Boolean: 'boolean',
  BigInt: 'bigint',
  Symbol: 'symbol',
};

export const HttpConstSignatures = {
  Post: 'POST',
  Put: 'PUT',
  Delete: 'DELETE',
  Get: 'GET',
  json: 'json',
};

export const TableHeaderValues = {
  addCom: 1,
  view: 2,
  edit: 3,
  delete: 4,
  save: 5,
  close: 6,
  add: 1,
};

export const ScrollData = {
  smooth: 'smooth',
  auto: 'auto',
};
export const selectedIdForSummaryTable = {
  Mawb: 'mawbId',
  Agent: 'agentId',
  Hawb: 'hawbId',
  Manifest: 'manifestId',
  Bag: 'bagId',
  Account: 'accountNumber',
  Id: 'id',
  FlightId: 'flightId',
  Commodity: 'commodityNumber',
  SmStatusCodes: 'statusCode',
  SmRolesAndPermissions: 'id',
  CommodityId: 'commodityId',
  PieceId: 'pieceId',
  SmUserManagement: 'id',
  tempId: 'tempId',
  SmAirports: 'airportCode',
  SmNumberRanges: 'id',
  SmAirlines: 'id',
  smAgentTypes: 'id',
  smhsCode: 'id',
  smFacilities: 'facCode',
  serviceTypes: 'serviceType',
  serviceTypeGroups: 'serviceTypeGroup',
  clearanceTypes: 'clearanceType',
  rateScales: 'rateScaleId',
  ibcChargeCodes: 'id',
  deliveryZone: 'id',
  agentDeliveryZoneDetailsId: 'agentDeliveryZoneDetailsId',
  apChargeCode: 'apChargeCode',
  accountsReceivables: 'invoiceNumber',
  apChargeCodeDetailsId: 'apChargeCodeDetailsId',
  apChargeCodeId: 'id',
  facilityId: 'fac_id',
  accountsPayable: 'id',
  invoiceChargesId: 'invoiceChargesId',
  apSftpFiles: 'fileName',
  scannedBagsInManifest: 'bagExt',
  currencyCode: 'id',
  routeId: 'routeId',
  criteriaId: 'criteriaId',
};

export const selectionTableNames = {
  Mawb: 'Mawb',
  MawbManifest: 'Mawb-Manifest',
  MawbFlight: 'Mawb-Flight',
  AgentFacility: 'agent-information',
  hawb: 'hawb',
  ShipmentCommodity: 'shipment-commodity',
  ShipmentPiece: 'shipment-piece',
  ShipmentGateway: 'shipment-gateway',
  ManifestBag: 'manifest-bag',
  BagShipment: 'bag-shipment',
  StatusCodes: 'status-codes',
  UserManagement: 'user-management',
  UserDepartment: 'user-department',
  UserLoginInfo: 'user-login-information',
  customerService: 'customer-service',
  AccountUsers: 'sm-account-users',
  Shipment: 'Shipment',
  DeliveryZone: 'delivery-zone',
  ApChargeCode: 'ap-charge-code',
  apChargesForApModule: 'ap-charges-for-ap-module',
  arChargesForARModule: 'ar-charges-for-ar-module',
  arChargesForShipmentAndMawb: 'ar-charges-for-shipment-mawb-info',
  apChargesForShipmentAndMawb: 'ap-charges-for-shipment-mawb-info',
  selectInvoiceFromShipments: 'select-invoice-from-shipments',
  ArChargeCodes: 'ar-charge-codes',
  mawbPallet: 'Mawb-Pallet',
  transitPointsInline: 'transit-points-inline',
  accountRoutingCriteria: 'account-routing-criteria',
  hawbTransitPoints: 'hawb-transit-points',
};

export const InputRowKeys = {
  Mawb: 'mawb',
  MawbManifest: 'manifestNumber',
  MawbFlight: 'airlineCode',
  Agent: 'code',
  AgentFacility: 'facilityCode',
  Hawb: 'hawbId',
  ManifestNo: 'manifestNo',
  Bag: 'bagId',
  Account: 'accountNumber',
  hawbExt: 'hawbExt',
  ManifestBag: 'bagExt',
  pieceExt: 'pieceExt',
  smStatusCodes: 'statusCode',
  smRolesAndPermissions: 'dept',
  smUserManagement: 'fullName',
  smAirports: 'airportCode',
  smNumberRanges: 'seriesName',
  smAirlines: 'airlineCode',
  bagExt: 'bagExt',
  agentType: 'agentType',
  hsCodes: 'hsCode',
  facilities: 'facilityCode',
  iataCode: 'iataCode',
  serviceTypes: 'serviceType',
  serviceTypeGroups: 'serviceTypeGroup',
  clearanceTypes: 'clearanceType',
  airportCode: 'airportCode',
  ibcChargeCodes: 'code',
  rateScales: 'rateScale',
  deliveryZone: 'deliveryZone',
  accountsPayable: 'invoiceNumber',
  agentCode: 'agentCode',
  facCode: 'facCode',
  accountsReceivable: 'invoiceNumber',
  rateScaleName: 'rateScale',
  apSftpFiles: 'agentData',
  shipment: 'shipment',
  scannedBagsInManifest: 'bagExt',
  currencyCode: 'currencyCode',
};

export const TableProps = {
  select: 'select',
  action: 'action',
  customize: 'customize',
  inputRow: 'isInputRow',
  rowDisable: 'isRowDisable',
};

export const fileFormats = {
  csv: 'csv',
  xlsx: 'xlsx',
};

export const fileFormatsType = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const dateTimeFormat = {
  dateTime12hours: 'MM-dd-yyyy h:mm:ss a',
  dateTime24hours: 'MM-dd-yyyy_HH-mm-ss',
  dateTime24hoursWithSpace: 'MM-dd-yyyy HH:mm:ss',
  shipmentDateTime: 'MM-dd-yyyy HH:mm',
  statusCodeDateTime: 'MM-dd-yyyy HH:mm:ss',
  onlyDate: 'MM-dd-yyyy',
  yearMonthDateFormat: 'yyyy-MM-dd',
  yearMonthFormat: 'yyyy-MM',
  date: 'MM-DD-YYYY',
  fileNameDateFormat: 'MMDDYYYY',
  appLocale: 'en-US',
};

/** Flight miscellaneous form / store field keys (aligned with `flightsMiscellaneousFormFieldsConfig`). */
export const flightMiscFormFieldKeys = {
  id: 'id',
  flightId: 'flightId',
  cargoOperator: 'cargoOperator',
  cargoOperatorName: 'cargoOperatorName',
  firstUsAirport: 'firstUsAirport',
  firstUsAirportName: 'firstUsAirportName',
  firstUsAirportDate: 'firstUsAirportDate',
  isSavedData: 'isSavedData',
  action: 'action',
} as const;

/** Subset of `flightMiscFormFieldKeys` sent on `flights[].miscellaneous` (see `buildFlightMiscellaneousApiPayload`). */
export const flightMiscApiPayloadKeys = [
  flightMiscFormFieldKeys.cargoOperator,
  flightMiscFormFieldKeys.firstUsAirport,
  flightMiscFormFieldKeys.firstUsAirportDate,
] as const;

export const editMessageTemplate = {
  editMessage: (count: number, itemType: string) =>
    `Would you like to edit ${count} ${itemType} at once?`,
};

export const setupAndMaintenanceMessages = {
  view_more: 'View More',
  noSearchResultsFound: 'No Setup & Maintenance sub module matched your search',
};
export const FetchUrlById = {
  hawbId: 'hawbIds/',
};

export const AttachmentMessages = {
  removeAttachment: 'Remove Attachment',
  removeAtatchmentMessage: `Are you sure you want to remove the attachment ?`,
  deleteAttachment: 'Attachment(s) deleted  successfully',
  AddAttachment: 'Attachment added successfully',
  DownloadAttachmentMsg: (val: string) =>
    `${val} Template downloaded successfully`,
  attachmentDownloadedSuccessfully: 'Selected File Downloaded Successfully',
};
export const SvgIconFrom = {
  page: 'Page',
};

export const MessageTemplates = {
  AllItemsSelected: (itemType: string, itemCount: number) =>
    `All ${itemCount === 1 ? '' : itemCount
    } ${itemType} on this page are selected.`,
  SelectAllItems: (itemType: string, totalItemCount: number) =>
    `Select all ${totalItemCount} ${itemType}`,
  selectAgentRequired: 'Please select an Agent before performing the upload',
};

export const popupWidth = {
  downloadPopup: '380px',
  searchPopup: '500px',
  deletePopup: '360px',
  arAdditionalChargesModal: '480px',
  hawbDelete: '400px',
  advancedSearchPopup: '1100px',
  printPopup: '900px',
  gatewayPopup: '95vw',
  warningPopup: '340px',
  gatewayLocationConfirmationPopup: '416px',
  existingAccountsPopup: '611px',
  windowAlert:
    'Password has been reset successfully. \n Please sign-in with your new password',
  cbpClearanceSearchPopup: '585px',
  setCBPResponsePopup: '450px',
  numberRangesPopup: '700px',
  apChargeCodesDetailPopup: '850px',
  helperTextPopup: '45em',
  scanBagShipPopup: '80vw',
  associateUfoShipmentsPopup: '900px',
  currencyPopup: '650px',
  transitPointDialog: '1200px',
  bulkRoutingPopup: '1200px',
  bulkPodPopup: '567px',
  manifestPodLinkedShipmentsPopup: '640px',
  cbpEntryNumbersPopup: '720px',
  searchPopupWidth: '535px',
  publishManifestPopup: '530px',
  partialAutoRatingProceedPopup: '560px',
  linkIbcUsersPopup: '600px',
};

export const MANIFEST_EMAIL_ACCEPTED_FILE_TYPES =
  '.csv,.xlsx,.xls,.pdf,.txt,.jpg,.jpeg,.png';

export const MAX_MANIFEST_EMAIL_ATTACHMENTS = 5;

export const hyperlinkLabels = {
  mawb: 'MAWB',
  agent: 'Code',
  hawb: 'Shipment',
  manifest: 'Manifest Number',
  bag: 'Bag',
  account: 'Account Number',
  smRolesAndPermissions: 'Department',
  fullName: 'Full Name',
  smAirports: 'Airport Code',
  seriesName: 'Series Name',
  smAirlines: 'Airline Code',
  smStatusCodes: 'IBC Code',
  fullmanFetchFileName: 'File Name',
  fullmanFetchErrorInfo: 'Error Information',
  cbpStatusDescription: 'CBP Status Description',
  fullmanAccount: 'Account',
  fullmanAction: 'Actions',
  ediFileName: 'EDI File Name',
  hsCode: 'HS Code',
  description: 'Description',
  facility: 'Code',
  serviceType: 'Type',
  agentType: 'Type',
  serviceTypeGroup: 'Group Code',
  responseInfo: 'Response Information',
  clearanceType: 'Type',
  range: 'Range(s)',
  actions: 'Actions',
  deliveryZone: 'Delivery Zone',
  rateScales: 'Rate Scale',
  ibcChargeCode: 'IBC Charge Code',
  apChargeCodes: 'Agent Code',
  accountsReceivables: 'Invoice Number',
  apChargeCodeLink: 'AP Charge Code(s)',
  accountsPayable: 'Invoice Number',
  agentShipmentTracker: 'Agent Shipment Tracker',
  addAttachment: 'Add Attachment',
  currencyCode: 'Currency Code',
  currencyDetails: 'Details',
  routing: 'Origin',
  shipment: 'Shipment',
  manifestPublishAttachment: 'Attachment'
};

export const LocalDataStorage = {
  FlightList: 'FlightList',
};

export const ShipmentsMiscellaneousMessages = {
  ResidentialHeader: 'Residential',
  Residential:
    '(Indicates that the Shipment is to be delivered at the Residence of the Consignee)',
  SignatureRequiredHeader: 'Signature Required',
  SignatureRequired:
    '(Indicates that the Delivery Agent should capture the Signature of the Consignee upon Delivery of the Shipment)',
};

export const ElementRefContainerNames = {
  container: 'container',
  panel: 'panel',
  mainContainer: 'maincontainer',
  validatedFullmanFileAccordion: 'validatedFullmanFileAccordion',
  customsCityDraftAccordion: 'customsCityDraftAccordion',
  customsCityAbiAccordion: 'customsCityAbiAccordion',
  cbpRequestStatusAccordion: 'cbpRequestStatusAccordion',
  cbpResponseAccordion: 'cbpResponseAccordion',
  uploadFullmanFileAndCbpResponse: 'uploadFullmanFileAndCbpResponse',
};

export const AttachmentsIconsOrder = [1, 5, 6, 4];
export const ShipmentIconsOrder = [1, 10, 11, 2, 3, 4, 9, 13, 5, 6, 7, 8];
export const UserIconsOrder = [1, 2, 3, 4, 9, 5, 6, 7, 8];

export const fileNameExtensions = ['pdf', 'csv', 'xls'];
export const fileMaximumSize = 25;

export const AutoRatedLockedFields: readonly string[] = [
  'accountNumber', 'origin', 'originId', 'destination', 'finalDestination',
];

/** Shipment weight / dimension fields gated by AR invoice (HAWB / MAWB). */
export const HawbWeightDimensionFieldNames: readonly string[] = [
  'weight',
  'dimL',
  'dimW',
  'dimH',
  'dimensionalWeight',
];

export const IconsOrder = {
  add: 1,
  view: 2,
  edit: 3,
  delete: 4,
  save: 5,
  close: 6,
  downloadFile: 7,
  print: 8,
  sendCbp: 9,
  blockShipment: 10,
  unblock: 11,
  autoRate: 12,
  gateway: 13,
};

export const downloadInfoPageSectionKeys = {
  mawbInfo: 'mawbInfo',
  mawbmiscellaneous: 'mawbmiscellaneous',
  notes: 'notes',
  events: 'events',
  attachments: 'attachments',
  details: 'details',
  mawbManifests: 'mawbManifests',
  mawbFlights: 'mawbFlights',
  manifestInfo: 'manifestInfo',
  manifestsBag: 'manifestsBags',
  widgets: 'widgets',
  blockUnblock: 'blockUnblock',
  hawbInfo: 'hawbInfo',
  hawbCommodity: 'hawbCommodity',
  hawbRoles: 'hawbRoles',
  hawbMiscellaneous: 'hawbMiscellaneous',
  hawbStatusCode: 'hawbStatusCode',
  hawbConsignee: 'hawbConsignee',
  hawbShipper: 'hawbShipper',
  bagInfo: 'bagInfo',
  bagCriteria: 'bagCriteria',
  bagMiscellaneous: 'bagMiscellaneous',
  bagPieces: 'bagPieces',
  userInfo: 'userInfo',
  userDepartments: 'departments',
  loginInfo: 'loginInfo',
  shipmentInfoFieldLevelAddFlow: 'shipmentInformationFieldLevelAddFlow',
  agentInfo: 'agentInfo',
  agentAddress: 'agentAddress',
  hsCode: 'hsCode',
  airlineInfo: 'airlineInfo',
  airlineAddress: 'airlineAddress',
  airportInfo: 'airportInfo',
  airportAddress: 'airportAddress',
  numberRange: 'numberRange',
  numberRangeDetails: 'numberRangeDetails',
  numberRanges: 'numberRanges',
  accountInfo: 'accountInfo',
  accountAddress: 'accountAddress',
  businessInformation: 'businessInformation',
  customerManagement: 'customerManagement',
  users: 'users',
  billingAddress: 'billingAddress',
  apChargeCodeInfo: 'apChargeCodeInfo',
  apChargeCode: 'apChargeCode',
  deliveryZoneInfo: 'deliveryZoneInfo',
  deliveryZoneInfoTable: 'deliveryZone',
  rateScaleInfo: 'rateScaleInfo',
  rateScaleAccessorialCharges: 'rateScaleAccessorialCharges',
  rateScaleWeightBasedCharges: 'rateScaleWeightBasedCharges',
  apInvoiceInfo: 'apInvoiceInfo',
  apCharges: 'apCharges',
  governmentInformation: 'governmentInformation',
  governmentCustomerService: 'governmentCustomerService',
  governmentMasterShipmentBilling: 'governmentMasterShipmentBilling',
  customerService: 'customerService',
  arInvoiceInfo: 'arInvoiceInfo',
  arCharges: 'arCharges',
  commodityDetails: 'commodityDetails',
  shipmentInfo: 'shipmentInfo',
  bagInformation: 'bagInformation',
  linkedShipmentInfo: 'linkedShipmentInfo',
  routingInfo: 'routingInfo',
  routes: 'routes',
  transitPoints: 'transitPoints',
};

export const downloadInfoPageSectionHeaders = {
  mawbInfo: 'MAWB Information',
  mawbmiscellaneous: 'MAWB Miscellaneous',
  notes: 'Notes',
  events: 'Events',
  attachments: 'Attachments',
  details: 'Details',
  mawbManifests: 'Manifest(s) linked',
  mawbFlights: 'Flight(s) linked',
  manifestInfo: 'Manifest Information',
  manifestBag: 'Bag(s) linked',
  widgets: 'Widgets',
  blockUnblock: 'Block or Unblock',
  hawbInfo: 'Shipment Information',
  hawbCommodity: 'Commodity(s)',
  hawbRoles: 'Roles',
  hawbMiscellaneous: 'Shipment Miscellaneous',
  hawbStatusCode: 'Status Codes',
  hawbConsignee: 'Consignee',
  hawbShipper: 'Shipper',
  bagInfo: 'Bag Information',
  bagMiscellaneous: 'Miscellaneous',
  bagCriteria: 'Criteria',
  bagPieces: 'Shipment(s) linked',
  userInfo: 'User Information',
  userDepartments: 'Departments',
  loginInfo: 'Login Information',
  shipmentInfoFieldLevelAddFlow:
    'Shipment Information - Field Level Permissions - AddFlow',
  agentInfo: 'Agent Information',
  agentAddress: 'Agent Address',
  address: 'Address',
  hsCodeInfo: 'HS Code Information',
  airlineInfo: 'Airline Information',
  airlineAddress: 'Airline Address',
  airportInfo: 'Airport Information',
  airportAddress: 'Airport Address',
  numberRange: 'Number Ranges',
  numberRangeDetails: 'Number Range Details',
  numberRanges: 'Range(s)',
  airlineIn: 'Airline Info',
  airportIn: 'Airport Info',
  accountInfo: 'Account Information',
  accountAddress: 'Account Address',
  businessInformation: 'Business Information',
  customerManagement: 'Customer Management',
  users: 'Users',
  billingAddress: 'Billing Address',
  apChargeCodeInfo: 'AP Charge Code Information',
  apChargeCode: 'AP Charge Code',
  deliveryZoneInfo: 'Delivery Zone Information',
  deliveryZoneInfoTable: 'Delivery Zone',
  rateScaleInfo: 'Rate Scale Information',
  rateScaleAccessorialCharges: 'Accessorial Charges',
  rateScaleWeightBasedCharges: 'Weight Based Charges',
  apInvoiceInfo: 'AP Invoice Information',
  apCharges: 'AP Charges',
  governmentInformation: 'Government Information',
  governmentMasterShipmentBilling: 'Master Shipment Billing',
  customerService: 'Customer Service',
  arInvoiceInfo: 'AR Invoice Information',
  arCharges: 'AR Charges',
  commodityDetails: 'Commodity Details',
  linkedShipmentInfo: 'Linked Shipment(s)',
  routingInfo: 'Routing Information',
  routes: 'Routes',
  transitPoints: 'Transit Points',
};

export const RefreshRelatedData = {
  refreshBagPaginationProps: 'refreshBagPaginationProps',
  manifestBulkEditData: 'manifestBulkEditData',
  refreshManifestPaginationProps: 'refreshManifestPaginationProps',
  refreshAgentsPaginationProps: 'refreshAgentsPaginationProps',
  bagBulkEditData: 'bagBulkEditData',
  hawbBulkEditData: 'hawbBulkEditData',
  refreshRoutingsPaginationProps: 'refreshRoutingsPaginationProps',
} as const;

export const AttachmentType = {
  CommercialInvoice: 'Commercial Invoices',
  Others: 'Others',
};

export const AttachmentTypeFlag = {
  CustomerInvoice: 'r',
  Others: 'o',
};
export const RolesType = {
  consignee: 'Consignee',
  shipper: 'Shipper',
  agentInfo: 'Agent-Info',
  address: 'Address',
  account: 'account',
  accountBillingAddress: 'accountBillingAddress',
  other: 'Other',
};

export const RolesTypeId = {
  C: 'C',
  S: 'S',
};

export const countryCodes = {
  usa: 'US',
  canada: 'CA',
  brazil: 'BR',
  unitedKingdom: 'GB',
};
export const stateRequiredCountryCodes: readonly string[] = [
  countryCodes.usa,
  countryCodes.canada,
  countryCodes.brazil,
];

export const consigneePostalRequiredCountryCodes: readonly string[] = [
  ...stateRequiredCountryCodes,
  countryCodes.unitedKingdom,
];

export function isStateRequiredCountry(countryCode: string): boolean {
  return stateRequiredCountryCodes.includes(countryCode);
}

export function isConsigneePostalRequiredCountry(countryCode: string): boolean {
  return consigneePostalRequiredCountryCodes.includes(countryCode);
}

export function isConsigneePostalOnlyCountry(countryCode: string): boolean {
  return (
    isConsigneePostalRequiredCountry(countryCode) &&
    !isStateRequiredCountry(countryCode)
  );
}

export const defaultOriginWarehouse = {
  code: 'USA',
  facType: 'CC',
};

export const matClasses = {
  matExpansionIndicator: 'mat-expansion-indicator',
};

export const dynamicClassNames = {
  bagCrudClass: 'bag-crud-container',
  shipmentCrudClass: 'shipment-crud-container',
  mawbCrudClass: 'mawb-crud-container',
  flightCrudClass: 'flight-crud-container',
  manifestCrudClass: 'manifest-crud-container',
};

export const formFieldIds = {
  manifestDate: 'manifestDate',
  shipmentBasicSearch: 'shipmentBasicSearchFields',
  dateAndTimeZone: 'dateandTimeZone',
  bagFlightNumber: 'bagFlightNumber',
  maxBagWeight: 'maxBagWeight',
  maxShipmentValue: 'maxShipmentValue',
  manifestMawbAirline: 'manifestMawbAirline',
  manifestAirlineFlight: 'manifestAirlineFlight',
  attachments: 'attachments',
  addtionalDetails: 'additionalDetails',
  shipmentsLinked: 'shipmentsLinked',
  codeAndDescription: 'codeAndDescription',
  dateTimeAndTimezone: 'dateTimeAndTimezone',
  deliveryAgent: 'deliveryAgent',
  rateScale: 'rateScale',
  airlineCode: 'airlineCode',
  location: 'location',
  department: 'department',
  role: 'role',
  group: 'group',
  commodityNumber: 'commodityNumber',
  seriesName: 'seriesName',
  facility: 'facility',
  numberRangeTypeName: 'numberRangeTypeName',
  applicableFor: 'applicableFor',
  statusType: 'statusType',
  statusCodeAgent: 'agent',
  statusCodeFacility: 'location',
  number: 'number',
  priority: 'priority',
  ibcChargeCode: 'ibcChargeCode',
  agentChargeCode: 'agentChargeCode',
  chargeCode: 'chargeCode',
  agentChargeTypes: 'agentChargeType',
  shipmentIdentifierValue: 'shipmentIdentifierValue',
  currency: 'incomingChargeCurrency',
  chargeType: 'chargeType',
  origin: 'origin',
  destination: 'destination',
  countryCode: 'countryCode',
};

export const StaticSymbols = {
  dollar: '$',
  dash: '-',
  colon: ':',
  lbUnit: ' lb',
  notApplicable: 'N/A',
};

export const matFormFieldsTypes = {
  input: 'input',
  select: 'select',
  date: 'datepicker',
  textarea: 'textarea',
  autocomplete: 'autocomplete',
};

export const BasicSearchValues = {
  pin: 'Pin',
  advancedSearch: 'Advanced Search',
  hamburger: 'Hamburger',
  helperText: 'Helper Text',
};

export const popupConditions = {
  allow: 'allow',
  remove: 'remove',
};

export const formFieldTypes = {
  input: 'input',
  datePicker: 'datepicker',
  select: 'select',
  radio: 'radio',
  checkbox: 'checkbox',
  multiSelectAutoComplete: 'multi-select-autocomplete',
  textarea: 'text',
};

export const AddDeleteRequestType = {
  Hawb: 'HAWB',
  Hawb_Commodities: 'HAWB_COMMODITIES',
  Bag: 'BAG',
  Manifest: 'MANIFEST',
  Mawb: 'MAWBS',
  mawbFlight: 'MAWB_FLIGHT',
};

export const breadcrumbNames = {
  CancelRecoverShipments: 'Cancel / Recover Shipments',
  Shipment: 'Shipment',
  ScanShipments: 'Scan Shipments',
  Bag: 'Bag',
  Manifest: 'Manifest',
  Account: 'Account',
  MAWB: 'MAWB',
  Flight: 'Flight',
  AccountInfo: 'Account Information',
  StatusCodes: 'Status Codes',
  RolesAndPermissions: 'Roles & Permissions',
  RolesAndPermissionsInfo: 'Roles & Permissions Information',
  fullmanUpload: 'Fullman File Upload',
  UserManagement: 'User Management',
  UserInformation: 'User Information',
  Airports: 'Airports',
  AirportInfo: 'Airport Information',
  UserProfile: 'My Profile',
  NumberRanges: 'Number Ranges',
  Airlines: 'Airlines',
  AirlineInfo: 'Airline Information',
  AgentTypes: 'Agent Types',
  HsCodes: 'HS Code',
  HsCodeInfo: 'HS Code Information',
  Facilities: 'Facilities',
  FacilityInfo: 'Facility Information',
  ServiceTypes: 'Service Types',
  AccountPayable: 'Accounts Payable',
  AccountReceivable: 'Accounts Receivable',
  Home: 'Home',
  ServiceTypeGroups: 'Service Type Groups',
  ClearanceTypes: 'Clearance Types',
  IbcChargeCodes: 'IBC Charge Codes',
  RateScales: 'Rate Scales',
  RateScaleInfo: 'Rate Scale Information',
  deliveryZone: 'Delivery Zone',
  deliveryZoneInfo: 'Delivery Zone Information',
  apChargeCode: 'AP Charge Codes',
  apChargeCodeInfo: 'AP Charge Code Information',
  apInfo: 'AP Invoice Information',
  accountsReceivableInvoiceInfo: 'AR Invoice Information',
  currencyManagement: 'Currency Management',
  Routing: 'Routing',
  RoutingInfo: 'Routing Information',
  Dashboard: 'Dashboard',
  Reports: 'Reports',
  ShortcutKeys: 'Shortcut keys',
};

/** SPEC-BTT-001 browser tab title: `PacTrak - <Module>`. */
export const PageTitle = {
  brand: 'PacTrak',
  separator: ' - ',
} as const;

export const PageTitleLabels = {
  Agent: 'Agent',
  SetupAndMaintenance: 'Setup & Maintenance',
  PrintPreview: 'Print Preview',
  ShortcutKeys: 'Shortcut Keys',
} as const;

/**
 * Module-level and Setup & Maintenance item breadcrumb aliases only. Page-level
 * aliases (e.g. `hawb-information`) are left out on purpose so nested pages
 * resolve to their owning module (D-BTT-003).
 */
export const PageTitleByBreadcrumbAlias: Readonly<Record<string, string>> = {
  home: breadcrumbNames.Home,
  dashboard: breadcrumbNames.Dashboard,
  hawb: breadcrumbNames.Shipment,
  bag: breadcrumbNames.Bag,
  manifest: breadcrumbNames.Manifest,
  mawb: breadcrumbNames.MAWB,
  'cancel-recover-shipments': breadcrumbNames.CancelRecoverShipments,
  'scan-shipments': breadcrumbNames.ScanShipments,
  'user-profile': breadcrumbNames.UserProfile,
  'accounts-payable': breadcrumbNames.AccountPayable,
  'accounts-receivable': breadcrumbNames.AccountReceivable,
  reports: breadcrumbNames.Reports,
  'shortcut-keys': PageTitleLabels.ShortcutKeys,
  'sm-dashboard': PageTitleLabels.SetupAndMaintenance,
  'agent-summary': PageTitleLabels.Agent,
  'account-summary': breadcrumbNames.Account,
  'status-codes': breadcrumbNames.StatusCodes,
  'roles-and-permissions': breadcrumbNames.RolesAndPermissions,
  'fullman-upload': breadcrumbNames.fullmanUpload,
  'user-management': breadcrumbNames.UserManagement,
  airports: breadcrumbNames.Airports,
  'ap-charge-codes': breadcrumbNames.apChargeCode,
  'number-ranges': breadcrumbNames.NumberRanges,
  airlines: breadcrumbNames.Airlines,
  'agent-types': breadcrumbNames.AgentTypes,
  'hs-codes': breadcrumbNames.HsCodes,
  facilities: breadcrumbNames.Facilities,
  'service-types': breadcrumbNames.ServiceTypes,
  'service-type-groups': breadcrumbNames.ServiceTypeGroups,
  'clearance-types': breadcrumbNames.ClearanceTypes,
  'currency-management': breadcrumbNames.currencyManagement,
  'ibc-charge-codes': breadcrumbNames.IbcChargeCodes,
  'delivery-zone': breadcrumbNames.deliveryZone,
  'rate-scales': breadcrumbNames.RateScales,
  routing: breadcrumbNames.Routing,
};

/** Alias-less popup routes, keyed by `routeConfig.path`. */
export const PageTitleByRoutePath: Readonly<Record<string, string>> = {
  'print-preview': PageTitleLabels.PrintPreview,
  'info-print-preview': PageTitleLabels.PrintPreview,
  'nested-print-preview': PageTitleLabels.PrintPreview,
  'shipment-label-generation-preview': breadcrumbNames.Shipment,
};

export const requestTypeIdCheck = {
  id: 'id',
  object: 'object',
  name: 'name',
};

export const TypeCheck = {
  number: 'number',
  string: 'string',
};

export const LabelsAndPlaceHolders = {
  variousValues: 'Various Values',
  floatLabelAlways: 'always' as const,
  floatLabelAuto: 'auto' as const,
  originLabel: 'Origin',
  destinationLabel: 'Destination',
  finalWareHouseDestLabel: 'Final Destination',
};

export const AdvanceSearchBulkEditConsingeeShipper = [
  'id',
  'type',
  'action',
  'roleType',
  'address',
  'country',
];

export const FlightMawbPart = {
  A: 'A',
};

export const MawbPartValuesForFlight = ['A', 'B', 'C'];

export const ShipmentGatewayConstants = {
  details: 'Details',
  consignee: 'Consignee',
  shipper: 'Shipper',
  route: 'Route',
  recievedAt: 'Received at',
  manifestedTo: 'Manifested to',
  performGatewayToStartRouting: 'Perform Gateway to start Routing',
};

export const ButtonText = {
  editShipment: 'Edit Shipment',
  cancel: 'Cancel',
  save: 'Save',
  createNewBag: 'Create New Bag',
  gateway: 'Gateway',
  createNewShipment: 'Create New Shipment',
  createNewManifest: 'Create New Manifest',
  createNewMAWB: 'Create New MAWB',
  apPublish: 'Publish',
  rePublish: 'Re-Publish',
  publishToGP: 'Publish to GP',
};

export const tooltipTexts = {
  gatewayScan: 'Scan or type manually',
};

export const MawbManifestTooltipMessages = {
  originDestinationAndMawbDateTooltip:
    'Please fill in MAWB Origin, Destination and MAWB Date first',
  invalidManifestMessage: 'No valid Manifests to link',
  invalidManifestEntered: 'Invalid Manifest entered',
};

export const MawbFlightRemoval = [
  'airline',
  'mawbPart',
  'slacCount',
  'slacWeight',
  'arrivalDate',
  'airlineId',
  'airlineCode',
];

export const MawbFlightMandatoryCheck = [
  'airline',
  'mawbPart',
  'arrivalDate',
  'airlineId',
  'airlineCode',
];

export const rolesOptionalFields = [
  'state',
  'email',
  'companyName',
  'address_2',
  'phone',
];

export const tabKeyboardEvent = new KeyboardEvent('keydown', {
  key: 'Tab',
  code: 'Tab',
  keyCode: 9,
  charCode: 0,
  which: 9,
  bubbles: true,
  cancelable: true,
});

export const NotesConstants = {
  noNotes: 'No Notes available on this date',
  noCloseDate: 'No Notes available on this date. Showing closest future note.',
  noFutureDate: 'No future notes available.',
};

export const GatewayConstants = {
  already1match: 'already gatewayed with 1 match',
  matchNotGateway: 'match found and not gatewayed',
  cbpClearnance: 'cbp clearance check',
  cbpHeldBlock: 'cbp held block',
  shipmentNotFound: 'Shipment not found',
  shipmentMultipleFound: 'Shipment multiple match found',
  shipmentNotExpectedAtLocation: "shipment not expected at location",
  shipmentRouteCompleted: "shipment route completed",
  gatewayLocationMismatch: "gateway location mismatch",
  previousTransitPointNotPublished: "previous transit point not published",
  masterMultiPieceGateway: 'masterMultiPieceGateway',
  pieceCountShortage: 'pieceCountShortage',
  pieceCountOverage: 'pieceCountOverage',
  houseOverageCbpNotCleared: 'houseOverageCbpNotCleared',
  scanPreferenceShipmentBlock: 'scan preference shipment block',
};

/** SPEC-OCS-001 — aligned with pactrak-node OVERAGE_CBP_STATUS_CODE. */
export const OVERAGE_CBP_STATUS_CODE = {
  notReported: 'ONRTC',
  reported: 'ORTC',
} as const;

export const conditionCheckConstants = {
  newBag: 'New Bag',
  billingAddress: 'billingAddress',
  autocompleteError: 'autocompleteError',
  extra: 'extra',
  invalidEndNumber: 'invalidEndNumber',
  newManifest: 'New Manifest',
  newMAWB: 'New MAWB',
  invalidPostalCode: 'invalidPostalCode',
  invalidPostalEnd: 'invalidPostalEnd',
  atLeastOneRequired: 'atLeastOneRequired',
  invalidShipmentExt: 'invalidShipmentExt',
  arInvoiceBlockedCannotAdd: 'arInvoiceBlockedCannotAdd',
  accountInvoiceHold: 'accountInvoiceHold',
  invalidMawbExt: 'invalidMawbExt',
  mawbBlockedArInvoice: 'mawbBlockedArInvoice',
  shipmentExt: 'shipmentExt',
  invalidDecimal: 'invalidDecimal',
  weight: 'weight',
  bagWeight: 'bagWeight',
  bagExt: 'bagExt',
  origin: 'origin',
  destination: 'destination',
  shipmentWeight: 'shipmentWeight',
  shipperInfo: 'shipperInfo',
  shipperDetails: 'shipperDetails',
  consigneeInfo: 'consigneeInfo',
  consigneeDetails: 'consigneeDetails',
  commodityDetails: 'commodityDetails',
  invalidAgentNumberExt: 'invalidAgentNumberExt',
  invalidShipmentAutoRate: 'invalidShipmentAutoRate',
  invalidAPInvoice: 'invalidAPInvoice',
  invalidARInvoice: 'invalidARInvoice',
  noFacilitiesForCountry: 'noFacilitiesForCountry',
  invalidStartNumber: 'invalidStartNumber',
  sameTransitPoint: 'sameTransitPoint',
  duplicatePriority: 'duplicatePriority',
  priorityOutOfRange: 'priorityOutOfRange',
};

export const autogenerateAccountNumbner = {
  nextMasterAccount: 'nextMasterAccount',
  nextLinkedAccount: 'nextLinkedAccount',
};

export const ModalPopupRadioOptions = {
  firstLinkedAccountOption1:
    'Change Existing Shipments to Account {newAccountNumber}',
  firstLinkedAccountOption2:
    'Leave Existing Shipments as Account {accountNumber}',
};

export const LinkedAccountConstants = {
  changeExisting: 'Change Existing Shipments',
  leaveExisting: 'Leave Existing Shipments',
  wrongAccountNumber: 'Account number should be always 7 digits',
  linkedAccountNotEndWith0: 'Linked account cannot be ending with 00',
  accountExists: 'Entered account number already exists',
  accountDependencyDelete: (count: number) =>
    `Selected Account has linked Account${count > 1 ? 's' : ''
    } or associated Shipment${count > 1 ? 's' : ''} and thus cannot be deleted`,
  multipleAccountDependencyDelete:
    'One or more Selected Accounts has linked accounts or associated Shipments and thus cannot be deleted',
  miscellaneousOrUfoAccountCannotBeDeleted: (accountType: string) =>
    `${accountType} account cannot be deleted. To proceed with deleting the other selected accounts, click Delete.`,
  miscellaneousAndUfoAccountCannotBeDeleted: 'Miscellaneous and UFO account cannot be deleted. To proceed with deleting the other selected accounts, click Delete.',
  bothmiscUfoAccountCannotBeDeleted: 'Miscellaneous and UFO account cannot be deleted',
};

export const apiCrudActions = {
  create: 'create',
  update: 'update',
  delete: 'delete',
};

export const UserManagementConstants = {
  invalidEmail: 'Please enter a valid email address',
  locationSelectionProgrammaticSentinel: 'data',
  departmentsSectionHelpTooltip:
    'Combination of Facility and Department or Role is mandatory in order to create a Department.',
};

/** Sentinel id for My Profile "All" default facility selection. */
export const USER_PROFILE_ALL_FACILITIES_ID = 0;

/** Display label for My Profile / User Management "All" default facility. */
export const USER_PROFILE_ALL_FACILITIES_LABEL = 'All';

/** Shown on every module when Default Facility is All and user has 2+ warehouses. */
export const ALL_FACILITIES_VIEW_ONLY_BANNER =
  "Only View is allowed when the Default Facility is set to 'All'";

/** Gateway is blocked until the user picks a single default warehouse. */
export const ALL_FACILITIES_GATEWAY_BLOCK_MESSAGE =
  'Change your Default location to a single warehouse to proceed with Gatewaying';

/** Display labels for super-admin users (User Management grid + User Profile). */
export const SuperAdminDisplayLabels = {
  role: 'Super Admin',
  department: 'NA',
  facility: USER_PROFILE_ALL_FACILITIES_LABEL,
} as const;

export const RolesAndPermissionsCheckBoxStates = {
  checked: 'checked',
  unchecked: 'unchecked',
};

export const RolesAndPermissionVisisbility = {
  all: 'All',
  customize: 'Customize',
  none: 'None',
};

export const RolesAndPermissionFlow = {
  add: 'Add flow',
  view: 'View flow',
  edit: 'Edit flow',
};

export const fieldLevelPermissionsTabTooltip =
   'Field Level Permissions applicable only for Edit and Bulk Edits';

export const DropdownPopupHeaderMessage: any = {
  group: 'Group',
  department: 'Department',
  role: 'Role',
  agentChargeCode: 'Agent Charge Code',
};

export const DropdownAPIRequestType: any = {
  Group: 'Groups',
  Department: 'Department',
  Role: 'Roles',
};

export const regexPattern = {
  alphanumericWithSpace: /[^a-zA-Z0-9 ]/g,
  alphabetsWithSpace: /[^a-zA-Z ]/g,
  caseInSensitive: /[\s-]/g,
  digitsOnly: /^\d+$/,
  reportFilterTypeUnderscore: /_/g,
  /** Lookup-less report filter named `*_month` (e.g. `start_month`) renders as a month/year picker. */
  reportFilterMonthName: /_month$/i,
  // Transit point specific patterns
  timeHHMM: /^([0-1]\d|2[0-3]):[0-5]\d$/,
  warehouseCodeExtract: /\(([^)]+)\)$/,
};

export const basicSearchInputRegex = {
  mainModules: /[^a-zA-Z0-9\s_]/g,
  usersModule: /[^a-zA-Z0-9\s._@:-]/g,
  smModules: /[^a-zA-Z0-9\s]/g,
};

export const userProfileCards = {
  userProfile: 'userProfileCard',
  changePassword: 'changePassword',
};

export const airlinePreferenceTexts = {
  preferenceCount:
    'There are two Airlines (Lufthansa Cargo, Lufthansa) with the same Airline Code (LH).',
  preferredAirline: 'Lufthansa Cargo (LH) is preferred by default.',
};

export const lookupHeaders = {
  country: 'Country',
  state: 'State',
  origin: 'Origin',
  destination: 'Destination',
};

export const lookupPluralHeaders = {
  countries: 'Countries',
  states: 'States',
  originFacility: ' Origin Facility',
  destinationFacility: ' Destination Facility',
};

export const lookupcontrolNames = [
  'origin',
  'originID',
  'originName',
  'destination',
  'destinationID',
  'destinationName',
  'finalWareHouseDest',
];

export const MandatoryFieldMessages = {
  chargeableWeightAndPallets:
    'Either Actual Weight or at least one Pallet with dimensions must be provided',
  requiredFields: (invalidTabsList: string[]) => {
    if (invalidTabsList.length === 1) {
      return `Please complete all the required fields in the following section:\n\n- ${invalidTabsList[0]}\n\nCheck all inputs before proceeding.`;
    } else {
      const formattedSections = invalidTabsList
        .map(section => `- ${section}`)
        .join('\n');
      return `Please complete all the required fields in the following sections:\n${formattedSections}\n\nCheck all inputs before proceeding.`;
    }
  },
  shipmentInfo: 'Shipment Information Section',
  ShipperConsgineeSection: 'Shipper & Consignee Section',
  bagInfo: 'Bag Information Section',
  manifestInfo: 'Manifest Information Section',
  mawbInfo: 'MAWB Information Section',
  accountInfo: 'Account Information Section',
  userInfo: 'User Information Section',
  flightInfo: 'Flight Section',
  governmentTabValidation: 'Government Information Section',
  businessInfoTabValidation: 'Business Information Section',
  customerManagementTabValidation: 'Customer Management Section',
  accountUsers: 'Users Section',
  accountAddressInfo: 'Address Section',
  rolesPermission: 'Roles & Permissions Information section',
  rateScaleInfo: 'Rate Scale Information Section',
  accountPayableInfo: 'Account Payable Information Section',
  accessorialOrWeightBased:
    'At least one Accessorial Charge or Weight Based Charge is required.',
  rateScaleIbcChargeCode: 'IBC charge code is mandatory',
  accountReceivableInfo: 'Account Receivable Information Section',
};
export const statusCodeTypeNames = {
  ace: 'ace',
  block: 'block',
};

export const fullmanAndCbpStatus = {
  completed: 'Completed',
  dataPending: 'Request data pending',
  inProgress: 'In progress',
  processed: 'Processed',
  rejected: 'Rejected',
  yetToStart: 'Yet to start',
};

/** Customs City draft grid (Table 2) — values as returned by API. */
export const customsCityDraftGridStatus = {
  queued: 'QUEUED',
  processing: 'PROCESSING',
  submitted: 'SUBMITTED',
  error: 'ERROR',
} as const;

/** Customs City ABI grid (Table 3) — values as returned by API. */
export const customsCityAbiGridStatus = {
  pending: 'PENDING',
  entryFetched: 'ENTRY_FETCHED',
  sent: 'SENT',
  accepted: 'ACCEPTED',
  rejected: 'REJECTED',
  error: 'ERROR',
} as const;

/** CBP event types from `cc_event_type` master lookup (Table 3 status column). */
export const customsCityCbpEventType = {
  amsAccepted: 'AMS_ACCEPTED',
  amsRejected: 'AMS_REJECTED',
  amsReleased: 'AMS_RELEASED',
  amsHold: 'AMS_HOLD',
  acasAccepted: 'ACAS_ACCEPTED',
  acasRejected: 'ACAS_REJECTED',
  abiAccepted: 'ABI_ACCEPTED',
  abiRejected: 'ABI_REJECTED',
} as const;

/** Shared badge classes for Fullman + Customs City status columns. */
export const gridStatusBadgeClass = {
  success: 'fullman-file-success-status',
  pending: 'fullman-files-pending-status',
  rejected: 'fullman-files-rejected-status',
  yetToStart: 'fullman-file-yet-to-start-status',
} as const;

export const fullmanAndCbpGridFields = {
  fileName: 'fileName',
  fullmanParsedDataId: 'fullmanParsedDataId',
  errorInfo: 'errorInfo',
  error: 'error',
  customCityClearanceJobId: 'customCityClearanceJobId',
  requestedEdi: 'requestedEdi',
  cbpRequestInfoId: 'cbpRequestInfoId',
  responseEdi: 'responseEdi',
  responseInfo: 'responseInfo',
  status: 'status',
  cbpStatusCodes: 'cbpStatusCodes',
  cbpStatusDescriptions: 'cbpStatusDescriptions',
};

export const fullmanFileAndCbpGridActionMessages = {
  viewErrorLog: 'View error log',
  viewCbpStatusDescriptions: 'View CBP status descriptions',
  viewCustomsCityDraftStatus: 'View Customs City Draft Status',
  viewCustomsCityAbiStatus: 'View Customs City ABI Status',
  viewCbpRequestStatus: 'View CBP Request Status',
  viewCbpResponseStatus: 'View CBP Response',
  viewCBPResponseInfo: 'View CBP Information',
};

export const CBPConstantMessages = {
  masterShipmentWarning:
    'Only the Master Shipments of the corresponding linked shipment will be sent for clearance',
};

export const BagGatewayMessages = {
  incomingBag: 'inc_bag',
  incomingMawb: 'inc_mawb',
  outgoingBag: 'out_bag',
  outgoingManifest: 'out_manifest',
  bagGatewayed: 'already gatewayed',
  createBag: 'Bag not found, please create a new Bag',
  createManifest: 'Manifest not found, please create a new Manifest',
  createNewManifest:
    'The entered Outgoing Manifest does not exist. <span class="lighter-bold">Please create a new manifest to proceed.</span>',
  createNewBag:
    'The entered Outgoing Bag does not exist. <span class="lighter-bold">Please create a new bag to proceed.</span>',
  cbpKeyword: 'cbp',
  clearedKeyword: 'cleared',
  clearanceKeyword: 'clearance',
};

export const ScanMessages = {
  scanConfirmationMessage: (existingValue: string, newValue: string) =>
    `Input already has a value:\n\nExisting: ${existingValue}\nScanned: ${newValue}\n\nDo you want to replace it with the scanned value?`,
  confirmReplace: 'Replace Scanned Value?',
};

export const ScanShipmentAudio = {
  Held: 'assets/audios/shipment_held_male.wav',
  Released: 'assets/audios/shipment_released_male.wav',
  Unknown: 'assets/audios/shipment_unknown_male.wav',
  Duplicate: 'assets/audios/duplicate_found_male.wav',
} as const;

export const eventsObjKeys = {
  shipments: 'shipments',
  bagShipmentData: 'bagShipmentData',
  shipmentList: 'shipmentList',
  notes: 'notes',
  miscellaneous: 'miscellaneous',
  attachmentList: 'attachmentList',
  commodityList: 'commodityList',
  roles: 'roles',
  consigneeShipper: 'consigneeShipper',
  notesData: 'notesData',
  is_published: 'is_published',
  bags: 'bags',
  manifests: 'manifests',
  flights: 'flights',
};

export const MAWBChargesEventKeys = {
  ar: 'ar',
  arCharges: 'arCharges',
  ap: 'ap',
  apCharges: 'apCharges',
} as const;

export const DocumentEventStrings = {
  deleted: 'Document Deleted',
  uploaded: 'Document Uploaded',
  uploadedVerb: 'uploaded',
} as const;

export const EventMessages = {
  attachmentList: (fileName: string, action: string) => {
    const verb =
      action === CrudActions.add || action === 'a'
        ? DocumentEventStrings.uploadedVerb
        : eventActionEnums[action];
    return `Document "${fileName}" ${verb}`;
  },
  shipmentEvent: (
    shipmentExt: string | number,
    location: string | number,
    action: string | number,
    actionWord: string
  ) =>
    `Shipment ${shipmentExt} ${eventActionEnums[action]} ${actionWord} the Bag at ${location}`,
  moduleLevelEvent: (module: string, action: string) =>
    `${eventDetailsPropertyKeyDisplayProps[module]} ${eventActionEnums[action]} with `,
  noteEvent: (note: string) => `Note "${note}" Added`,
  commodityEvent: (commodityNumber: number, action: string) =>
    `Commodity "${commodityNumber}" ${eventActionEnums[action]} ${action === CrudActions.create || action === CrudActions.update
      ? 'with'
      : ''
    }`,
  roleEvent: (roleName: string | number, action: string) =>
    `Role "${roleName}" ${eventActionEnums[action]} with`,
  consgineeOrShipperEvent: (type: string, action: string) =>
    `"${eventActionEnums[type]}" ${eventActionEnums[action]} with`,
  parentEventTypeMessage: (module: string, action: string) =>
    `${module} ${eventActionEnums[action]}`,
  childEventTypeMessage: (objType: string, action: string) =>
    `${mapObjectTypeToEvents[objType] || ''} is ${eventActionEnums[action]}`,
  isPublishedMessage: (
    moduleName: string,
    isPublished: boolean,
    location: string
  ) => `${moduleName} is ${isPublished ? 'published' : ''} at ${location}`,
  manifestBagLinkDeLinkMessage: (
    bag: string,
    action: string,
    toFrom: string,
    location: string
  ) =>
    `Bag ${bag} ${eventActionEnums[action]} ${toFrom} the Manifest at ${location}`,
  gatewayedEvent: (isFrom: string, location: string) =>
    `${isFrom} Gatewayed at ${location}`,
  blockedEvent: (isFrom: string, action: string, comments?: string) =>
    `${isFrom} ${action}${comments ? ` - ${comments}` : ''}`,
  mawbManifestLinkDelinkMessage: (
    manifest: string,
    action: string,
    location: string
  ) => {
    {
      let actionWord;
      if (
        action === CrudActions.create ||
        action === CrudActions.add ||
        action === CrudActions.link
      ) {
        actionWord = 'added to';
      } else {
        actionWord = 'removed from';
      }
      return `Manifest ${manifest} ${actionWord} the MAWB at location ${location}`;
    }
  },
  mawbFlightAddMessage: (flight: string, action: string) => {
    let actionWord;
    if (action === CrudActions.create || action === CrudActions.add) {
      actionWord = 'added to';
    } else if (action === CrudActions.update) {
      actionWord = 'updated in';
    } else {
      actionWord = 'removed from';
    }
    return `Flight ${flight} ${actionWord} the MAWB`;
  },
  accountsEvent: (accModule: string, mode: string) =>
    `${eventDetailsPropertyKeyDisplayProps[accModule]} ${eventActionEnums[mode]}`,
  apChargeEvent: (action: string) =>
    `AP Charge ${eventActionEnums[action]}`,
  arChargeEvent: (action: string) =>
    `AR Charge ${eventActionEnums[action]}`,
  autoRateEvent: () => 'Shipment was Auto Rated',
};

export enum eventTypeCode {
  ParentObject = 'po',
  ChildObject = 'cs',
  Blocked = 'bk',
  Unblocked = 'ubk',
  CustomerNotes = 'cn',
  IbcNotes = 'in',
  CommodityCustomerNotes = 'ccn',
  CommodityIbcNotes = 'cin',
  FlightCustomerNotes = 'fcn',
  FlightIbcNotes = 'fin',
  Accounts = 'acc',
  SubAccount = 'sa',
  Gateway = 'g',
  HawbRole = 'hr',
  CommodityRole = 'cr',
  Attachment = 'at',
  CommodityAttachment = 'cat',
  FlightAttachment = 'fat',
  linking = 'l',
  delinking = 'e',
  Published = 'pb',
  FlightLink = 'f',
  CBP = 'cbp',
  commodity = 'cm',
  statusCode = 'sc',
  bagged = 'b',
  manifest = 'm',
  accountUser = 'au',
  APCharge = 'apc',
  ARCharge = 'arc',
  AutoRate = 'ar',
  UnAutoRate = 'uar',
  OverageFound = 'ovf',
  OverageAbiSubmittedManual = 'ovm',
  OverageAbiUpdated = 'ovu',
  OverageAbiUpdateFailed = 'ovx',
  /** SPEC-OMDR-CRON-001 — overage change (MDR) sent to CBP. */
  OverageChangeSent = 'ovs',
  /** SPEC-RSB-001 (PAC-24568) — Customs City resubmit. */
  ResubmitRequested = 'rsq',
  Resubmitted = 'rss',
  ResubmitFailed = 'rsf',
}

export function isParentObjectEventType(
  eventType: string | undefined | null
): boolean {
  return (
    eventType === eventTypeCode.ParentObject ||
    eventType === eventTypeCode.ChildObject
  );
}

/** Node inserts `sc`; fetch/SP may return `st` or `pd` (POD) — treat all as status-code events. */
export function isStatusCodeEventType(
  eventType: string | undefined | null
): boolean {
  const normalized = eventType?.trim();
  return (
    normalized === eventTypeCode.statusCode ||
    normalized === 'st' ||
    normalized === 'pd'
  );
}

export const eventActionEnums: Record<string | number, string> = {
  a: 'added',
  add: 'added',
  l: 'added',
  e: 'removed',
  c: 'created',
  u: 'updated',
  d: 'deleted',
  S: 'Shipper',
  C: 'Consignee',
  g: 'Gatewayed',
  r: 'removed',
};

export const mapObjectTypeToEvents: Record<string, string> = {
  [eventTypeCode.IbcNotes]: 'Note',
  [eventTypeCode.Attachment]: 'Document',
  [eventTypeCode.Gateway]: 'Gatewayed',
  [eventTypeCode.CustomerNotes]: 'Note',
  [eventTypeCode.HawbRole]: 'Role',
  [eventTypeCode.CommodityRole]: 'Commodity Role',
};

export const eventDetailsPropertyKeyDisplayProps: Record<string, string> = {
  accountNumber: 'Account Number',
  destination: 'Destination',
  origin: 'Origin',
  dimH: 'Height (inch)',
  dimL: 'Length (inch)',
  dimW: 'Width (inch)',
  hawbExt: 'Shipment Ext',
  serviceType: 'Service Type',
  weight: 'Weight (lbs)',
  dimensionalWeight: 'Dimensional Weight (lbs)',
  shipperReference: 'Shipper Reference',
  agentInfo: 'Agent',
  agentShipmentTracker: 'Agent Tracking Number',
  masterShipment: 'Master Shipment',
  address_1: 'Address Line 1',
  address_2: 'Address Line 2',
  city: 'City',
  country: 'Country',
  countryCode: 'Country Code',
  state: 'State',
  name: 'Name',
  companyName: 'Company Name',
  phone: 'Phone',
  zipCode: 'Postal Code',
  email: 'Email',
  isResidential: 'Is residential?',
  signatureRequired: 'Is signature required?',
  deliveryTerms: 'Delivery Terms',
  originatorCode: 'Originator Code',
  commodityNumber: 'Commodity Number',
  countryOfManufacturer: 'Country Of Manufacturer',
  hawbId: 'Shipment ID',
  hsCode: 'HS Code',
  value: 'Value',
  location: 'Location',
  bagNumber: 'Bag',
  description: 'Description',
  flightNumber: 'Flight',
  airline: 'Airline',
  arrivalDate: 'Arrival Date',
  slacCount: 'SLAC Count',
  slacWeight: 'SLAC Weight (lb)',
  cargoOperatorName: 'Cargo Operator',
  firstUsAirportName: 'First Us Airport',
  firstUsAirportDate: 'First Us Airport Date',
  manifestNumber: 'Manifest Number',
  tareWeight: 'Tare Weight (lbs)',
  hawbStatusDate: 'Shipment Status Date',
  address: 'Address',
  account_id: 'Account ID',
  account_number: 'Account Number',
  userName: 'User',
  id: 'ID',
  bagExt: 'Bag Ext',
  information: 'Information',
  hawb: 'Shipment',
  commodity: 'Commodity',
  bag: 'Bag',
  manifest: 'Manifest Number',
  agentId: 'Agent ID',
  airlineName: 'Airline Name',
  manifestDate: 'Manifest Date',
  manifestId: 'Manifest ID',
  manifest_id: 'Manifest ID',
  mawb_id: 'MAWB ID',
  bag_id: 'Bag ID',
  hawb_id: 'HAWB ID',
  mawbPart: 'MAWB Part',
  agentName: 'Agent Name',
  originName: 'Origin',
  destinationName: 'Destination',
  mawb: 'MAWB',
  mawbDate: 'MAWB Date',
  mawbExt: 'MAWB',
  clearanceTypeCode: 'Clearance Type',
  clearanceAgent: 'Clearance Agent',
  linehaulCharge: 'Linehaul Charge',
  usCbpEccf86EntryNumber: 'US CBP ECCF 86 Entry Number',
  usCBPEntryNumbers: 'US CBP Entry Numbers',
  mawbFlight: 'Flight',
  customTranslation: 'Custom Translation',
  needsClearanceNonUsDelivery:
    'Needs clearance for Non US delivery shipments landing in US',
  noNeedClearance: 'No Need Clearance',
  scanPreference: 'Scan Preference',
  DUNSNumber: 'DUNS Number',
  paymentTerms: 'Payment Terms',
  billingType: 'Billing Type',
  serviceTypes: 'Service Types',
  deliveryAgents: 'Delivery Agents',
  rateScales: 'Rate Scales',
  chargesConfigured: 'Charges Configured',
  invoiceFileFormat: 'Invoice File Format',
  invoiceType: 'Invoice Type',
  isAutoRate: 'Auto-rate',
  isOutlyingShipment: 'Outlying Shipment',
  organizationName: 'Organization Name',
  accountName: 'Account Name',
  serviceable: 'Serviceable',
  salesPersonId: 'Sales Person',
  account: 'Account',
  deliveryAgentIds: 'Delivery Agent',
  additionalInformation: 'Additional Information',
  FEINNumber: 'FEIN Number',
  creditLimit: 'Credit Limit',
  markUp: 'Mark Up',
  stateTaxId: 'State Tax Id',
  contactPerson: 'Contact Person',
  clearanceChannel: 'Clearance Channel',
  clearanceType: 'Clearance Type',
  clearanceChargesClearanceType: 'Clearance Charges Clearance Type',
  clearanceChargesIbcChargeCode: 'Clearance Charges IBC Charge Code',
  clearanceChargesValuePerShipment: 'Clearance Charges Value Per Shipment',
  deliveryAmount: 'Delivery Amount',
  deliveryIbcChargeCode: 'Delivery IBC Charge Code',
  deliveryWeightMetric: 'Delivery Weight Metric',
  iscActualCost: 'ISC Actual Cost',
  iscFlatFee: 'ISC Flat Fee',
  iscIbcChargeCode: 'ISC IBC Charge Code',
  recoveryAmount: 'Recovery Amount',
  recoveryIbcChargeCode: 'Recovery IBC Charge Code',
  recoveryWeightMetric: 'Recovery Weight Metric',
  reimbursableChargesClearanceType: 'Reimbursable Charges Clearance Type',
  reimbursableChargesIbcChargeCode: 'Reimbursable Charges IBC Charge Code',
  reimbursableChargesValuePerShipment:
    'Reimbursable Charges Value Per Shipment',
  measurementOptions: 'Measurement Options',
  timeZone: 'Time Zone',
  chargeAmount: 'Charge Amount ($)',
  conversionRate: 'Conversion Rate',
  originalChargeAmount: 'Charge Amount (Incoming)',
  originalChargeCurrencyId: 'Original Charge Currency',
  invoiceDate: 'Invoice Date',
  invoiceNumber: 'Invoice Number',
  chargeDescription: 'Charge Description',
  ibcChargeCode: 'IBC Charge Code',
  ibcChargeCodeId: 'IBC Charge Code ID',
  agentChargeCodeId: 'Agent Charge Code ID',
  shipmentIdentifier: 'Shipment Identifier',
  shipmentIdentifierId: 'Shipment Identifier ID',
  destinationId: 'Destination ID',
  publishedToGp: 'Published to GP',
  agent: 'Agent',
  agentChargeCode: 'Agent Charge Code',
  originalChargeCurrency: 'Original Charge Currency',
  ibcChargeDescription: 'IBC Charge Description',
  listPrice: 'List Price',
  orderDate: 'Order Date',
  orderId: 'Order ID',
  retailPrice: 'Retail Price',
  url: 'URL',
  manufacturerId: 'Manufacturer ID',
  itemNumber: 'Item Number',
};

export const apiSuccessMessage = {
  blockShipment: 'Shipment is blocked',
  unblockShipment: 'Shipment is unblocked',
};

export const shipmentCreateUpdateEventDescReqKeys = [
  'hawbExt',
  'masterShipment',
  'accountNumber',
  'origin',
  'destination',
  'serviceType',
  'shipperReference',
  'agentInfo',
  'agentShipmentTracker',
  'weight',
  'dimH',
  'dimL',
  'dimW',
  'dimensionalWeight',
  'isOutlyingShipment',
];

export const shipmentCommodityReqKeys = [
  'commodityNumber',
  'hsCode',
  'description',
  'value',
  'countryOfManufacturer',
  'url',
  'weight'
];

export const shipmentCommodityMiscReqKeys = [
  'itemNumber',
  'listPrice',
  'manufacturerId',
  'orderDate',
  'orderId',
  'url',
  'retailPrice'
];

/** Shipment hawb miscellaneous — always show Delivery Terms in events even when empty. */
export const shipmentHawbMiscellaneousEventKeys = [
  'isResidential',
  'signatureRequired',
  'deliveryTerms',
  'ior',
] as const;

/** Flight miscellaneous event description — display names only, not FK ids. */
export const mawbFlightMiscEventKeys = [
  flightMiscFormFieldKeys.cargoOperatorName,
  flightMiscFormFieldKeys.firstUsAirportDate,
  flightMiscFormFieldKeys.firstUsAirportName,
];

export const shipmentRolesReqKeys = [
  'city',
  'name',
  'email',
  'phone',
  'address_1',
  'address_2',
  'address1',
  'address2',
  'companyName',
  'countryCode',
  'zipCode',
  'state',
];

export const bagCreateUpdateEventDescReqKeys = [
  'bagExt',
  'bagNumber',
  'origin',
  'destination',
  'manifestNumber',
  'description',
  'airline',
  'flightNumber',
];

export const manifestCreateUpdateEventDescReqKeys = [
  'manifestDate',
  'originName',
  'destinationName',
  'agentName',
  'mawb',
  'airline',
  'flightNumber',
  'manifestNumber',
  'information',
];

export const inValidDecimalInputs = ['0', '0.', '0.0', '0.00', '0.000'];
export const miscellaneousCheck = 'Miscellaneous';

export const ibcAddress = [
  {
    companyName: 'IBC',
    addressLineOne: '8401 NW 17th St',
    city: 'Miami',
    state: 'FL',
    postalCode: '33126',
    countryCode: 'US',
    phone: '305 591 8080',
    website: 'www.ibcinc.com',
  },
];

export const shipmentLabelKeys: Record<string, string> = {
  dateAndTime: 'DATE',
  description: 'DESCRIPTION',
  dimensions: 'DIMS',
  masterShipment: 'MASTER SHIPMENT',
  service: 'SERVICE',
  type: 'TYPE',
  value: 'VALUE',
  weight: 'WEIGHT',
};
export const mawbCreateUpdateEventDescReqKeys = [
  'mawbDate',
  'mawbExt',
  'origin',
  'destination',
  'clearanceTypeCode',
  'airlineName',
  'agentName',
  'information',
];

export const mawbFlightReqKeys = [
  'arrivalDate',
  'airline',
  'flightNumber',
  'slacWeight',
  'slacCount',
  'mawb',
  'mawbPart',
  'information',
];

export const addressField = {
  phone: 'phone',
  email: 'email',
};

export const allowedKeys = [
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Shift',
  'Control',
  'Alt',
  'Escape',
];

export const specialKeys = [
  'Backspace',
  'Tab',
  'End',
  'Home',
  'ArrowLeft',
  'ArrowRight',
  'Control',
];

export const rateScaleErrorMessages = {
  noAgent:
    'The uploaded file contains Agent information and cannot be uploaded. Please select an Agent in the Rate scale before proceeding.',
  singleFieldError: (fieldName: string) =>
    `${fieldName} is invalid, please recheck and upload the file.`,
  multipleError:
    'One or more fields contain invalid data or mismatched data types. Please review your file and ensure all values match the required format data.',
  noAgentChargeCode:
    'The selected agent does not have any associated Agent Charge Codes. \n Please configure the required Agent Charge Codes to proceed with adding Accessorial Charges. If you wish to create weight-based charges, you may continue without configuring the codes',
  headerError:
    'Header mismatch: Please upload a valid Excel file with correct column order.',
  headerErrorEmpty: 'No header row found in the uploaded file.',
  headerMismatchAtColumn: (
    column: number,
    found: string,
    expected: string
  ) => `Column ${column}: found "${found}", expected "${expected}".`,
  headerUnexpectedColumn: (
    column: number,
    found: string,
    expectedColumnCount: number
  ) =>
    `Column ${column}: found "${found}". Expected ${expectedColumnCount} columns only.`,
  headerExtraZoneColumn: (
    column: number,
    found: string,
    agentZonesCount: number
  ) => {
    const maxZone =
      agentZonesCount > 0 ? `Zone${agentZonesCount}` : 'Zone1';
    return `Column ${column}: found "${found}", expected up to "${maxZone}" (${agentZonesCount || 1} zone column${agentZonesCount === 1 ? '' : 's'}).`;
  },
  headerMismatchSummary: (details: string[]) => details.join('\n'),
  copyRateScale: 'Copy Rate Scale',
  copyRateScaleField: (fieldName: string) =>
    `Do you want to create a copy of the existing Rate Scale " ${fieldName} " with a markup applied to the weight-based charges?`,
  duplicateWieghtbasedEntry: 'Entered Weight Based Charge already exists',
  duplicateAccessorialEntry: 'Entered Accessorial Charge already exists',
  noWeightBasedChargesConfirmation: (rateScaleName: string) =>
    `The selected rate scale ${rateScaleName} does not have any weight-based charges associated with it. Are you sure you want to proceed with copying a rate scale that has no weight-based charges?`,
};
export const fileExtName = {
  apInvoiceFileExt: 'apCharges.xlsx',
};

export const apErrorMessages = {
  headerErrorMismatch:
    'The uploaded file is not supported. Please use the correct template.',
};

export const FILE_TYPE = {
  EXCEL: '.xlsx,.xls',
  CSV: '.csv',
};

export const ACCEPTED_FILE_TYPES = `${FILE_TYPE.EXCEL},${FILE_TYPE.CSV}`;

export const DropdownSelectionValues = {
  Select: 'Select',
  All: 'All',
  Gatewayed: 'Gatewayed',
};

export const accountCreateUpdateEventDescReqKeys = [
  'accountName',
  'description',
  'serviceable',
  'accountNumber',
  'organizationName',
  'additionalInformation',
  'measurementOptions',
  'timeZone',
];
export const accountBillingEventKey = [
  'DUNSNumber',
  'FEINNumber',
  'stateTaxId',
  'paymentTerms',
  'creditLimit',
];
export const accountGovernmentEventKeys = [
  'recoveryAmount',
  'recoveryWeightMetric',
  'recoveryIbcChargeCode',
  'deliveryAmount',
  'deliveryWeightMetric',
  'deliveryIbcChargeCode',
  'reimbursableChargesClearanceType',
  'reimbursableChargesValuePerShipment',
  'reimbursableChargesIbcChargeCode',
  'clearanceChargesClearanceType',
  'clearanceChargesValuePerShipment',
  'clearanceChargesIbcChargeCode',
  'iscActualCost',
  'iscFlatFee',
  'iscIbcChargeCode',
];
export const accountsAddressEventKeys = [
  'name',
  'companyName',
  'address_1',
  'address_2',
  'city',
  'state',
  'zipCode',
  'countryCode',
  'phone',
  'email',
  'contactPerson',
];

export const staticWeightBasedHeaders = [
  'AgentServiceType',
  'IBCServiceType',
  'Weight',
];

export const apChargeCodeFields = {
  agentChargeCodeId: 'agentChargeCodeId',
  agentChargeCode: 'agentChargeCode',
  agentChargeCodeLabel: 'Agent Charge Code',
  description: 'description',
};

export const ApChargeTypeDefault = 'Internal';

export const measurementOptionsFileds = {
  poundOrInches: 'Pounds/Inches',
};
export const AccountMasterBillingKeys = [
  'recoveryAmount',
  'recoveryWeightMetric',
  'recoveryIbcChargeCode',
  'deliveryAmount',
  'deliveryWeightMetric',
  'deliveryIbcChargeCode',
  'reimbursableChargesClearanceType',
  'reimbursableChargesValuePerShipment',
  'reimbursableChargesIbcChargeCode',
  'clearanceChargesClearanceType',
  'clearanceChargesValuePerShipment',
  'clearanceChargesIbcChargeCode',
  'iscActualCost',
  'iscFlatFee',
  'iscIbcChargeCode',
];

export const AccountWeightMetric = [
  'recoveryWeightMetric',
  'deliveryWeightMetric',
];
export const todayDate = new Date();
export const AccountMSBIbcFieldKey = [
  'recoveryIbcChargeCode',
  'deliveryIbcChargeCode',
  'reimbursableChargesIbcChargeCode',
  'clearanceChargesIbcChargeCode',
  'iscIbcChargeCode',
];
export const AccountMSBClearacneTypeKeys = [
  'clearanceChargesClearanceType',
  'reimbursableChargesClearanceType',
];

export const AutoRateFeatureKeys = {
  specialService: 'SS',
  createInvoice: 'createInvoice',
  hawbSummary: 'hawbSummary',
  proceed: 'proceed',
  weightMetric: 'weightMetric',
};

export function isShipmentSpecialForAutoRate(
  serviceType: unknown,
  accountConfiguredServiceTypes?: readonly string[] | null
): boolean {
  const normalizedServiceType = String(serviceType ?? '').trim();
  if (
    normalizedServiceType.toUpperCase() === AutoRateFeatureKeys.specialService
  ) {
    return true;
  }

  if (!accountConfiguredServiceTypes?.length) {
    return false;
  }

  return !accountConfiguredServiceTypes.includes(normalizedServiceType);
}

export const shipmentPieceGridStatus = {
  shipementCreated: 'Shipment created',
  shipementGatewed: 'Shipment Gatewayed',
  shipementBagged: 'Shipment Bagged',
  shipmentManifested: 'Shipment Manifested',
  shipmentHeld: 'Shipment Held',
  shipmentCleared: 'Shipment Cleared',
  shipmentStatusCodeAdded: 'Status Code Added',
  overageNotReportedToCbp: 'Overage Not Reported To CBP',
  overageReportedToCbp: 'Overage Reported To CBP',
  overageFound: 'Overage Found',
  overageAbiSubmittedManual: 'Overage Manual Update Required',
  overageAbiUpdated: 'Overage ABI Piece Count Updated',
  overageAbiUpdateFailed: 'Overage ABI Update Failed',
  overageChangeSent: 'Overage Change Sent To CBP',
  resubmitRequested: 'Resubmission Requested',
  resubmitted: 'Resubmitted To CBP',
  resubmitFailed: 'Resubmission Failed',
  shipmentStatusCodeUpdated: 'Status Code Updated',
  apChargeAdded: 'AP Charge Added',
  apChargeUpdated: 'AP Charge Updated',
  apChargeDeleted: 'AP Charge Deleted',
  arChargeAdded: 'AR Charge Added',
  arChargeUpdated: 'AR Charge Updated',
  arChargeDeleted: 'AR Charge Deleted',
  shipmentAutoRated: 'Shipment Auto Rated',
  shipmentUnAutoRated: 'Shipment Un-Auto rated',
};

export const manifestLinkedBagsKeys = {
  bagLinked: 'Bag(s) Linked',
  manifestsBags: 'manifestsBags',
  bag: 'Bag',
  shipment: 'Shipment',
  origin: 'Origin',
  destination: 'Destination',
  shipperDetails: 'Shipper Details',
  consigneeDetails: 'Consignee Details',
  commodityDetails: 'Commodity Details',
  commodityText: 'Commodity',
  hSCode: 'HS Code',
  commodityValue: 'Commodity Value',
  commodityDescription: 'Commodity Description',
  weight: 'Weight (lb)',
};

/** PAC-24667: nested sub-level entity print. */
export const entityPrintKeys = {
  manifestsLinked: 'Manifest(s) Linked',
  manifest: 'Manifest',
  linkedShipments: 'Linked Shipment(s)',
  masterSuffix: ' (MASTER)',
  hawbIdsRequest: 'hawbIds/',
  idChunkSize: 50,
  fetchConcurrency: 3,
} as const;

export const defaultPrintWidth = {
  small: 50,
  maxSmall: 100,
  medium: 150,
  ml: 120,
  large: 200,
};

export const PHONE_FORMAT_CONSTANTS = {
  CONTROL_KEYS: ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'],
  VALIDATION_LIMITS: {
    DIGITS_MIN: 8,
    DIGITS_MAX: 11,
    ALPHANUMERIC_MIN: 8,
    ALPHANUMERIC_MAX: 10,
  },
  ERROR_TYPES: {
    PHONE_MIN_LENGTH: 'phoneMinLength',
    PHONE_MAX_LENGTH: 'phoneMaxLength',
  },
  REVALIDATION_DELAY: 10,
};

export const PHONE_REGEX_PATTERNS = {
  ALLOWED_CHARACTERS: /[a-zA-Z0-9]/,
  EXTRACT_DIGITS: /\D/g,
  EXTRACT_ALPHANUMERIC: /[^a-zA-Z0-9]/g,
  REMOVE_INVALID_CHARS: /[^a-zA-Z0-9]/g,
  FORMATTING_CHARS: /[ ()-]/g,
};

export const InvoiceMessages = {
  invoiceAlreadyExists: 'Entered Invoice already exists',
};

export function normalizeNumericValue(val: any): number | null {
  if (val === null || val === undefined || val === '') {
    return null;
  }
  const num = Number(val);
  return Number.isNaN(num) ? null : num;
}

export const InvoiceActionType = {
  existing: 'existing',
  typed: 'typed',
  new: 'new',
} as const;

export const RoutingMessages = {
  pageTitle: 'Route Information',
  routeAddedSuccess: 'Route added successfully',
  routeUpdatedSuccess: 'Route updated successfully',
  routeDeletedSuccess: 'Selected Route deleted successfully',
  selectRouteFirst: 'Please select a route first',
  failedToLoadRoutingInfo: 'Failed to load routing information',
  errorLoadingRoutingInfo: 'Error loading routing information',
  deleteRouteConfirmation: 'Delete Route ?',
  atLeastOneRouteRequired:
    'At least one route should be existing between the Origin and Destination',
  routingIdNotFound: 'Routing ID not found',
  routingInfoSection: 'Routing Information',
  routesSection: 'Routes',
  transitPointsSection: 'Transit Points',
  noRoutesToUpdate: 'No routes to update',
  someRoutesFailedToUpdate: 'Some routes failed to update',
  errorUpdatingRoutes: 'Error updating routes',
  errorCreatingRoute: 'Error creating route',
  chooseServiceTypeBeforeAddingRoutingCriteria: 'Choose the Service Type in Government Information Tab before adding the Routing Criteria',
  confirmationTitle: 'Confirmation',
  originChangeConfirmation:
    'Changing the Origin will refresh the below section and all unsaved changes would be lost. Would you like to change the Origin?',
  destinationChangeConfirmation:
    'Changing the Destination will refresh the below section and all unsaved changes would be lost. Would you like to change the Destination?',
} as const;

export const HawbRouteMessages = {
  routeCreatedSuccess: 'Route created successfully',
  routeLinkedSuccess: 'Route Selected Successfully',
  hawbIdRequired: 'HAWB ID is required to create a route',
  selectOriginDestination: 'Please select origin and destination first',
  errorCreatingRoute: 'Error creating route',
  chooseFinalIBCWarehouse:
    'Choose Final Destination before proceeding with Routing.',
  routeUpdatedSuccess: 'Transit points updated successfully',
  unableToFindSelectedRouteData: 'Unable to find selected route data',
  routesInfoText:
    'Choosing a new route after the shipment starts to move is not possible. But you can change the transit points in the selected route after the shipment started to move',
  performGatewayBeforeRouting:
    'Gateway the Shipment to proceed for Routing',
  routingNotNeededWhenGatewayAndFinalSame:
    'Routing not needed when the first gatewayed location and Final Destination are the same.',
  routingNotPossibleInBulkEdit: 'Routing not possible in Bulk Edit',
  masterLinkedRoutingBanner:
    'Routing setup at Master Shipment will be applied to all Linked Shipments',
  preferredRoutesAccountLevelInfo:
    'Preferred routes based on the Account level configuration (if any) are also displayed below',
} as const;

export const RoutingLandingPageHelpSections = [
  {
    title: 'Route Listing Information',
    content: [
      'All routes listed for an origin-destination pair are displayed individually in the landing page grid',
      'Example: If NYC to MIA has 4 routes, all 4 routes will be listed as 4 separate rows in the grid',
      'Each route appears as a distinct row, even if they share the same origin and destination',
    ],
  },
  {
    title: 'Route Uniqueness Requirements',
    content: [
      'For a route, at least one field must be unique to differentiate it from other routes for the same origin and destination',
      'Unique fields can include: Airlines, Starting Time, Arrival Time, Minimum Weight, Maximum Weight, Value, or other route-specific fields',
      'All 4 visible columns (Origin, Destination, Transit Points, Airlines) can be the same for multiple routes. However, at least one other factor in the route will be different (e.g., timing, weight limits, charges, etc.), which can be viewed from the Route Information page',
      'This uniqueness requirement ensures that each route can be properly identified and selected',
    ],
  },
];

export const RoutingInfoPageHelpSections = [
  {
    title: 'Route Uniqueness Requirements',
    content: [
      'For a route, at least one field must be unique to differentiate it from other routes for the same origin and destination',
      'Unique fields can include: Airlines, Starting Time, Arrival Time, Minimum Weight, Maximum Weight, Value, or other route-specific fields',
      'This uniqueness requirement ensures that each route can be properly identified and selected',
    ],
  },
];

export const TransitPointValidationMessages = {
  selectValidDestination: 'Please select a valid destination',
  selectValidModeOfTransport: 'Please select a valid mode of transport',
  enterCharge: 'Please enter a charge',
  firstTransitPointOrigin: (originName: string, originCode: string) =>
    `First transit point's origin must be ${originName} (${originCode})`,
  originMustMatch: (previousDestinationName: string) =>
    `Origin must match ${previousDestinationName}`,
  destinationMustMatchNext: (nextOriginName: string) =>
    `Destination must match the next transit point's origin: ${nextOriginName}`,
  routeMustEndAtDestination:
    'You have not reached the destination yet. You cannot save it.',
  previousDestination: 'previous destination',
  nextTransitPoint: 'next transit point',
  minWeightGreaterThanMax: 'Minimum weight cannot be greater than Maximum weight',
  duplicateWarehouse:
    'Same IBC Warehouse visited more than once. Please update the route',
  finalWarehouseViolation:
    'Final IBC Warehouse already reached and no other IBC Warehouses can be reached further',
  shipmentAlreadyArrived:
    'Cannot delete or add before this transit point: Shipment has already arrived at this location',
  chainBroken:
    'Transit point chain is broken. Origin and destination do not match',
  rowLocked:
    'This transit point cannot be modified as it has already been processed',
  deleteNotAllowed:
    'Cannot delete this transit point as it would break the route chain',
  addSuccess: 'Transit point added successfully',
  editSuccess: 'Transit point updated successfully',
  deleteSuccess: 'Transit point deleted successfully',
  selectDestination: 'Please select a destination for the transit point',
  routeNotLinkedProperly: (rowIndex: number, outTPName: string, nextInTPName: string) =>
    `Route is not linked properly: Transit Point ${rowIndex} destination (${outTPName}) does not match Transit Point ${rowIndex + 1} origin (${nextInTPName})`,
  routeLinkageError: 'Transit points are not linked properly. Each transit point\'s destination must match the next transit point\'s origin.',
  transitPointAfterDestination:
    'The Route cannot have any transit point after reaching the Destination. Kindly update the route',
  addAtLeastOneTransitPoint:
    'Add at least one transit point to complete the route before saving.',
  originDestinationSame: (rowIndex: number, warehouseName: string) =>
    rowIndex === 0
      ? `Origin and Destination cannot be the same (${warehouseName})`
      : `Row ${rowIndex}: Origin and Destination cannot be the same (${warehouseName})`,
} as const;

export const TransitPointInlineTableMessages = {
  emptyState: 'No transit points added yet. Click + to add a new row.',
  cannotEditFinalDestination: 'Cannot edit - this is the final destination row',
  cannotEditRow: 'Cannot edit this row',
  cannotAddDestinationNotSet: 'Set a destination before adding a new row',
  cannotDeleteFinalDestination: 'Cannot delete final Destination row',
  cannotDeleteOriginRow: 'Cannot delete - this is the origin row',
  cannotDeleteProtectedUpdateModeRows:
    'Cannot delete - the last two transit points on this route cannot be removed',
  actionNotPossibleVisited: 'Action not possible as the TP has been visited',
  cannotDeleteRow: 'Cannot delete this row',
  cannotAddAfterFinalDestination: 'Cannot add row after final Destination',
  cannotAddRow: 'Cannot add row here',
  defaultModeOfTransportType: 'air',
  saveRowMandatoryFieldsDestAndMode:
    'Fill in the mandatory field (Destination)',
  saveRowMissingMandatoryFields: (fields: string[]) =>
    `Fill in mandatory fields: ${fields.join(', ')}`,
  saveRowMandatoryFieldsDestModeAndCharge:
    'Fill in the mandatory field (Destination)',
} as const;

export const DashboardConstants = {
  configureTitle: (dashboardType: string) =>
    `Configure ${dashboardType.toUpperCase()} Dashboard`,
  navigationPermissionBanner:
    'Navigation from the dashboard to the corresponding screens is dependent on the permissions assigned to you. If you cannot navigate to any specific screen from here, contact your system administrator for permissions',
} as const;

export const DashboardArChargesUi = {
  dialogWidth: '860px',
  dialogMaxWidth: '95vw',
  tooltipPositionAbove: 'above',
  noSearchFiltered:
    "Choose the filters and hit on 'Submit' to view changes in the grid",
  noDataFound: 'No Data found',
  noDataFoundFor: (title: string) => `No Data found for ${title}`,
  dialogTitle: 'Multiple Invoice Linked to the Additional charge',
  dialogInfoMessage:
    'There are multiple invoices (mentioned below) which are linked to the shipment, choose one to add the charge to the corresponding AR Invoice',
  addChargesButtonLabel: 'Add Charges to AR Invoice',
  choiceDialogTitle: 'Additional charges',
  choiceDialogPrompt:
    'Link these charges to an existing published AR invoice, or create a new AR invoice.',
  linkToExistingArInvoiceLabel: 'Link to Existing AR Invoice',
  createNewArInvoiceLabel: 'Create New AR Invoice',
  tableHeaders: {
    arInvoice: 'AR Invoice',
    invoiceDate: 'Invoice Date',
    accountName: 'Account Name',
    accountNumber: 'Account Number',
    charges: 'Charges ($)',
    action: 'Action',
  },
} as const;

export const ArAdditionalChargesChoice = {
  linkToExisting: 'arAdditionalCharges:linkExisting',
  createNew: 'arAdditionalCharges:createNew',
} as const;

export type ArAdditionalChargesChoiceValue =
  (typeof ArAdditionalChargesChoice)[keyof typeof ArAdditionalChargesChoice];

export type ArAdditionalChargesModalDialogResult =
  | {
    popupClose: true;
    data: ArAdditionalChargesChoiceValue;
  }
  | null
  | undefined;

export interface ArAdditionalChargesNewInvoiceHandoff {
  isArAdditionalChargesNewInvoice: true;
  arCharges: any[];
  apInvoiceChargeIds: number[];
  accountNumber: string;
}

export const DashboardSectionId = {
  inTransit: 'in-transit',
  incomingShipmentStatus: 'incoming-shipment-status',
  additionalChargesForPublishedInvoices: 'additional-charges-for-published-invoices-with-shipment-mawb',
} as const;

export const IncomingShipmentStatusUi = {
  hiddenByDefaultColumnKeys: ['blockedCount', 'nsCount'] as readonly string[],
  /** Brokerage grid: pinned left ahead of the date column (matched by a key ending in "date"). */
  brokeragePinnedColumnKeys: ['mawbNumber', 'manifestNumber'] as readonly string[],
} as const;

export const IncomingShipmentStatusHelpSections = [
  {
    title: 'BK – Blocked',
    content: [
      'Shipments on hold in PacTrak, blocked manually or by the system (for example DDU (DAP): duties pending, or AMS hold: pending CBP release)',
      'A blocked shipment cannot be bagged, gatewayed or published on a manifest until it is unblocked',
      'Hidden by default; turn it on from Customize Columns (3 dots)',
    ],
  },
  {
    title: 'NS – Not Submitted',
    content: [
      'Shipments that need customs clearance but have not been submitted to CBP yet',
      'Needs clearance means the shipment is international and its account does not have No Need Clearance set',
      'Once a shipment is submitted it leaves NS, whatever the CBP response (pending, rejected or accepted)',
      'Hidden by default; turn it on from Customize Columns (3 dots)',
    ],
  },
  {
    title: 'HA – Hold (AMS)',
    content: [
      'Shipments CBP has placed on hold (AMS hold)',
      'They cannot move forward until CBP releases them',
    ],
  },
  {
    title: 'CC – Customs Cleared',
    content: ['Shipments CBP has released (AMS released)'],
  },
  {
    title: 'GR – Gateway Received',
    content: ['Shipments that have been received at the gateway'],
  },
  {
    title: 'POD – Proof of Delivery',
    content: ['Shipments that have been delivered (POD status recorded)'],
  },
  {
    title: 'Total',
    content: ['All shipments on the manifest'],
  },
  {
    title: 'Overage',
    content: [
      'Shipments where more pieces were received than were filed with CBP',
      'Compares the physical piece count with the filed piece count',
    ],
  },
  {
    title: 'In-Bond (7512)',
    content: [
      'Shipments whose final destination is outside the US, so they move in bond (CBP Form 7512)',
      'US territories such as PR, VI and GU count as in-bond',
      'Shipments with no final destination set are not counted',
    ],
  },
  {
    title: 'Manifest Weight',
    content: ['Total weight of all shipments on the manifest, in the unit it was entered'],
  },
];

export const DashboardRoute = {
  root: '/dashboard',
  shipment: '/shipment',
} as const;

export const AdditionalChargesColumnKeys = {
  shipmentMawbAgentTrackingNumber: 'shipmentMawbAgentTrackingNumber',
  accountNumber: 'accountNumber',
  arInvoice: 'arInvoice',
  additionalChargeDescription: 'additionalChargeDescription',
  chargeAmount: 'chargeAmount',
  vendor: 'vendor',
  invoiceDate: 'invoiceDate',
  linkRoute: 'linkRoute',
  linkId: 'linkId',
  arInvoiceDetails: 'arInvoiceDetails',
  apInvoiceChargeIds: 'apInvoiceChargeIds',
} as const;

export const DashboardArChargesMessages = {
  noCalculableArCharges:
    'No calculable AR charges found for the selected AP charges',
  noApChargeIds: 'No AP charge IDs available for this row',
  noArInvoicesLinked:
    'No AR invoices are linked to this shipment',
} as const;

export const accountRoutingCriteriaAutocompleteControls = {
  serviceType: 'serviceType',
  modeOfTransport: 'modeOfTransport',
  shipmentType: 'shipmentType',
};

export const APSelectionType = {
  agentTracking: 'agentTracking',
  shipment: 'shipment',
} as const;

export type APSelectionTypeValue = typeof APSelectionType[keyof typeof APSelectionType];

export const ARSelectionType = {
  shipment: 'shipment',
} as const;

export type ARSelectionTypeValue = typeof ARSelectionType[keyof typeof ARSelectionType];

export const arInvoicefileNameExtension = {
  pdf: 'pdf',
  csv: 'csv',
  xlsx: 'xlsx',
};

export const DashboardType = {
  ap: 'ap',
  ar: 'ar',
  de: 'data-entry',
  wh: 'warehouse',
  brokerage: 'brokerage',
  operations: 'operations',
  customerService: 'customer-service',
} as const;

export interface ManifestPublishFormatOption {
  id?: number;
  stationFormatId?: number;
  formatCode?: string;
  formatDescription?: string;
  outputFileExtension?: string;
  name: string;
}

export const manifestPublishPopupMsg = {
  manifestPublishAttachment: 'manifest-publish-attachments',
  attachmentActions: 'attachmentActions'
} as const;

export const ArChargesComponentInputNames = {
  crudLocked: 'arChargesCrudLocked',
} as const;

export const MawbArWeightFieldNames: readonly string[] = [
  'dimL',
  'dimW',
  'dimH',
  'dimensionalWeight',
  'actualWeight',
  'chargeableWeight',
];

export const KEYBOARD_EVENT_CODES = {
  Comma: 'Comma',
  Period: 'Period',
  KeyB: 'KeyB',
  KeyC: 'KeyC',
  KeyP: 'KeyP',
} as const;

export const EventFilterOptions = {
  allEvents: 'All Events',
  cbpEvents: 'CBP Events',
} as const;

export const CBPStatus = {
  shipmentHeld: 'shipment held',
  shipmentCleared: 'shipment cleared',
} as const;

export const CCITY_CBP_ACTION_DETAIL_DISPLAY_SEPARATOR = ' - ';
export const CCITY_CBP_ACTION_DETAIL_SEPARATOR_PATTERN =
  /^([A-Za-z0-9]+)\s*[-\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]\s*(.+)$/;

export const recordActions = {
  blocked: 'Blocked',
  unblocked: 'Unblocked',
  gatewayed: 'Gatewayed',
  published: 'Published',
} as const;

export const ShortcutKeysPageCopy = {
  loading: 'Loading…',
  loadError:
    'Could not read the keyboard shortcut reference. If this continues, try refreshing the page.',
  missingParamOpen: 'Open ',
  missingParamBold: 'Show Shortcut Keys',
  missingParamRest:
    ' from the footer while you are on a screen that has a shortcut map (for example HAWB summary or bag summary).',
  notFound: 'No shortcut list was found for the requested page id.',
  keyComboJoin: '+',
  keyComboOr: 'or',
} as const;

export const ShortcutKeysDefaultCatalogTitle = 'Keyboard shortcuts';

export const ShortcutKeysQueryParam = {
  page: 'page',
} as const;

export const ShortcutKeysLoadResultKind = {
  missing: 'missing',
  ok: 'ok',
  error: 'error',
} as const;

export const ShortcutKeysRouterPath = {
  docsFeature: 'shortcut-keys',
  hawb: 'hawb',
  bag: 'bag',
  manifest: 'manifest',
  mawb: 'mawb',
  hawbSummary: 'hawb-summary',
  hawbInformation: 'hawb-information',
  bagSummary: 'bag-summary',
  bagInformation: 'bag-information',
  manifestSummary: 'manifest-summary',
  manifestInformation: 'manifest-information',
  mawbSummary: 'mawb-summary',
  mawbInformation: 'mawb-information',
} as const;

export const ShortcutKeysFooterLinkPageIds = new Set<string>([
  ShortcutKeysRouterPath.hawbSummary,
  ShortcutKeysRouterPath.hawbInformation,
  ShortcutKeysRouterPath.bagSummary,
  ShortcutKeysRouterPath.bagInformation,
  ShortcutKeysRouterPath.manifestSummary,
  ShortcutKeysRouterPath.manifestInformation,
  ShortcutKeysRouterPath.mawbSummary,
  ShortcutKeysRouterPath.mawbInformation,
]);

export const dataTableModeValues = {
  allSmall: 'all',
  NA: 'NA',
  advance: 'advance',
  none: 'none',
  partial: 'partial'
} as const;

export const RolesAndPermissionPrintKeys = {
  actions: 'actions',
  summary: 'summary',
  fieldLevel: 'field-level',
  fieldLegend: 'field-legend',
  fields: 'fields',
  fieldPermssion: 'Field Permissions',
  fieldLevelPermissions: 'Field Level Permissions',
  descritpionForField: 'The permission is in the context of Edit, if the user can edit a field or not',
} as const;
/**
 * Copy for the Cancel / Recover Shipments screen (SPEC-CRS-001-v1.0).
 * Server-owned strings (upload validation errors, commit failures) are rendered
 * as they arrive and are deliberately NOT duplicated here.
 */
export const CancelRecoverShipmentsMessages = {
  pageHeader: 'Cancel / Recover Shipments',
  cancelTab: 'Cancel Shipments',
  recoverTab: 'Recover Shipments',

  uploadCardLabel: 'Upload .xlsx file',
  uploadHelperText:
    'After upload all the fields below will be disabled, remove the uploaded file if you want to choose manually',
  browse: 'Browse',
  remove: 'Remove',
  acceptedUploadTypes: '.xlsx',

  mawbLabel: 'Search for MAWB',
  manifestLabel: 'Manifest',
  containerLabel: 'Container',
  shipmentLabel: 'Shipment',

  selectManifestTitle: 'Select Manifest',
  selectContainerTitle: 'Select Container',
  selectShipmentTitle: 'Select Shipment',
  shipmentPopupTitle: 'Shipment',
  searchManifestPlaceholder: 'Search Manifest',
  searchContainerPlaceholder: 'Search Container / Bags',
  searchShipmentPlaceholder: 'Search Shipment',

  invalidMawbEntered: 'Invalid MAWB entered',
  mawbNotCancelled: 'MAWB not cancelled',
  multipleMawbsInFile:
    'Uploaded file has more than one MAWB. Kindly try uploading a valid file',
  invalidWorkbookHeaders:
    'Uploaded file must have a MAWB column. Kindly try uploading a valid file',
  emptyWorkbook:
    'Uploaded file has no MAWB. Kindly try uploading a valid file',
  recoverUploadNotCancelled:
    'The data loaded has some shipments that are not yet cancelled. Kindly upload a different file with the right data',

  resetButton: 'Reset',
  confirmButton: 'Confirm',
  hawbOthersSuffix: '+ others',
  hawbLinkLabel: (count: number) => `${count} HAWB(s)`,
  selectedForCancellation: (count: number) =>
    `${count} Shipment(s) selected for cancellation`,
  selectedForRecovery: (count: number) =>
    `${count} Shipment(s) selected for recovery`,

  cancelConfirmationHeader: 'Cancel Shipments',
  recoverConfirmationHeader: 'Recover Shipments',
  cancelConfirmationMessage: (count: number) =>
    `Are you sure you want to cancel all the ${count} shipments and send FXX/3 to CBP?`,
  recoverConfirmationMessage: (count: number) =>
    `Are you sure you want to recover all the ${count} shipments and send FXC/21 to CBP?`,
  cancelConfirmButton: 'Cancel',
  recoverConfirmButton: 'Recover',
  closeButton: 'Close',

  cancelSuccess: (count: number) =>
    `${count} Shipment(s) cancelled successfully`,
  recoverSuccess: (count: number) =>
    `${count} Shipment(s) recovered successfully`,
  // SPEC-CAN-001 v1.2 — partial cancel/recover: HAWBs Customs City would not allow.
  notCancelledHeader: 'Shipments not cancelled',
  notRecoveredHeader: 'Shipments not recovered',
  partiallyCancelledHeader: 'Shipments partially cancelled',
  partiallyRecoveredHeader: 'Shipments partially recovered',
  skippedShipmentLine: (hawbExt: string, detail: string) =>
    `${hawbExt}: ${detail}`,
  noneCancelledSummary: 'No shipments could be cancelled.',
  noneRecoveredSummary: 'No shipments could be recovered.',
  partialCancelledSummary: 'Following shipments could not be cancelled:',
  partialRecoveredSummary: 'Following shipments could not be recovered:',

  noManifestsMatch: 'No Manifests matched your search',
  noContainersMatch: 'No Containers matched your search',
  noShipmentsMatch: 'No Shipments matched your search',
} as const;

/** SPEC-RSB-001 (PAC-24568) — Customs City resubmit texts (shipment search + Shipment Information actions). */
export const customsCityResubmitMessages = {
  queued: (count: number) => `${count} shipment(s) queued for resubmission`,
  skippedHeader: 'Shipments not resubmitted',
  partialHeader: 'Shipments partially resubmitted',
  noneSummary: 'No shipments could be resubmitted.',
  partialSummary: 'Following shipments could not be resubmitted:',
  skippedShipmentLine: (hawbExt: string, detail: string) =>
    `${hawbExt}: ${detail}`,
} as const;
